/**
 * Agent React hooks
 *
 * Provides hooks for agent session management and streaming.
 *
 * Transport (Phase 4b-A3 — AG-UI is now unconditional, no flag, no fallback):
 * 1. Run stream — sendMessage POSTs a RunAgentInput to /api/agent/agui; the
 *    response body IS the run's AG-UI SSE, inverse-translated into the
 *    existing StreamEvent reducer (createAguiToStream → handleAgentEvent).
 *    On mount we re-attach to any in-flight run via a prompt-less resume POST.
 * 2. Bus events  — domain events (compaction, summary, revert) via SSE →
 *    /api/agent/event (separate channel, unchanged).
 *
 * The legacy bespoke WS path (papan:agent-event subscription + the
 * /sessions/:id/message POST) was removed in A3; its client + server
 * plumbing (agentSubscribe/agentUnsubscribe in useRealtime.tsx, the
 * agent:subscribe/unsubscribe WS handlers in realtime/server) was fully
 * deleted afterwards — the shared `papan:<t>:agent:<s>` Redis channel
 * remains, now consumed only by the AG-UI bridge (subscribeToAgentEvents).
 */
import { dropStaleCards } from './stale-cards.js'
import { readClearedAttachment, type ClearedAttachment } from '../core/cleared-attachment.js'
import { useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useBusySessions, busySessionsKey } from './useBusySessions.js'
import { shouldNudgeForeignRun } from './foreign-run-discovery.js'
import { request, getApiBase, getActiveTenantId, reportApiFailure, currentSessionSignal, sessionFetch, sessionStreamSignal, sessionEndedSince } from '@papan/api-client'
import { onSessionEnd, accountEpoch } from '@papan/core-ui/ui/sessionEnded'
import { sessionBoundAttempt } from './session-bound-send.js'
import i18n from '@papan/i18n'
import { presenceHeartbeat, hasOpenCard } from './presence-heartbeat.js'
import { questionExpiredNote, questionAlreadyAnswered, type ExpiredQuestionNotice } from './question-expired.js'
import { createAwaitingPing } from './awaiting-ping.js'
import { useRealtimeOptional } from '@papan/realtime/client'
import type { ChatCostVisibility } from '@papan/types'

/** What the display-currency minor units on `/api/agent/usage` are IN.
 *  `null` from the server means nothing was converted. */
export interface ChatCostCurrency { code: string; minorUnitsPerMajor: number }

/**
 * `GET /api/agent/usage`, as both readers see it.
 *
 * One declaration rather than two inline literals: `useAgentSession` and
 * `useAgentUsage` read the same endpoint for overlapping fields, and the pair
 * drifting is how one of them comes to render a converted figure while the
 * other renders the canonical one in the same window.
 */
export interface AgentUsageResponse {
  costVisibility?: ChatCostVisibility
  workspaceCost: number
  sessionCost: number
  /** `null` = canonical USD, nothing converted. THE `≈` test. */
  workspaceCostDisplayMinor?: number | null
  sessionCostDisplayMinor?: number | null
  currency?: ChatCostCurrency | null
  estimatedTokens: number
  contextLimit: number
}
import type { SessionProcessor } from '../core/processor.js'
import { createAguiToStream } from './agui-to-stream.js'
import { sentMessageConfirmation } from './sent-message-confirmation.js'
import { credentialDropsOf, CREDENTIAL_DROPS_EVENT, type CredentialDropsEventDetail } from '../core/credential-drop-notice.js'
import { recoverRun, fetchWithGatewayRetry, attachOutcomeForStatus, isRecoverableOutcome, cutOutcomeFor, reconcileAfterGiveUp, onceBackOnline, RECONCILE_GIVE_UP_AFTER_MS, type AttachOutcome } from './run-recovery.js'

/** #813: thrown inside the send path when a retried send finds its own run already
 *  admitted (SESSION_BUSY) — handled by the catch as a lost connection. */
class RunConnectionLost extends Error {
  constructor() { super('The connection to the assistant was lost.') }
}

/**
 * #813: optimistic user bubbles whose message IS stored server-side but whose
 * canonical copy has not been loaded yet (a send whose stream was cut by an API
 * restart). Whichever canonical load comes next — recovery's own, another surface's
 * end-of-run reload, a session switch — drops them before merging (mergeTranscript
 * otherwise keeps every temp_ row, so the prompt would show twice). Module-level
 * so every mounted surface of a session honours it.
 */
const storedTempIds = new Map<string, Map<string, number>>() // sid → tempId → registration seq
let storedTempSeq = 0
function markStoredTemp(sid: string, tempId: string) {
  const entries = storedTempIds.get(sid) ?? new Map<string, number>()
  entries.set(tempId, ++storedTempSeq)
  storedTempIds.set(sid, entries)
}
/** The registration sequence NOW — a load records it at request START. */
function storedTempMark(): number {
  return storedTempSeq
}
/** Take the entries registered BEFORE `mark` (a load that started earlier fetched a
 *  transcript that may predate the message — later entries wait for the next load). */
function takeStoredTemps(sid: string, mark: number): Set<string> | undefined {
  const entries = storedTempIds.get(sid)
  if (!entries) return undefined
  const taken = new Set<string>()
  for (const [id, seq] of entries) if (seq <= mark) { taken.add(id); entries.delete(id) }
  if (entries.size === 0) storedTempIds.delete(sid)
  return taken
}

interface RecoverOpts {
  /** Re-send as a RESUME (a suspended turn's answered run), not a plain re-attach. */
  resume?: boolean
  /** The lost send's optimistic user bubble — its message is stored server-side. */
  tempId?: string
}
import { aguiErrorResult, type GateErrorAction } from './agui-error.js'
import { apiToolPartToRow } from './tool-part-row.js'
import './agentTools.js' // side-effect: populate the frontend-tool registry
import { getAgentTool, type AgentToolEntry } from './registerAgentTool.js'
import {
  scopeTargetlessCall, resultWorkspaceId, withinDeferWindow, waitKey, type ClientToolOffer,
  keptResult, beginAttempt, endAttempt, onOffer, offerMayPost, ranOnThisPage, takeResumeRequest, markResumeAttach,
  startResultPump, replayKeptResult, type ReplayLookup, registerResumeStarter, requestResume, pollOutcome, runClaimedOffer, refreshKeptClaim, rearmDeferredRedispatch,
  onUnresolvedWaitAnnounced,
} from './clientToolRun.js'
import { isDatabaseFileName } from './dbEditorTarget.js'

// Unique client ID per page load — used to target editor actions to the originating browser
const CLIENT_ID = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)


/** The browser's IANA time zone, or undefined if the engine can't report one. */
function browserTimeZone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return typeof tz === 'string' && tz ? tz : undefined
  } catch { return undefined }
}

export function getClientId(): string { return CLIENT_ID }

/** #540 PR E: the run props of each session's last send (model, variant, persona),
 *  so a RESUME run of a suspended turn continues with the same ones.
 *  Module-level: a resume can be triggered by a different hook instance (a reply
 *  card) than the composer that sent. Absent after a reload — the server then falls
 *  back to the session model and the suspended message's persona. */
const lastSendProps = new Map<string, Record<string, unknown>>()
/** What each session's last send from THIS page carried (#352): its
 *  workspace and its editor-context chip — what that session's streamed
 *  db_editor SQL preview mirrors into, the same target the worker binds the
 *  executed call to. A run this page only ATTACHED to (sent from another tab
 *  or device) falls back to the hook's own workspace and no chip — a
 *  preview-only imprecision: the executed call always uses the worker's stamp. */
const lastSendTarget = new Map<string, { workspaceId: string; chip: { fileId: string; fileName: string } | null }>()
/** Record what a send from this page carried (`lastSendTarget`). Exported for tests. */
export function recordSendTarget(sessionId: string, workspaceId: string, activeTab: { fileId: string; fileName: string } | null | undefined): void {
  lastSendTarget.set(sessionId, { workspaceId, chip: activeTab ? { fileId: activeTab.fileId, fileName: activeTab.fileName } : null })
}
/** Where a session's streamed db_editor SQL preview goes (#352): a session sent
 *  from this page → its recorded workspace and chip; one this page only attached
 *  to → the hook's own workspace, no chip. Pure apart from `lastSendTarget`. */
export function streamPreviewTarget(sessionId: string, hookWorkspaceId: string): { workspaceId: string; chip: { fileId: string; fileName: string } | null } {
  return lastSendTarget.get(sessionId) ?? { workspaceId: hookWorkspaceId, chip: null }
}

// ─── useAgentSession ────────────────────────────────────────────────────

interface UseAgentSessionOptions {
  workspaceId: string
  /** Called when the agent completes a turn (e.g. to refresh session list for title updates) */
  onComplete?: () => void
  /**
   * #662: open a just-moved drive file in the workspace. Fired ONCE when a
   * `move_to_drive` tool completes LIVE this turn (never on reload — the event
   * only arrives on the active stream), so an AI "move this attachment to the
   * drive" auto-opens the result exactly like the manual Save-to-drive dialog
   * does (AttachmentChip's `onSaved` → `onOpen`). Host passes its workspace
   * open-file callback (the same one the chip's `onOpen` uses).
   */
  onOpenFile?: (path: string) => void
}

/**
 * Collision-proof id for committed streaming parts. `Date.now()` alone
 * collides when two commits land in the same millisecond (e.g. AG-UI
 * re-delivers TEXT_MESSAGE_END over the live SSE *and* the bus). Two
 * parts with the same id become two React children with the same
 * `key`, which corrupts reconciliation — parts get duplicated/omitted
 * on every re-render. The chat-panel resize handler (setChatWidth on
 * each mousemove) turns that into a visible "Done." pile-up. A
 * module-monotonic suffix guarantees uniqueness within the session.
 */
let __streamPartSeq = 0
const nextStreamPartId = (kind: string): string =>
  `${kind}_${Date.now()}_${++__streamPartSeq}`

/** Local UI representation of a message with parts */
interface MessageWithParts {
  id: string
  role: string
  parts: MessagePart[]
  createdAt?: string
  /** Assistant message metadata */
  modelID?: string
  personaId?: string
  timeCreated?: number
  timeCompleted?: number
  /** USD dollars — legacy, unchanged. */
  cost?: number
  /**
   * The settled BILLING-native amount for this message (parent + subagents),
   * and its currency, persisted by the worker at settle time from the exact PG
   * stamps. Absent on older messages → badge withheld.
   */
  costBillingMicro?: number
  costCurrency?: string
  parentID?: string
  /** System-written user turn (compaction nudge / overflow replay) — hidden. */
  synthetic?: boolean
  /** Sent mid-run and steered into the running turn (#481 item 4) — the UI marks
   *  it so a steer the model folded in doesn't look ignored. */
  steer?: boolean
  /** E7 Codex rounds 8, 10: a steer whose post has no answer yet (a timeout, a network error) — retried; still unknown
   *  past the bound, it stays so marked (the next transcript load shows the truth). */
  steerUnconfirmed?: boolean
  /** Assistant message that IS a compaction summary (`info.summary`). */
  isSummary?: boolean
}

/** Outcome of a manual "Summarize session".
 *
 *  `message` is the SERVER's own copy when it refused — a budget breakdown or
 *  a wallet mismatch — so the caller can show the reason instead of guessing. */
export interface SummarizeResult {
  /** `'queued'` since #443: the route ACCEPTS the work and a worker does it, so
   *  the reply cannot say whether it succeeded. `'done'` and `'skipped'` are
   *  gone rather than kept as dead branches — the outcome now surfaces in the
   *  transcript itself (a summary divider, or a `[Compaction failed: …]`
   *  summary) once the resync lands. */
  status: 'queued' | 'error' | 'busy'
  message?: string
  code?: string
  httpStatus?: number
}

/** `busy_elsewhere` (#1468 I1): 409 SESSION_BUSY_ELSEWHERE — this chat shares its (pre-release) id with another
 *  project's chat whose turn is live; a wait, not a fault. */
export type SteerRefusal = 'not_running' | 'compacting' | 'queued' | 'starting' | 'ending' | 'busy_elsewhere' | 'error'

/** A steer either reached a live worker or it did not. `ok: false` always means
 *  the text was NOT delivered and has been handed back to the composer. */
export type SteerResult = { ok: true } | { ok: false; reason: SteerRefusal }

/** One place the refusal becomes user-facing copy — the same rule as
 *  `describeSummarize`, and for the same reason: four surfaces would otherwise
 *  drift.
 *
 *  Every message says the text was NOT sent, because that is the fact the user
 *  needs; the differences are only in what to do about it. `null` means say
 *  nothing — the turn simply ended between the keystroke and the request, and
 *  the caller resends it as an ordinary prompt, which is the right outcome
 *  rather than an error. */
export function describeSteerRefusal(reason: SteerRefusal): string | null {
  switch (reason) {
    case 'not_running': return null
    case 'compacting': return 'Still summarizing — your message was not sent. Try again in a moment.'
    case 'queued': return 'The turn has not started yet — your message was not sent. Try again in a moment.'
    case 'starting': return 'The turn is still starting up — your message was not sent. Try again in a moment.'
    // E7 full review round 8: the turn is finishing — it can no longer take the message in
    case 'ending': return 'The turn is just finishing — your message was not sent. Send it again in a moment.'
    case 'busy_elsewhere': return i18n.t('agent:session.busyElsewhere')
    case 'error': return 'Could not send that message. Try again.'
  }
}

/** One place the "Summarize session" outcome becomes user-facing copy.
 *
 *  There were FOUR copies of this branch — the slash command, the session
 *  menu, the context-ring menu and HeroChat's menu — and they had already
 *  drifted: two said "(need more than 6)", one did not. More importantly all
 *  four flattened every failure to "Failed to summarize session", which since
 *  #438 R3 can mean "you are over your spending cap" (402) or "this session
 *  belongs to a different wallet" (409). The server writes copy for both; this
 *  shows it. */
export function describeSummarize(result: SummarizeResult): {
  kind: 'success' | 'info' | 'error'
  text: string
} {
  if (result.status === 'busy') return { kind: 'info', text: 'Already summarizing this session' }
  // Accepted, not finished (#443). The copy has to promise only what the reply
  // proves: the work is queued. The visible indicator then runs off
  // `compaction-progress` — the same one auto-compaction drives — and the
  // OUTCOME appears in the transcript when the resync lands, as a summary
  // divider or a `[Compaction failed: …]` summary.
  //
  // This is why there is no longer a 'skipped' branch. It used to report
  // `compactNow` returning null, which is NOT "not enough messages" but a real
  // failure (the summarization call threw or came back empty) — and that is now
  // decided in the worker, long after this reply.
  if (result.status === 'queued') return { kind: 'info', text: 'Summarizing session…' }
  // A REFUSAL IS NOT A FAILURE. The server fence (#466) answers 409
  // SESSION_BUSY when the session already has a compaction or a turn on it —
  // which is a normal, expected, retriable condition, and is precisely the
  // second-tab case the fence was BUILT for. Routing it to the generic branch
  // gave that user a red failure toast, while the local `summarizingRef` guard
  // one line above — the same situation, caught in the same tab — got a calm
  // info toast. The advisory/failure split the steer refusal already makes.
  //
  // BY CODE, never by `httpStatus === 409`. This route returns a third 409 —
  // `SESSION_WALLET_MISMATCH`, when a support-seat caller opens a session
  // belonging to another wallet — which is PERMANENT: retrying can never
  // succeed, the answer is to fork. A status-based arm reported that as a calm
  // "try again in a moment".
  if (result.code === 'SESSION_BUSY' || result.code === 'SESSION_MUTATING') {
    return { kind: 'info', text: result.message || 'This session is busy — try again in a moment' }
  }
  return { kind: 'error', text: result.message || 'Failed to summarize session' }
}

/** Live status update from a subagent — appended to the parent's running
 *  task tool-call card while the child runs. Yield-port Day 10b-d UI
 *  follow-up; populated by the 'subagent-update' StreamEvent. */
export interface SubagentUpdate {
  childSessionId: string
  kind: 'tool-start' | 'tool-done' | 'text' | 'completion' | 'error'
  summary: string
  detail?: string
  at: number  // client-side receive timestamp
}

/** Simplified part types for the UI layer */
type MessagePart =
  | { id: string; messageId: string; type: 'text'; content: string; isFinal?: boolean; synthetic?: boolean; cleared?: ClearedAttachment }
  | { id: string; messageId: string; type: 'file'; mime: string; url: string; filename: string; path?: string; source?: string; handle?: string; fileId?: string; previewUrl?: string }
  | { id: string; messageId: string; type: 'tool-call'; toolName: string; callId: string; input: Record<string, unknown>; status: 'pending' | 'running' | 'completed' | 'error'; title?: string; metadata?: Record<string, unknown>; inputSize?: number; subagentUpdates?: SubagentUpdate[] }
  | { id: string; messageId: string; type: 'tool-result'; callId: string; output: string; error?: string; attachments?: Array<{ kind: 'snapshot'; handle: string; mime: string; filename?: string }> }
  | { id: string; messageId: string; type: 'reasoning'; content: string; time?: { start: number; end?: number } }
  | { id: string; messageId: string; type: 'compaction'; summary: string }
  | { id: string; messageId: string; type: 'step'; step: number; action: 'start' | 'finish' }
  | { id: string; messageId: string; type: 'patch'; files?: string[]; snapshots?: Array<{ path: string; action: string; binary?: boolean; fileId?: string }> }
  // A file the agent wrote in the sandbox could NOT be committed to the drive
  // (quota / disk full). Rendered inline in the transcript as a persistent
  // per-file warning so a silently-lost write is never hidden behind the
  // model's "created" narration. Client-synthesised from the `file-sync-failed`
  // stream event (not persisted server-side — it lives for the live session).
  | { id: string; messageId: string; type: 'sync-failed'; path: string; reason: string }

/** Attachment in the input area (pre-send) */
export interface Attachment {
  id: string
  filename: string
  mime: string
  url?: string
  path?: string
  source: 'paste' | 'drop' | 'drive' | 'attach'
  status: 'uploading' | 'ready' | 'error'
  progress?: number
  /** Session-private attachment handle (#639) — carries an already-uploaded
   *  attachment's handle through to the outgoing `file` part so the server
   *  can resolve it without re-uploading. */
  handle?: string
  /** Drive file id (#662) — set once this attachment has been moved to the
   *  drive. A moved attachment opens via `onOpen(path)` (a workspace tab),
   *  never the viewer modal, and offers no Move action. */
  fileId?: string
  /** #662 — dormant copy of the original inline `data:` bytes, kept when an
   *  inline attachment is moved so a later drive-file deletion can revert the
   *  chip to its original inline preview (see the FilePart schema field). */
  previewUrl?: string
  /** #1292 — raw bytes this attachment inlines into the send, set at attach
   *  time on the inline route: what it holds against the message's 10 MB
   *  inline budget (`inlineBudget.ts`). Composer-only; never sent. */
  inlineBytes?: number
  /** The raw size of a TEXT attachment, inline or uploaded — what it would add to
   *  the message's text budget (`messageTextBudgetNotices`). Composer-only; never
   *  sent. */
  textBytes?: number
  /** #1292 — set when the file will be attached but the AI won't see it
   *  (`aiVisibilityNotice`, a code worded by `aiNoticeMessage` at render); the
   *  composer shows it. Composer-only; never sent. */
  aiNotice?: import('./attachmentLogic.js').AiNotice
}

/** Simplified session info for the UI layer */
interface AgentSessionInfo {
  id: string
  title?: string
  model: string
  status: string
  createdAt?: string
  updatedAt?: string
  /**
   * Whether this conversation can be CONTINUED by the current viewer (0180),
   * computed server-side — it needs the session's wallet and the viewer's, and
   * the second is a membership lookup the browser has no business doing.
   *
   * Absent on an older API during a rolling restart, which is why the composer
   * treats "missing" as writable: failing closed there would disable chat for
   * everyone the moment a deploy lagged.
   */
  wallet?: {
    canSend: boolean
    reason?: { code: string; sessionIsPlatformFunded: boolean; message: string }
  }
  /** Auto mode chosen for this conversation; absent = never chosen (off). */
  autoMode?: boolean
  /** The conversation's stored "Always" answers (allow rules). */
  permission?: Array<{ permission: string; pattern: string; action: string }>
}

/**
 * Diff summary — cumulative file changes in a session.
 *
 * TOTALS only. The per-file breakdown used to ride along here as `paths` +
 * `perFile`; it now lives in its own table and is served paginated from
 * `GET /sessions/:id/diff/files`, because inlining it meant one entry per
 * changed file — ~17 MB for a session that synced a mid-size repository.
 * Nothing on this surface rendered it.
 */
interface SummaryInfo {
  additions: number
  deletions: number
  files: number
  /** The stored per-file detail is known to be incomplete. */
  truncated?: boolean
}

/** Revert state — whether the session's file changes have been rolled back */
interface RevertState {
  reverted: boolean
  info?: {
    messageID: string
    snapshot?: string
    diff?: string
  }
}

/**
 * Merge a server transcript with the rows the client gained while the request
 * for it was in flight.
 *
 * `loadMessages` REPLACES the array, and several unawaited callers can be in
 * flight at once. Two things must survive a response that predates them:
 * optimistic `temp_` rows the server has not persisted yet, and anything that
 * ARRIVED DURING THE FETCH — the user sends 100ms after a reload starts,
 * the send's confirmation (`sentMessageConfirmation`) renames the `temp_` row to its real id, and the slow
 * response then writes a transcript from before the send with no `temp_` left
 * to preserve. The message they just sent disappears.
 *
 * Keyed on "not present at request start", which is exactly the set this
 * response is ignorant of. That needs no clock, and it cannot resurrect a
 * message the server deleted — a revert's rows WERE present at start.
 *
 * Rows are re-anchored IN PLACE, never appended. `steerMessage` splices its
 * bubble BEFORE the last assistant message (a trailing user bubble reads
 * backwards) and the worker persists a matching pre-assistant timestamp so a
 * later reload agrees; concatenating threw a mid-flight steer below the
 * assistant reply until some later full reload corrected it.
 *
 * Pure and exported so the ordering can be tested directly — the shape of this
 * is not something a source-read assertion can check.
 */
export function mergeTranscript(
  server: MessageWithParts[],
  local: MessageWithParts[],
  idsAtRequestStart: Set<string>,
  /** E7 Codex round 7: optimistic steer bubbles whose route response has not arrived yet — kept like `temp_` rows
   *  (a reload must not drop one before `reconcileSteerBubble` knows the id it was saved under). */
  keepIds?: ReadonlySet<string>,
): MessageWithParts[] {
  const serverIds = new Set(server.map(m => m.id))
  // Each preserved row remembers which of its local predecessors the server
  // also returned; `null` means it precedes every surviving server row.
  const anchorOf = new Map<string, string | null>()
  let lastServerSeen: string | null = null
  for (const m of local) {
    if (serverIds.has(m.id)) { lastServerSeen = m.id; continue }
    if (m.id.startsWith('temp_') || !idsAtRequestStart.has(m.id) || keepIds?.has(m.id)) {
      anchorOf.set(m.id, lastServerSeen)
    }
  }
  if (anchorOf.size === 0) return server

  const byAnchor = new Map<string | null, MessageWithParts[]>()
  for (const m of local) {
    if (!anchorOf.has(m.id)) continue
    const key = anchorOf.get(m.id)!
    const bucket = byAnchor.get(key)
    if (bucket) bucket.push(m)
    else byAnchor.set(key, [m])
  }
  const merged: MessageWithParts[] = [...(byAnchor.get(null) ?? [])]
  for (const m of server) {
    merged.push(m)
    const after = byAnchor.get(m.id)
    if (after) merged.push(...after)
  }
  return merged
}

/**
 * A turn is LIVE — the four states in which the transcript is being written and
 * a wholesale `loadMessages()` would destroy client-built state.
 *
 * ONE implementation, because there were three and they disagreed. Both chat
 * surfaces computed this same four-way union inline as `isProcessing` (to
 * decide Stop vs Send), while the two compaction deferral decisions tested
 * `status === 'processing'` alone — so `submitting`, `awaiting_permission` and
 * `awaiting_answer` took the immediate-refetch branch that `resyncAfterTurn`
 * exists to avoid. Sending straight after a summarize and hitting a permission
 * prompt within the poll's 4s tick is enough to reach it.
 */
export function isTurnLive(status: string): boolean {
  return status === 'processing'
    || status === 'submitting'
    || status === 'awaiting_permission'
    || status === 'awaiting_answer'
}

/**
 * Work the turn is doing OUTSIDE any tool call.
 *
 * Named rather than inlined because four places have to agree on it: the state
 * field, the reducer, and the two turn-ending clears. `phase` mirrors
 * `SyncProgress['phase']` in shared/storage — not imported, because
 * `features/agent/ui` is browser code and that module is not.
 */
export type TurnActivity = {
  kind: 'sync'
  phase: 'walk' | 'hash' | 'sync' | 'delete'
  done: number
  total: number
}

interface AgentSessionState {
  session: AgentSessionInfo | null
  messages: MessageWithParts[]
  streamingText: string
  streamingReasoning: string
  /** Client-measured start of the open reasoning block (AG-UI's standard
   *  REASONING_MESSAGE_START carries no timestamp). null = no block open. */
  streamingReasoningStart: number | null
  activeToolCalls: Map<string, { toolName: string; input: Record<string, unknown> }>
  status: string
  pendingPermissions: PermissionRequest[]
  /** ALL pending questions — the model may run several HITL tools in one
   *  step (e.g. render_ui ask + cred_to_env in parallel); a single slot
   *  silently dropped one card and stranded its tool (the "tool render
   *  racing" bug, 2026-07-04). Cards stack in the UI. */
  pendingQuestions: QuestionRequest[]
  /** I198 / D56: the note shown where a question card was when its late answer came
   *  back 409 QUESTION_EXPIRED. Cleared by the next send or the next question. */
  expiredQuestionNotice: ExpiredQuestionNotice | null
  /**
   * Work the turn is doing OUTSIDE any tool call — today, the post-`bash` tree
   * sync, which runs in the worker's `afterToolCall` hook and therefore AFTER
   * `tool-call-done` has already marked every tool card completed.
   *
   * null = nothing to say, which is the resting state and also what every turn
   * teardown sets. Anything that ends a turn MUST clear it: the worker sends an
   * explicit clear, but a dropped frame or a crashed turn would otherwise leave
   * "Syncing…" on screen permanently, and a stale indicator is worse than none.
   */
  turnActivity: TurnActivity | null
  error: string | null
  /**
   * What the reader may DO about `error`, when that is a page in this app.
   *
   * Beside `error` rather than folded into it: `error` is a plain string read
   * by several call sites and both banner renderers already accept a string or
   * an `Error`-ish value, so widening it would be a change to all of them for
   * the sake of one optional field.
   *
   * Only budget exhaustion sets this today, and only for a reader who can act
   * — see `agui-error.ts` for why the other two rows deliberately get nothing.
   */
  errorAction: GateErrorAction | null
  /** Cumulative file change stats for the session */
  summary: SummaryInfo | null
  /** Whether the session has been compacted (older messages summarized) */
  compacted: boolean
  /** A manual "Summarize session" is in flight.
   *
   *  Compaction runs an LLM over the WHOLE transcript — at the trigger point
   *  that is ~240K input tokens, so this is tens of seconds, not a blink. The
   *  request was awaited with no pending state at all: the menu closed and
   *  nothing happened until the transcript silently changed. */
  summarizing: boolean
  /** An AUTO-compaction is running mid-turn. Distinct from `summarizing`,
   *  which is the user pressing "Summarize session": one is the system pausing
   *  a turn, the other is a deliberate act. Both drive the same indicator. */
  compacting: boolean
  /** The message a compaction PAUSE synthesised a `complete` for. Survives the
   *  indicator clear, so the real terminal for it is still recognisable. */
  pausedTerminalId: string | null
  /** A compaction rewrote the transcript this turn; refetch once it ENDS. */
  resyncAfterTurn: boolean
  /** The current `error` is ADVISORY — "not now, try again shortly" — rather
   *  than a stuck session. `dismissError` must not force-unlock for one: that
   *  drains the queue, and for a compaction still waiting (workers saturated,
   *  or parked by `withTenantFairness`) it REMOVES the job the user paid
   *  attention for, leaving the transcript unchanged with no explanation.
   *  Same shape as the steer refusal: an advisory wired to a destructive
   *  control. */
  errorAdvisory: boolean
  /** Current revert state (null = not loaded yet, { reverted: false } = no revert) */
  revertState: RevertState | null
  /** Cumulative cost for this session in USD */
  sessionCost: number
  /** `sessionCost` in display-currency minor units; `null` = canonical USD.
   *  Converted PER ROW server-side — never re-derive it from `sessionCost`. */
  sessionCostDisplayMinor: number | null
  /** B(a): messageId of the last `complete` we applied. AG-UI can deliver
   *  `complete` twice (two live streams for one session, or SSE+bus) — a second
   *  application double-counts sessionCost (#269) and re-commits parts. Dedupe on
   *  it: a `complete` for the same messageId is a no-op. */
  lastCompletedMessageId?: string
  /** Last personaId found in messages (for restoring persona on session load) */
  lastPersonaId?: string
  /** Estimated tokens used in this session (chars / 4) */
  estimatedTokens: number
  /** Model's context window limit in tokens */
  contextLimit: number
  /** Current retry state (null = not retrying) */
  retry: { attempt: number; error: string; delay: number } | null
  /** #762: the live connection to a running turn was lost and recovery gave up; the chat checks quietly until the
   *  server answers again (`reconcileAfterGiveUp`) instead of showing an error. */
  reconnecting: boolean
  /** Session todos */
  todos: TodoItem[]
}

/**
 * Lift the gate action off a thrown value, if it has one.
 *
 * The `catch` this serves receives ANY throw from the send path — a network
 * failure, a JSON parse error, a bug — not only the gate rejection that
 * attaches `gateAction`. So this validates the shape rather than casting: an
 * unrelated error must yield `null` and leave the banner with no button, never
 * a button built from whatever happened to be on the object.
 */
function gateActionOf(err: unknown): GateErrorAction | null {
  if (!(err instanceof Error)) return null
  const a = (err as { gateAction?: unknown }).gateAction
  if (!a || typeof a !== 'object') return null
  const { route, label } = a as { route?: unknown; label?: unknown }
  return route === 'org-billing' && typeof label === 'string' ? { route, label } : null
}

/**
 * Force a session out of a non-terminal ('processing'/'submitting'/…) status
 * back to idle — unless it has already settled to a terminal state. Shared by
 * BOTH cancel()'s abort-safety timeout (the WS/stream never delivered the
 * cancellation) and the orphan-recovery poll (a worker restart killed the job
 * without emitting a completion event). Extracted so the two callers can't
 * drift. Returns the same reference when nothing changes so setState no-ops.
 */
/**
 * What a terminal `complete` means when its messageId was already claimed.
 *
 * A compaction PAUSE synthesises a `complete` for the interrupted message —
 * purely to reuse the commit — and that claims `lastCompletedMessageId`. When a
 * compaction then cannot continue (it threw, or hit the round cap) the worker
 * publishes the REAL terminal for that same message, and the duplicate guard
 * would drop it, leaving the run on 'processing' with the composer locked.
 *
 * Keyed on `pausedTerminalId`, NOT on `compacting`. An earlier version tested
 * the latter and was defeated by its own sibling fix: the worker clears the
 * indicator BEFORE publishing the terminal (it must — the bridge drops
 * everything after a terminal), so `compacting` is already false by the time
 * the terminal lands. Two fixes that were each correct alone and cancelled out
 * together; only a test that drives the real event ORDER catches that.
 *
 * Returns `null` when the event is a genuine duplicate to ignore.
 */
export function resolveClaimedTerminal<S extends {
  status: string; compacting: boolean; pausedTerminalId: string | null
  turnActivity?: TurnActivity | null
}>(prev: S, messageId: string): S | null {
  if (messageId && messageId === prev.pausedTerminalId) {
    // The run really is over — take the ending, skip the (already-done) commit.
    return { ...prev, compacting: false, status: 'idle', pausedTerminalId: null, turnActivity: null }
  }
  return null
}

/** Every path that ENDS a turn must also drop the compaction indicator.
 *  Only `compaction-progress{active:false}` and `resolveClaimedTerminal` used
 *  to clear it, so pressing Stop during a compaction — the slowest part of a
 *  turn, and therefore the likeliest moment to — left "Summarizing
 *  conversation" pinned on screen until a session switch or reload. */
/**
 * Should the deferred compaction resync run for THIS `complete`?
 *
 * Exported because the reducer lives inside the hook and cannot be driven from
 * a test — and a test that re-implements the rule proves only the copy. That is
 * not hypothetical: the first version of this guard was tested against a
 * hand-written `apply()` in the test file, and mutating the real code changed
 * nothing.
 *
 * A compaction PAUSE borrows the complete reducer to reuse the commit, so from
 * the SECOND round onward (round 1 having set the flag) it would fire the
 * wholesale `loadMessages` replace the deferral exists to avoid.
 */
export function shouldRunDeferredResync(
  prev: { resyncAfterTurn: boolean },
  ev: { compactPending?: boolean },
): boolean {
  return prev.resyncAfterTurn && ev.compactPending !== true
}

export function clearTurnIndicators<S extends {
  compacting: boolean; turnActivity?: TurnActivity | null
}>(prev: S): S {
  // `turnActivity` joins the compaction indicator here for the same reason it
  // was written: the worker sends an explicit clear, but a turn that ENDS —
  // cancelled, failed, or killed mid-sync — may never deliver one. An
  // indicator that only the happy path can switch off is the bug this helper
  // already exists to prevent.
  if (!prev.compacting && prev.turnActivity == null) return prev
  return { ...prev, compacting: false, turnActivity: null }
}

export function forceIdleIfStuck<S extends {
  status: string; compacting?: boolean; turnActivity?: TurnActivity | null
  streamingText?: string; streamingReasoning?: string; streamingReasoningStart?: number | null
}>(prev: S, reason: string): S {
  if (prev.status !== 'idle' && prev.status !== 'failed') {
    console.warn(`[Agent] ${reason} — forcing idle status`)
    // A worker that died mid-compaction leaves the indicator set; recovering
    // the status without it pins "Summarizing conversation" forever. The same
    // is true of `turnActivity`: this path exists precisely for the turn that
    // never sent its own ending, which is the turn that never sent a clear.
    // The in-flight streamed text goes too: the stream that was building it is
    // aborted (or dead), so it would sit as a half-message beside the reloaded,
    // finished one — or alone, as the "final" answer, until a page reload.
    return {
      ...prev,
      status: 'idle',
      ...(prev.compacting ? { compacting: false } : {}),
      ...(prev.turnActivity != null ? { turnActivity: null } : {}),
      ...(prev.streamingText ? { streamingText: '' } : {}),
      ...(prev.streamingReasoning ? { streamingReasoning: '' } : {}),
      ...(prev.streamingReasoningStart != null ? { streamingReasoningStart: null } : {}),
    }
  }
  return prev
}

/**
 * WHICH stream event dispatches a client-executed tool, and with WHOSE args.
 *
 * Two events can start the same call and they carry DIFFERENT SHAPES:
 *   • `tool-call-start.input` — the model's RAW args, and
 *   • `client-tool-request.args` — the args the WORKER resolved
 *     (share_link turns a `/papan/`-relative path into a concrete file id;
 *     share_link resolves a path to a file id). Only this one carries `payload`.
 *
 * A tool whose handler needs the worker's payload declares
 * `argsFrom: 'worker'` and is NOT dispatched from the model's call — running
 * it there would take the page's once-only run slot (per WAIT; the bare call id
 * and its waits refuse each other) and POST a wrong-args result
 * before the worker's own dispatch lands. That is exactly what shipped: every
 * access tool issued an id-less REST call (`GET /api/folders//permissions`
 * → 404) reported as the opaque "Request failed".
 *
 * Pure so the decision is testable without rendering the hook (same shape as
 * isSessionOrphaned / forceIdleIfStuck above).
 */
export function clientToolDispatch(
  se: SessionProcessor.StreamEvent,
  argsFromOf: (toolName: string) => 'tool-call' | 'worker' | undefined,
): { offer: ClientToolOffer; mayDefer: boolean } | null {
  // The LIVE twin of the `/pending` recovery replay — same event, same args,
  // so the two paths cannot disagree about what the browser executes. It is
  // deferrable for the same reason the replay is: the worker recorded this
  // call before publishing, so the /pending poll will come back for it if the
  // tool's surface is not ready yet.
  if (se.type === 'client-tool-request') {
    // The whole request — its `workspaceId` and `waitId` too (#352 R7/R9), so a
    // live offer and its `/pending` replay key the same wait.
    const { toolName, args, toolCallId, workspaceId, waitId } = se
    return { offer: { toolName, args, toolCallId, ...(workspaceId ? { workspaceId } : {}), ...(waitId ? { waitId } : {}) }, mayDefer: true }
  }
  if (se.type !== 'tool-call-start') return null
  if (argsFromOf(se.toolName) === 'worker') return null
  // NOT deferrable: at tool-call-start the worker may not have reached
  // ctx.clientTool, so nothing is recorded to retry from.
  return { offer: { toolName: se.toolName, args: se.input as Record<string, unknown>, toolCallId: se.callId }, mayDefer: false }
}

/**
 * The readiness-gate decision the client-tool executor makes: when a dispatch a
 * retry WILL follow (`mayDefer`) targets a tool whose `ready()` says its surface
 * isn't ready, DEFER — do NOT consume the callId or POST a result; the next
 * `/pending` poll retries until ready. Extracted from `runClientToolByCall` (a
 * hook-internal closure) so this decision is unit-testable against the REAL
 * registered tools + their `ready()` — e.g. #356's preview_eval/preview_screenshot
 * deferring while a preview reload is in flight.
 *
 * `entry.maxDeferMs` (with the call's `waitKey`) bounds the deferral: some tabs
 * `ready()` waits on never render a preview until the user acts (an uninstalled
 * PDApp, a Settings face, a triggers-only app, a page awaiting consent, an
 * over-ceiling inline page), so an uncapped wait would hang to the worker's
 * ~5 min ceiling instead of answering "no preview open". The cap keeps the
 * reload-remount defer (R5) and bounds the rest. Pure apart from the
 * first-deferred clock in clientToolRun.ts; `now` is injectable.
 */
export function shouldDeferClientTool(entry: AgentToolEntry | undefined, mayDefer: boolean, args: Record<string, unknown> = {}, key?: string, now: number = Date.now()): boolean {
  if (!(mayDefer && entry?.ready && !entry.ready(args))) return false
  return key === undefined ? true : withinDeferWindow(key, entry.maxDeferMs, now)
}

// The page-wide once-only run set (#352) lives in clientToolRun.ts, keyed per
// WAIT (`waitKey`) so a resumed call's second wait still runs (R9); a bare call
// id (the model's tool-call-start) and its waits refuse each other.

/**
 * Decide whether the orphan-recovery poll should treat a 'processing' session
 * as genuinely dead. `/pending` returning null is ambiguous — a happily-running
 * job with no HITL prompt ALSO has no pending request — so this confirms
 * against the authoritative busy set (`GET /sessions/status`, the same
 * BullMQ active+waiting scan the rail dots use). Only when THIS session is
 * absent from that set is the run truly orphaned. Returns true = safe to force
 * idle. Kept pure so it's unit-testable without rendering the hook.
 */
export function isSessionOrphaned(sessionId: string, busy: Array<{ id: string }>): boolean {
  return !busy.some(b => b.id === sessionId)
}

/** How long a LIVE stream may go without a data frame before the orphan poll may
 *  treat it as dead. */
export const ORPHAN_LIVE_STREAM_GRACE_MS = 10_000

/**
 * The orphan poll's whole decision. The job leaving the busy set does NOT mean
 * the browser has its ending: the worker finishes, BullMQ drops the job, and the
 * stream's tail (the last text, RUN_FINISHED → `complete`, the cost) is often
 * still in flight to the browser. Aborting then lost the final text and the
 * completion until a reload (rollout e2e, 2026-10-11: "Orphan recovery … forcing
 * idle" ~5.5 s after the prompt, every failing trace). So while a stream is live
 * for the session, it is left alone unless it has delivered no data frame for
 * {@link ORPHAN_LIVE_STREAM_GRACE_MS} — a genuinely dead stream is still
 * recovered, a little later (or by the lost-connection recovery first). Pure.
 */
export function shouldForceIdleOrphan(o: { busy: boolean; liveRun: boolean; lastFrameAt: number | null; now: number }): boolean {
  if (o.busy) return false
  if (!o.liveRun) return true
  return o.lastFrameAt == null || o.now - o.lastFrameAt > ORPHAN_LIVE_STREAM_GRACE_MS
}

/** Simplified permission request for UI — matches what the SSE stream sends */
interface PermissionRequest {
  id: string
  sessionID: string
  permission: string
  patterns: string[]
  metadata: Record<string, unknown>
  description?: string
  /** #648: when the person was first asked (ms); a long-waiting prompt shows its age. */
  askedAt?: number
  /** False when the card offers no "Always" (every bash ask — always-grant.ts). Absent = offered. */
  alwaysOffered?: boolean
}

/** Simplified question request for UI */
interface QuestionRequest {
  id: string
  sessionID: string
  questions: Array<{
    question: string
    header: string
    options: Array<{ label: string; description: string }>
    multiple?: boolean
  }>
  /** #648: when the person was first asked (ms). */
  askedAt?: number
  /** Owning tool call — lets the reducer dismiss exactly the question a
   *  completing tool owned (parallel HITL tools each own one). */
  /** `toolName` is stamped server-side and is the model-proof discriminant for
   *  an access card — see parseAccessRequest. Optional: a previous-build worker
   *  does not send it. */
  tool?: { messageID?: string; callID?: string; toolName?: string }
}

/** Todo item from the session */
interface TodoItem {
  id: string
  content: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  priority: 'low' | 'medium' | 'high'
}

export type { MessagePart, PermissionRequest, QuestionRequest, SummaryInfo, RevertState, TodoItem }

/** #1316: the send's "may not have been sent" error (P8) — its session ended before it could be told what happened. */
const sessionEndedError = (): Error => new Error(i18n.t('agent:send.sessionEnded'))

/**
 * Map an API part (DB schema fields) to UI MessagePart shape(s).
 * Returns an array because a completed tool part produces both a tool-call and a tool-result.
 */
/**
 * AG-UI's run transport uses raw `fetch()` (it needs the streaming SSE
 * response), which bypasses `@papan/api-client`'s automatic
 * `x-tenant-id` injection. Without it, `resolveTenant` falls back to
 * the user's PERSONAL tenant, so a run in an ORG-tenant workspace
 * fails `requireWorkspace` → 404 "Project not found". Set the same
 * active-tenant header every other API call carries.
 */
function aguiHeaders(extra?: Record<string, string>, tenant?: { id: string | null }): Record<string, string> {
  // #352 I3b: a re-POST of a kept result sends the tenant active when it was KEPT —
  // never the one active now (`null` = the original sent no header, nor does this).
  const tid = tenant ? tenant.id : getActiveTenantId()
  return { 'content-type': 'application/json', ...(tid ? { 'x-tenant-id': tid } : {}), ...(extra ?? {}) }
}

/**
 * #352 I3b — POST a KEPT client-tool result (clientToolRun.ts). Page-level, not a hook
 * closure: the pump calls it after a reload with no hook mounted. Everything comes
 * from the kept entry — its tenant, session, workspace and wait — never from whatever
 * the page shows now. A hung request ends as a network failure (30 s), so an entry
 * never sits in flight forever. `resumeReady` (the call was suspended and its turn is
 * now answered) resumes the KEPT session: a mounted hook starts it, else the first
 * hook to mount does.
 */
/** #352 owner ruling 2026-10-19 — the registered handler + readiness gate a REPLAY of a
 *  kept result runs (clientToolRun.ts `replayKeptResult`). */
const replayLookup: ReplayLookup = (toolName) => {
  const e = getAgentTool(toolName)
  return e && e.transport === 'client-executed' && e.handler
    ? { handler: e.handler, ...(e.ready ? { ready: e.ready } : {}), ...(e.canResolve ? { canResolve: e.canResolve } : {}), ...(e.replayable ? { replayable: e.replayable } : {}), ...(e.pinReplayTarget ? { pinsTarget: true } : {}) }
    : undefined
}

export async function postKeptResult(key: string): Promise<void> {
  const kept = keptResult(key)
  if (!kept) return
  // #352 I1: every attempt first refreshes this tab's `ran` claim (with the KEPT holder,
  // tenant and wait), so another tab never re-runs the wait while this one can still
  // deliver its result. Fire and forget: the POST does not wait on it.
  void refreshKeptClaim(key).catch(() => {})
  beginAttempt(key)
  try {
    const res = await fetch(`${getApiBase()}/api/agent/agui`, {
      method: 'POST',
      credentials: 'include',
      headers: aguiHeaders(undefined, { id: kept.tenantId }),
      ...(typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function' ? { signal: AbortSignal.timeout(30_000) } : {}),
      body: JSON.stringify({
        threadId: kept.sessionId,
        runId: `tool-result-${kept.toolCallId}`,
        state: {},
        messages: [{ id: `tr_${kept.toolCallId}`, role: 'tool', toolCallId: kept.toolCallId, content: kept.body }],
        tools: [],
        context: [],
        forwardedProps: { workspaceId: kept.workspaceId, ...(kept.waitId ? { clientToolWaitId: kept.waitId } : {}) },
      }),
    })
    const json = await res.json().catch(() => null) as { resumeReady?: boolean; accepted?: boolean } | null
    const state = endAttempt(key, res.status, json?.accepted)
    if (state !== 'taken') console.warn(`[agent] client-tool result POST answered ${res.status} — kept, re-sent later (#352 I3b)`)
    if (json?.resumeReady) requestResume(kept.sessionId, kept.workspaceId)
  } catch (err) {
    endAttempt(key, 'network')
    console.warn('[agent] client-tool result POST failed — kept, re-sent later (#352 I3b):', err)
  }
}

/**
 * The ONE filter for a tool result's snapshot attachments (#639). Both the
 * reload path (`mapApiPartToUi`, reading a persisted part's `state.attachments`)
 * and the LIVE path (`useAgentSession`'s `tool-call-done` reducer, reading
 * `event.metadata.attachments`) run this so they cannot diverge — a snapshot
 * must render the same whether it arrives on the stream or after a reload.
 * Keeps only well-formed `{kind:'snapshot', handle}` entries; anything missing
 * a string `handle` or the `snapshot` kind is dropped rather than rendered as a
 * broken preview. Exported for direct unit testing.
 */
export function pickSnapshotAttachments(
  raw: unknown,
): Array<{ kind: 'snapshot'; handle: string; mime: string; filename?: string }> {
  if (!Array.isArray(raw)) return []
  return (raw as Array<{ kind?: string; handle?: string; mime?: string; filename?: string }>)
    .filter((a): a is { kind: 'snapshot'; handle: string; mime: string; filename?: string } =>
      !!a && a.kind === 'snapshot' && typeof a.handle === 'string')
}

// Exported so the mapping can be unit-tested directly (same reasoning as
// `mergeTranscript`/`isTurnLive` above) — it is otherwise only reached
// through the full `useAgentSession` hook.
export function mapApiPartToUi(p: any, messageId: string): MessagePart[] {
  const base = { id: p.id || '', messageId: p.messageID || messageId }

  switch (p.type) {
    case 'text':
      // `synthetic` rides along so the transcript can hide compaction's
      // fabricated user turns on rows written before the message-level flag.
      // `synthetic` rides along so the transcript can hide compaction's
      // fabricated user turns on rows written before the message-level flag.
      // 0353: a text part that stands where a cleared attachment was.
      // Read off `metadata` so the bubble can draw it as a greyed chip
      // where the file's chip used to be, in the member's own language,
      // instead of dropping the stored English sentence into the middle
      // of their own words. `null` for every other text part.
      return [{ ...base, type: 'text', content: p.text || '', synthetic: p.synthetic, cleared: readClearedAttachment(p.metadata) ?? undefined }]
    case 'file':
      // `handle` (#639) rides along so a later turn can resolve "that image
      // from 40 turns ago" back to the stored session-private attachment.
      return [{ ...base, type: 'file', mime: p.mime || '', url: p.url || '', filename: p.filename || '', path: p.path, source: p.source, handle: p.handle, fileId: p.fileId, previewUrl: p.previewUrl }]
    case 'tool': {
      const row = apiToolPartToRow(p)
      const callId = row.callId
      const toolCall: MessagePart = {
        ...base,
        type: 'tool-call',
        toolName: row.toolName,
        callId,
        input: row.input,
        status: row.status,
        title: row.title,
        metadata: row.metadata,
      }
      const parts: MessagePart[] = [toolCall]
      // Emit a tool-result part so MessageBubble can match output by callId
      if (row.output !== undefined) {
        // #639: carry the result's snapshot attachments (preview_screenshot's
        // CAS-uploaded capture — `kind:'snapshot'` + `handle`) into the client
        // model so MessageBubble can render them as clickable chips. The raw
        // persisted part already has them on `state.attachments`; only `output`
        // was being copied, so the snapshot was invisible to the UI.
        const snapshotAttachments = pickSnapshotAttachments(p.state.attachments)
        parts.push({
          id: `${base.id}_result`, messageId: base.messageId, type: 'tool-result', callId, output: p.state.output,
          ...(snapshotAttachments.length ? { attachments: snapshotAttachments } : {}),
        })
      } else if (row.error !== undefined) {
        parts.push({ id: `${base.id}_result`, messageId: base.messageId, type: 'tool-result', callId, output: '', error: row.error })
      }
      return parts
    }
    case 'reasoning':
      return [{ ...base, type: 'reasoning', content: p.text || '', time: p.time }]
    case 'compaction':
      return [{ ...base, type: 'compaction', summary: p.summary || '' }]
    case 'step-start':
      return [{ ...base, type: 'step', step: 0, action: 'start' }]
    case 'step-finish':
      return [{ ...base, type: 'step', step: 0, action: 'finish' }]
    case 'patch':
      // Pass through patch parts for hasChanges detection (not rendered in UI).
      // Narrowed to the fields the client type declares: rows written before
      // #768 still carry both sides' full file text, and nothing in the browser
      // reads it, so it is not kept in client state.
      return [{
        ...base,
        type: 'patch',
        snapshots: Array.isArray(p.snapshots)
          ? p.snapshots.map((s: any) => ({ path: s.path, action: s.action, binary: s.binary, fileId: s.fileId }))
          : p.snapshots,
      }]
    default:
      // Unknown part type — render as text fallback
      return [{ ...base, type: 'text', content: '' }]
  }
}

/**
 * Rewrite the matching `file` part across all messages to point at the drive
 * file it was moved into (#662) — `fileId`+`path` set, `url` emptied, and
 * `handle` dropped so the chip resolves to the drive file (workspace tab,
 * live content, versions) instead of the private snapshot. Backs
 * `moveAttachmentPart` (the dialog's local `onMoved` patch). Pure/exported
 * for direct unit testing — same reasoning as `mergeTranscript`/
 * `placeSteerBubble` above.
 */
export function applyAttachmentMove(
  messages: MessageWithParts[],
  partId: string,
  drive: { fileId: string; path: string },
): MessageWithParts[] {
  // Reference-stable like `applyAttachmentDeletion`: an unaffected message (the
  // overwhelming majority — one part in one message moves) keeps its identity,
  // and a no-match returns the same `messages`, so a future `React.memo` on
  // message identity stays effective.
  let changed = false
  const next = messages.map(m => {
    let partChanged = false
    const parts = m.parts.map(p => {
      if (p.type === 'file' && p.id === partId) {
        partChanged = true
        // Stash the original inline bytes so a later drive-file deletion can
        // revert the chip to its inline preview (#662). `url` is still emptied
        // so the model sees the drive path, not these bytes.
        const previewUrl = p.url && p.url.startsWith('data:') ? p.url : p.previewUrl
        return { ...p, fileId: drive.fileId, path: drive.path, previewUrl, url: '', handle: undefined }
      }
      return p
    })
    if (!partChanged) return m
    changed = true
    return { ...m, parts }
  })
  return changed ? next : messages
}

/**
 * Apply a `message.part.updated` bus event (fired by `Session.updatePart` —
 * see session.ts — e.g. an AI-tool move via `repointAttachmentPartsToDrive`,
 * or a move landing from another tab) to the messages state.
 *
 * Routed through `mapApiPartToUi` — the SAME mapper the reload path uses —
 * rather than a raw `{ ...old, ...event.part }` merge: `JSON.stringify`
 * drops any key whose value is `undefined` (the cleared `handle` on a moved
 * part is written as `handle: undefined`, see `repointAttachmentPartsToDrive`),
 * so the wire payload never carries that key at all and a spread merge would
 * leave the OLD value in place instead of clearing it. `mapApiPartToUi`
 * reads each field by property access (`p.handle`), which is `undefined`
 * whether or not the key exists on the parsed object, so it clears
 * correctly either way. Pure/exported for direct unit testing.
 */
export function applyPartUpdateEvent(
  messages: MessageWithParts[],
  rawPart: { id: string; messageID: string } & Record<string, unknown>,
): MessageWithParts[] {
  const uiParts = mapApiPartToUi(rawPart, rawPart.messageID)
  const uiPartIds = new Set(uiParts.map(up => up.id))
  // Reference-stable like `applyAttachmentDeletion`: only the message that owns
  // the updated part is reallocated; the rest keep their identity, and a
  // no-match returns the same `messages`.
  let changed = false
  const next = messages.map(m => {
    if (!m.parts.some(p => uiPartIds.has(p.id))) return m
    changed = true
    return { ...m, parts: m.parts.map(p => uiParts.find(up => up.id === p.id) ?? p) }
  })
  return changed ? next : messages
}

/**
 * Swap the live parts of the named tool calls in message `messageId` for the
 * server's saved ones (#874): an oversized live tool result / metadata frame was
 * cut, and the saved part — written when the tool finished — has it all. Matched
 * by part type + `callId`; every other part (the streamed text included) is left
 * exactly as it is. A targeted patch, never a transcript replace: a wholesale
 * `loadMessages` at turn end races the worker's save (#481 item 4).
 * Pure/exported for tests.
 */
export function patchToolCallParts(
  messages: MessageWithParts[],
  messageId: string,
  serverParts: MessagePart[],
  callIds: Set<string>,
): MessageWithParts[] {
  const key = (p: MessagePart) => {
    const callId = (p as { callId?: string }).callId
    return (p.type === 'tool-call' || p.type === 'tool-result') && callId && callIds.has(callId) ? `${p.type}:${callId}` : null
  }
  // Only a call whose SAVED tool part is finished: one the worker failed to
  // save is still `running`, and putting that back would undo the live card
  // (and `finalizeStaleToolCalls`) for a spinner.
  const finished = new Set(serverParts
    .filter(p => p.type === 'tool-call' && ((p as { status?: string }).status === 'completed' || (p as { status?: string }).status === 'error'))
    .map(p => (p as { callId?: string }).callId))
  const saved = new Map<string, MessagePart>()
  for (const p of serverParts) {
    const k = key(p)
    if (k && finished.has((p as { callId?: string }).callId)) saved.set(k, p)
  }
  if (saved.size === 0) return messages
  let changed = false
  const next = messages.map(m => {
    if (m.id !== messageId) return m
    const parts = m.parts.map(p => { const k = key(p); const sp = k ? saved.get(k) : undefined; if (sp) { changed = true; return sp } return p })
    return changed ? { ...m, parts } : m
  })
  return changed ? next : messages
}

/**
 * Set the text parts of message `id` to `text` (#874: the redacted text of a
 * sent message whose confirmation could not carry it). Pure/exported for tests.
 */
export function patchMessageText<S extends { messages: MessageWithParts[] }>(state: S, id: string, text: string): S {
  if (!state.messages.some(m => m.id === id)) return state
  return {
    ...state,
    messages: state.messages.map(m => m.id === id
      ? { ...m, parts: m.parts.map(p => p.type === 'text' ? { ...p, content: text } : p) }
      : m),
  }
}

/**
 * #874 — whether a session can take a whole-transcript reload NOW: only when no
 * turn is live and it is the one on screen. Mid-turn a `loadMessages` replaces
 * the transcript under the streaming message, and at the turn's end it races
 * the worker's save (#481 item 4); a session not on screen loads when opened.
 * Used for an oversized non-part event and a failed `contentOmitted` fetch.
 * Pure/exported for direct unit testing.
 */
export function canReloadNow(status: string, displayed: boolean): boolean {
  return !isTurnLive(status) && displayed
}

/**
 * Clear the moved-to-drive state on every `file` part that points at a drive
 * file which has just been DELETED (#662 slice 9) — `fileId`/`path` dropped.
 * The "In the drive" tick disappears SILENTLY (no toast/badge). If the move
 * stashed the original inline bytes (`previewUrl`), `url` is restored from it
 * so the chip reverts to its original inline preview; otherwise `url` is empty
 * and the chip is a plain, non-opening reference. Backs both the proactive
 * realtime clear (`file:deleted` → `clearMovedAttachment`) and the lazy
 * fallback (an explicit open that 404s). Matched by `fileId` ALONE, so an
 * inline/handle part that was never moved is left untouched.
 *
 * Returns the SAME `messages` reference when nothing matched — a `file:deleted`
 * for an unrelated drive file is the common case (every tenant delete fires
 * one), and a no-op there must neither re-render nor trigger a persist.
 * Pure/exported for direct unit testing — same reasoning as
 * `applyAttachmentMove`/`applyPartUpdateEvent` above.
 */
export function applyAttachmentDeletion(
  messages: MessageWithParts[],
  deletedFileId: string,
): MessageWithParts[] {
  let changed = false
  const next = messages.map(m => {
    let partChanged = false
    const parts = m.parts.map(p => {
      if (p.type === 'file' && p.fileId === deletedFileId) {
        partChanged = true
        // Revert to the original inline preview if we stashed it on the move
        // (#662) — the chip becomes a clickable inline attachment again rather
        // than a dead reference. Otherwise it's a plain, non-opening chip.
        return { ...p, fileId: undefined, path: undefined, url: p.previewUrl ?? '', previewUrl: undefined }
      }
      return p
    })
    if (!partChanged) return m
    changed = true
    return { ...m, parts }
  })
  return changed ? next : messages
}

/**
 * Update the `path` (and label) of every moved `file` part matching a drive
 * `fileId` whose file has MOVED/renamed (#662) — the chip follows the file to
 * its new location (cloud stays, opens the right place) instead of reverting
 * because its frozen path went stale. Matched by `fileId` ALONE (the stale path
 * is exactly what's being replaced). Reference-stable like the siblings above:
 * unaffected messages keep identity, and a no-op (path already current) returns
 * the same `messages` reference.
 */
export function applyAttachmentPathRefresh(
  messages: MessageWithParts[],
  fileId: string,
  newPath: string,
): MessageWithParts[] {
  const filename = newPath.split('/').pop() || undefined
  let changed = false
  const next = messages.map(m => {
    let partChanged = false
    const parts = m.parts.map(p => {
      if (p.type === 'file' && p.fileId === fileId && p.path !== newPath) {
        partChanged = true
        return { ...p, path: newPath, ...(filename ? { filename } : {}) }
      }
      return p
    })
    if (!partChanged) return m
    changed = true
    return { ...m, parts }
  })
  return changed ? next : messages
}

/**
 * Follow a drive file whose `fileId` CHANGED — a SYNC-DETECTED agent `mv`
 * (#662 follow-move) mints a NEW drive row, so unlike a drive-UI drag the id is
 * not preserved. The `file:moved` event carries `previousFileId` (the old id the
 * chip stored) and `newFileId`. Re-point every `file` part matching the OLD id
 * onto the NEW id + path + basename. Reference-stable like the siblings above.
 */
export function applyAttachmentIdSwap(
  messages: MessageWithParts[],
  previousFileId: string,
  newFileId: string,
  newPath: string,
): MessageWithParts[] {
  const filename = newPath.split('/').pop() || undefined
  let changed = false
  const next = messages.map(m => {
    let partChanged = false
    const parts = m.parts.map(p => {
      if (p.type === 'file' && p.fileId === previousFileId && (p.fileId !== newFileId || p.path !== newPath)) {
        partChanged = true
        return { ...p, fileId: newFileId, path: newPath, ...(filename ? { filename } : {}) }
      }
      return p
    })
    if (!partChanged) return m
    changed = true
    return { ...m, parts }
  })
  return changed ? next : messages
}

/** Draft session IDs are client-only — no server session exists yet */
export const DRAFT_PREFIX = 'draft_'
export function isDraftSession(id: string | null): boolean {
  return !!id && id.startsWith(DRAFT_PREFIX)
}

// ─── Shared per-session store (Case 1) ────────────────────────────────
//
// The chat surfaces (chat-home HeroChat, workspace ChatPanel, conflict chat)
// each mount their own `useAgentSession`, and the workspace tab bar reuses a
// SINGLE instance across sessions by swapping the `sessionId` argument. If
// each instance owned its state in component-local `useState`, (a) a run's
// stream landed in whichever session the instance currently displayed — the
// tab-switch "wrong chat" bug — and (b) navigating chat-home → workspace threw
// the live state away and re-fetched.
//
// Instead, reducer state lives in a module store keyed by sessionId. The hook
// is a thin `useSyncExternalStore` view over the entry for the session it's
// showing, and a run writes to ITS OWN session's entry (see `applyTarget` in
// sendMessage) regardless of what any surface currently displays. No two
// instances mount the same sessionId at once (the surfaces are route-exclusive
// and conflict chat uses a fresh child session), so no owner election is
// needed — sharing is sequential (unmount/remount) plus runs that outlive the
// surface that started them.
function makeInitialSessionState(): AgentSessionState {
  return {
    session: null,
    messages: [],
    streamingText: '',
    streamingReasoning: '',
    streamingReasoningStart: null,
    activeToolCalls: new Map(),
    status: 'idle',
    pendingPermissions: [],
    pendingQuestions: [],
    expiredQuestionNotice: null,
    turnActivity: null,
    error: null,
    errorAction: null,
    summary: null,
    compacted: false,
    summarizing: false,
    compacting: false,
    pausedTerminalId: null,
    resyncAfterTurn: false,
    errorAdvisory: false,
    revertState: null,
    sessionCost: 0,
    sessionCostDisplayMinor: null,
    estimatedTokens: 0,
    contextLimit: 0,
    retry: null,
    reconnecting: false,
    todos: [],
  }
}

interface SessionStoreEntry {
  state: AgentSessionState
  listeners: Set<() => void>
  /** Mounted hook instances viewing this session — for entry GC. */
  subscribers: number
  /** The OWNER token of the run currently streaming into this entry — a send loop OR
   *  an attach — or null. An owner-token (not a bare boolean) because BOTH the send
   *  loop's and an attach's `finally` release it: a bare boolean let a superseded/
   *  stale finally() clear a DIFFERENT owner's live claim, so the 4s watch stacked a
   *  second resume stream → doubled transcript + double-counted cost. A remounting
   *  surface / the watch checks `getLiveRun` to avoid a SECOND subscription; only the
   *  token holder can release. (#269 re-verify, finding 6.) */
  liveRun: object | null
  /** B(b): the AbortController of the run currently streaming into this entry.
   *  claimLiveRun aborts it before storing a new one, so a superseding run
   *  (resend after a zombie, a fresh send) REPLACES the previous stream instead
   *  of leaking it — the send loop had NO controller at all, so nothing could
   *  ever close it. B(a) makes any residual overlap harmless; this stops it at
   *  the source. */
  liveCtrl: AbortController | null
  /** R2.1: the runId of the run currently streaming into this entry. The client
   *  mints it BEFORE the /agui POST, so cancel() can send it to /abort even if the
   *  POST hasn't reached the server yet — the durable abort is then keyed by this
   *  id and honored at pickup. Null when no send-initiated run is live. */
  liveRunId: string | null
  /** When the live stream last delivered a data frame (ms; set at claim) — the
   *  orphan poll's "is it still delivering" signal (shouldForceIdleOrphan). */
  lastFrameAt: number | null
  /** The send's optimistic user bubble (`temp_…`) for the live run, if a send owns it. */
  liveTempId: string | null
  /** The live stream delivered at least one data frame — the server admitted the run,
   *  so its user message is stored (the proof #813 uses). */
  sawFrame: boolean
}

const sessionStore = (() => {
  const entries = new Map<string, SessionStoreEntry>()
  const ensure = (key: string): SessionStoreEntry => {
    let e = entries.get(key)
    if (!e) {
      e = { state: makeInitialSessionState(), listeners: new Set(), subscribers: 0, liveRun: null, liveCtrl: null, liveRunId: null, lastFrameAt: null, liveTempId: null, sawFrame: false }
      entries.set(key, e)
    }
    return e
  }
  // Drop an entry once nothing references it (no mounted view, no live run) so
  // a long session with many chats doesn't leak state forever. Also require zero
  // listeners: `subscribe` (useSyncExternalStore) registers a listener in a DIFFERENT
  // effect than `acquire` bumps `subscribers`, so a bus event's gcIfIdle landing in
  // that mount gap must not delete an entry a live view's callback is bound to — that
  // silently froze the view's re-renders. (#269 re-verify, finding 7.)
  const gc = (key: string) => {
    const e = entries.get(key)
    if (e && e.subscribers === 0 && !e.liveRun && e.listeners.size === 0) entries.delete(key)
  }
  return {
    getState: (key: string): AgentSessionState => ensure(key).state,
    // Snapshot of the currently-loaded entry keys (each key IS a session id, or
    // `__none__`/`draft_*`). Used by the drive `file:deleted` sweep so a moved
    // chip clears in EVERY loaded session, not just whichever one is on screen.
    keys: (): string[] => [...entries.keys()],
    subscribe: (key: string, cb: () => void): (() => void) => {
      const e = ensure(key)
      e.listeners.add(cb)
      // GC on listener-removal too: on unmount `release()` (subscribers→0) can run
      // while listeners.size is still 1 (gc skips), then this cleanup removes the last
      // listener — without gc'ing here the entry would be stranded forever. Ordering-
      // independent: whichever of release/this runs last drops the idle entry. (#269 re-verify #7.)
      return () => { e.listeners.delete(cb); gc(key) }
    },
    update: (key: string, updater: (prev: AgentSessionState) => AgentSessionState): void => {
      const e = ensure(key)
      const next = updater(e.state)
      if (next === e.state) return
      e.state = next
      e.listeners.forEach(l => l())
    },
    acquire: (key: string): void => { ensure(key).subscribers++ },
    release: (key: string): void => {
      const e = entries.get(key)
      if (!e) return
      e.subscribers = Math.max(0, e.subscribers - 1)
      gc(key)
    },
    // Claim this entry's live run with an owner token (caller checks getLiveRun first).
    // B(b): abort the PREVIOUS stream's controller before storing — a superseding
    // run replaces, not leaks. `ctrl` is optional so a caller without a controller
    // (older call site mid-deploy) still claims, it just can't be aborted.
    claimLiveRun: (key: string, token: object, ctrl?: AbortController, opts?: { tempId?: string }): void => {
      const e = ensure(key)
      if (e.liveCtrl && e.liveCtrl !== ctrl) e.liveCtrl.abort()
      e.liveRun = token
      e.liveCtrl = ctrl ?? null
      e.lastFrameAt = Date.now() // a stream that has not sent its first frame yet is not "silent" yet
      e.liveTempId = opts?.tempId ?? null
      e.sawFrame = false
    },
    // Release ONLY if the caller still owns the claim — a stale/superseded finally()
    // for a different owner no-ops, so it can't unblock a live run (#269 re-verify #6).
    releaseLiveRun: (key: string, token: object): void => {
      const e = entries.get(key)
      if (!e || e.liveRun !== token) return
      e.liveRun = null
      e.liveCtrl = null
      e.liveRunId = null
      e.lastFrameAt = null
      e.liveTempId = null
      e.sawFrame = false
      gc(key)
    },
    // The live stream delivered a data frame (both read loops call it).
    noteFrame: (key: string): void => { const e = entries.get(key); if (e?.liveRun) { e.lastFrameAt = Date.now(); e.sawFrame = true } },
    /** The live run's own optimistic bubble and whether its stream delivered a frame. */
    getLiveRunInfo: (key: string): { tempId: string | null; sawFrame: boolean } | null => {
      const e = entries.get(key)
      return e?.liveRun ? { tempId: e.liveTempId, sawFrame: e.sawFrame } : null
    },
    getLastFrameAt: (key: string): number | null => entries.get(key)?.lastFrameAt ?? null,
    getLiveRun: (key: string): boolean => entries.get(key)?.liveRun != null,
    /** #1316: does `token` still own this entry's live run (a newer claim — the next session's attach — does not)? */
    ownsLiveRun: (key: string, token: unknown): boolean => entries.get(key)?.liveRun === token,
    // R2.1: record / read the live run's client-minted runId so cancel() can target
    // it precisely (a cancel that races ahead of the /agui POST still names the run).
    setLiveRunId: (key: string, runId: string): void => { ensure(key).liveRunId = runId },
    getLiveRunId: (key: string): string | undefined => entries.get(key)?.liveRunId ?? undefined,
    // B(b): abort whatever stream is live for this session WITHOUT touching the
    // owner token. Used when forcing idle — if we're declaring the run over, its
    // stream is by definition no longer delivering truth, so close it.
    abortLiveRun: (key: string): void => { entries.get(key)?.liveCtrl?.abort() },
    // Drop an entry that no view is subscribed to and no run keeps live. Used after a
    // bus event / background write for a session that's neither displayed nor live —
    // otherwise those `ensure()`-minted entries accumulate for the page lifetime
    // (#269 review, finding 7). Its state is re-fetched when the session is opened.
    gcIfIdle: (key: string): void => gc(key),
  }
})()

/** Test-only: the live-run half of the module store (claim / frames / lookup), so a
 *  test can drive it exactly as the send loop and the orphan poll do. */
export const __liveRunStoreForTests = {
  claimLiveRun: sessionStore.claimLiveRun,
  releaseLiveRun: sessionStore.releaseLiveRun,
  noteFrame: sessionStore.noteFrame,
  /** #813's registry, as a canonical load reads it (mark at start, take at merge). */
  storedTempMark: () => storedTempMark(),
  takeStoredTemps: (sid: string, mark: number) => takeStoredTemps(sid, mark),
}

/** Which optimistic bubble a forced idle may hand to the next canonical load as
 *  STORED: the run's OWN tempId, and only once its stream delivered a frame (the
 *  server admitted it, so the message is stored). A POST that stalled before any
 *  frame may not have stored it — its bubble stays. Pure. */
export function orphanStoredTempId(run: { tempId: string | null; sawFrame: boolean } | null): string | null {
  return run?.sawFrame && run.tempId ? run.tempId : null
}

/** The orphan poll's step BEFORE it aborts the run (the abort releases the claim and
 *  its info): register the run's own bubble as stored, so the reload swaps it for the
 *  stored copy instead of keeping both (mergeTranscript keeps every temp_ row, and a
 *  MESSAGES_SNAPSHOT that never arrived never renamed it). Returns the registered id. */
export function registerOrphanStoredTemp(sessionId: string): string | null {
  const id = orphanStoredTempId(sessionStore.getLiveRunInfo(sessionId))
  if (id) markStoredTemp(sessionId, id)
  return id
}

/**
 * The store key a send streams into — its live-run claim and every frame it records.
 * A first send from a DRAFT passes the materialized session id as the override (the
 * surface creates the session first; the hook's own `sessionId` is null for a draft),
 * so the run is claimed under the REAL id — the same id the orphan poll looks up once
 * the surface shows that session. One key, no draft→real hand-over to keep in step.
 */
export function sendTargetId(sessionIdOverride: string | undefined, hookSessionId: string | null): string | null {
  return sessionIdOverride || hookSessionId
}

/** The orphan poll's question for `sessionId`, read from the store the send/attach
 *  loops write (shouldForceIdleOrphan decides). */
export function orphanPollShouldForceIdle(sessionId: string, busy: Array<{ id: string }>, now: number = Date.now()): boolean {
  return shouldForceIdleOrphan({
    busy: !isSessionOrphaned(sessionId, busy),
    liveRun: sessionStore.getLiveRun(sessionId),
    lastFrameAt: sessionStore.getLastFrameAt(sessionId),
    now,
  })
}

/**
 * Where an optimistic steer bubble goes in the live message list (#481).
 *
 * A steer reads correctly as user → steer → assistant, so while THIS turn's
 * reply bubble is streaming we splice the steer just before it. But that is only
 * right when the last assistant IS this turn's reply — i.e. it comes after the
 * latest user message. If the steer is sent before the reply bubble exists (a
 * fast follow-up, or window A), `lastIndexOf('assistant')` finds a PREVIOUS
 * turn's reply and would wedge the steer above it — above the very message it
 * follows ("I sent 20 then 30, but 30 showed at the top"). Then append at the
 * end; the server stores it after the current user message, so a reload agrees.
 */
/**
 * E7 Codex round 7 — the optimistic steer bubbles whose route response has not arrived yet (module-level: one per
 * client id, removed the moment its response — or its failure — lands). `loadMessages` keeps them through a reload.
 */
const unansweredSteerBubbles = new Set<string>()
/** E7 Codex round 10 — the retry loops of unanswered steer posts, per session store key: cancelled with the hook that
 *  started them (unmount), the session's delete, or a full sign-out (`cancelSteerRetries()`). */
const steerRetriesBySession = new Map<string, Set<AbortController>>()
export function cancelSteerRetries(sessionKey?: string): void {
  for (const [k, set] of steerRetriesBySession) {
    if (sessionKey !== undefined && k !== sessionKey) continue
    for (const c of set) c.abort()
    steerRetriesBySession.delete(k)
  }
}
function trackSteerRetry(key: string, lifecycle: AbortSignal): { signal: AbortSignal; done: () => void } {
  const c = new AbortController()
  if (lifecycle.aborted) c.abort()
  else lifecycle.addEventListener('abort', () => c.abort(), { once: true })
  const set = steerRetriesBySession.get(key) ?? new Set<AbortController>()
  set.add(c); steerRetriesBySession.set(key, set)
  return { signal: c.signal, done: () => { set.delete(c); if (set.size === 0 && steerRetriesBySession.get(key) === set) steerRetriesBySession.delete(key) } }
}

/** E7 Codex round 11 — the client id that keys a steer post's idempotency (`isSteerClientId`: `[A-Za-z0-9_-]{1,100}`).
 *  A UUID, so two sends in one millisecond never collide (time + 4 base-36 chars did); where `crypto.randomUUID` is
 *  unavailable (an insecure context), 128 random bits from `getRandomValues`, else a per-load counter + time + random. */
let steerIdFallbackSeq = 0
export function newSteerClientId(c: { randomUUID?: () => string; getRandomValues?: <T extends Uint8Array>(a: T) => T } | undefined =
  (globalThis as { crypto?: Crypto }).crypto): string {
  if (typeof c?.randomUUID === 'function') return `msg_steer_${c.randomUUID()}`
  if (typeof c?.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16))
    return `msg_steer_${Array.from(b, x => x.toString(16).padStart(2, '0')).join('')}`
  }
  steerIdFallbackSeq += 1
  return `msg_steer_${Date.now().toString(36)}_${steerIdFallbackSeq.toString(36)}_${Math.random().toString(36).slice(2, 12)}`
}

/** E7 Codex round 8 — a steer post that threw: a DEFINITE refusal (an HTTP 4xx answer) or an UNKNOWN outcome (a timeout,
 *  a network error, a 5xx) — the server may have saved it. Pure. */
export function classifySteerFailure(err: unknown): 'refused' | 'unknown' {
  const status = (err as { status?: unknown } | null)?.status
  return typeof status === 'number' && status >= 400 && status < 500 ? 'refused' : 'unknown'
}

/** E7 Codex round 10 — the waits (ms) between retries of an UNANSWERED steer post (same client id — the route is
 *  idempotent by it); the last value repeats until `STEER_RETRY_BOUND_MS` from the first post. */
export const STEER_RETRY_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 20_000]
export const STEER_RETRY_BOUND_MS = 120_000

/**
 * E7 Codex rounds 8, 10 — one steer post and its effect on ONE session's entry (`update`, bound to the session the steer
 * was sent in — never the displayed one, which may have changed by the time the answer lands):
 *  - the optimistic bubble is placed; kept through reloads until a definite answer (`unansweredSteerBubbles`);
 *  - `steered: true` ⇒ exactly one bubble with the server id (`reconcileSteerBubble`);
 *  - a DEFINITE refusal (`steered: false`, or a 4xx) ⇒ the bubble removed, `ok: false` (the caller keeps the text);
 *  - an UNKNOWN outcome (a timeout, a network error, a 5xx) ⇒ the SAME post is retried — the route is idempotent by the
 *    client id, so a retry of a post that was in fact accepted gets the same server id and enqueues nothing — backing off
 *    (`STEER_RETRY_BACKOFF_MS`) until a definite answer, within `STEER_RETRY_BOUND_MS`. The bubble is marked "not
 *    confirmed" meanwhile. Past the bound it stays so marked, `ok: true`, the text NOT handed back (a resend could
 *    duplicate it; the next transcript load shows the truth). `signal` (the hook's lifecycle: unmount, the session's
 *    delete) cancels the retries.
 * Round 9's settle (a reload after the turn ended, the text matching, "not sent") is gone: the client no longer guesses.
 */
export async function postSteer<M extends MessageWithParts>(deps: {
  update: (u: (prev: AgentSessionState) => AgentSessionState) => void
  clientId: string
  bubble: M
  post: () => Promise<{ steered: boolean; reason?: string; id?: string }>
  signal?: AbortSignal
  /** #1316 review round 1 (B-2): the session the steer was sent in — once it ended, the post is never retried (a retry
   *  would go out as the NEXT session); the bubble stays "not confirmed", the text is not handed back (it may have
   *  landed). */
  session?: AbortSignal
  now?: () => number
  wait?: (ms: number, signal?: AbortSignal) => Promise<void>
}): Promise<SteerResult> {
  const { update, clientId: id, bubble } = deps
  const now = deps.now ?? Date.now
  const wait = deps.wait ?? ((ms: number, signal?: AbortSignal) => new Promise<void>((resolve) => {
    const t = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => { clearTimeout(t); resolve() }, { once: true })
  }))
  const startedAt = now()
  unansweredSteerBubbles.add(id)
  update(prev => ({ ...prev, messages: placeSteerBubble(prev.messages, bubble as MessageWithParts) }))
  try {
    for (let attempt = 0; ; attempt++) {
      let res: { steered: boolean; reason?: string; id?: string }
      try { res = await deps.post() } catch (err) {
        if (classifySteerFailure(err) === 'refused') {
          update(prev => ({ ...prev, messages: prev.messages.filter(m => m.id !== id) }))
          // #1468 I1: a same-id chat of another project is running — its own (localized, retry) refusal
          return { ok: false, reason: (err as { code?: unknown }).code === 'SESSION_BUSY_ELSEWHERE' ? 'busy_elsewhere' : 'error' }
        }
        update(prev => ({ ...prev, messages: prev.messages.map(m => m.id === id ? { ...m, steerUnconfirmed: true } : m) }))
        const delay = STEER_RETRY_BACKOFF_MS[Math.min(attempt, STEER_RETRY_BACKOFF_MS.length - 1)]!
        if (deps.signal?.aborted || sessionEndedSince(deps.session, err) || now() + delay - startedAt > STEER_RETRY_BOUND_MS) return { ok: true }   // left "not confirmed"
        await wait(delay, deps.signal)
        if (deps.signal?.aborted || sessionEndedSince(deps.session)) return { ok: true }
        continue
      }
      if (!res.steered) {
        // Drop the optimistic bubble and hand the reason UP. Deliberately NOT `state.error`: that renders the red banner
        // whose only controls abort the run (see describeSteerRefusal). `not_running`: the server has NO run for this
        // session but the client still thinks one is live (#540: a SUSPENDED turn) — drop the stale live state + the
        // dead cards so the caller's fallback sends the text as a normal prompt.
        const reason = (res.reason as SteerRefusal) ?? 'not_running'
        update(prev => ({
          ...prev,
          messages: prev.messages.filter(m => m.id !== id),
          ...(reason === 'not_running' ? { status: 'idle' as const, pendingPermissions: [], pendingQuestions: [] } : {}),
        }))
        return { ok: false, reason }
      }
      // rounds 6–7: the server MINTED the id it saved the steer under — exactly one bubble carries it, confirmed
      const savedId = typeof res.id === 'string' ? res.id : id
      unansweredSteerBubbles.delete(id)
      update(prev => ({ ...prev, messages: reconcileSteerBubble(prev.messages, id, savedId, bubble as MessageWithParts)
        .map(m => m.id === savedId && (m as { steerUnconfirmed?: boolean }).steerUnconfirmed ? { ...m, steerUnconfirmed: false } : m) }))
      return { ok: true }
    }
  } finally {
    unansweredSteerBubbles.delete(id)
  }
}

/**
 * E7 Codex round 7 — once the steer route answers with the id it SAVED the steer under (`savedId`, server-minted),
 * exactly ONE bubble carries that id: a reload that already brought it in ⇒ the optimistic one (`clientId`) is dropped;
 * not there but the optimistic one is ⇒ renamed (its text part to the persisted part id); neither (a reload dropped the
 * optimistic one first) ⇒ re-added as `savedId`, with the sent text and the steered mark, where a steer goes. Pure.
 */
export function reconcileSteerBubble<T extends { id: string; role: string; parts: Array<{ id: string; messageId?: string }> }>(
  messages: T[], clientId: string, savedId: string, bubble: T,
): T[] {
  const hasSaved = messages.some(m => m.id === savedId)
  if (hasSaved) return clientId !== savedId && messages.some(m => m.id === clientId) ? messages.filter(m => m.id !== clientId) : messages
  const rename = (m: T): T => ({ ...m, id: savedId, parts: m.parts.map((p, i) => ({ ...p, id: i === 0 ? `${savedId}_text_0` : p.id, messageId: savedId })) })
  if (messages.some(m => m.id === clientId)) return messages.map(m => m.id === clientId ? rename(m) : m)
  return placeSteerBubble(messages, rename(bubble))
}

export function placeSteerBubble<T extends { role: string }>(messages: T[], steerMsg: T): T[] {
  const roles = messages.map((m) => m.role)
  const lastAssistantIdx = roles.lastIndexOf('assistant')
  const lastUserIdx = roles.lastIndexOf('user')
  const spliceBeforeAssistant = lastAssistantIdx >= 0 && lastAssistantIdx > lastUserIdx
  return spliceBeforeAssistant
    ? [...messages.slice(0, lastAssistantIdx), steerMsg, ...messages.slice(lastAssistantIdx)]
    : [...messages, steerMsg]
}

/** #648: skip calls (~15 s each) before a still-finishing turn is reported as such. */
const SKIP_ATTEMPTS = 4
/** #648: the stale-card reconcile's looks after a turn's `complete` (the job leaves the queue a moment later). */
const STALE_CARD_ATTEMPTS = 6
const STALE_CARD_RETRY_MS = 2_000

export function useAgentSession(rawSessionId: string | null, options: UseAgentSessionOptions) {
  // Draft sessions are client-only — treat as null for all API calls
  const sessionId = isDraftSession(rawSessionId) ? null : rawSessionId
  const { workspaceId, onComplete, onOpenFile } = options
  // For handlers whose identity must not follow it (handleAgentEvent).
  const workspaceIdRef = useRef(workspaceId)
  workspaceIdRef.current = workspaceId
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const onOpenFileRef = useRef(onOpenFile)
  onOpenFileRef.current = onOpenFile

  // State lives in the module store keyed by the raw session id (drafts get
  // their own isolated entry). The hook is a thin view; switching `rawSessionId`
  // just points it at a different entry — no reset effect needed, and each
  // session keeps its own streaming/pending state while off-screen.
  const storeKey = rawSessionId ?? '__none__'
  const storeKeyRef = useRef(storeKey)
  storeKeyRef.current = storeKey
  const state = useSyncExternalStore(
    useCallback((cb: () => void) => sessionStore.subscribe(storeKey, cb), [storeKey]),
    useCallback(() => sessionStore.getState(storeKey), [storeKey]),
    useCallback(() => sessionStore.getState(storeKey), [storeKey]),
  )
  // Display-bound writer: routes to the session THIS instance currently shows.
  // Run-stream writes use a target-bound writer instead (see sendMessage), so a
  // background/handed-off run never lands in the displayed session's entry.
  const setState = useCallback(
    (updater: (prev: AgentSessionState) => AgentSessionState) => sessionStore.update(storeKeyRef.current, updater),
    [],
  )
  // Reference-count the entry for GC (drop it when no view + no live run).
  useEffect(() => {
    sessionStore.acquire(storeKey)
    return () => sessionStore.release(storeKey)
  }, [storeKey])

  // Track sessionId in a ref so the SSE handler can read the latest value
  const sessionIdRef = useRef(sessionId)
  sessionIdRef.current = sessionId

  // E7 Codex round 10: this hook's lifecycle for its steer retries — aborted on unmount (StrictMode re-arms it)
  const steerLifecycleRef = useRef(new AbortController())
  useEffect(() => {
    if (steerLifecycleRef.current.signal.aborted) steerLifecycleRef.current = new AbortController()
    const c = steerLifecycleRef.current
    return () => c.abort()
  }, [])
  // Refs for reload functions — called from event handlers without stale closures
  const reloadRef = useRef<{
    messages: () => void; cost: () => void; todos: () => void
    /** #874: patch these tool calls (messageId → callIds) from the saved parts. */
    toolParts: (calls: Map<string, Set<string>>) => void
    /** #874: patch one already-loaded part from the saved message. */
    part: (messageId: string, partId: string) => void
    /** #874: set these messages' text from their saved (redacted) text. */
    messageText: (messageIds: Set<string>) => void
    /** #648: drop cards the server no longer holds open, once the turn has ended. */
    staleCards: () => void
  }>({ messages: () => {}, cost: () => {}, todos: () => {}, toolParts: () => {}, part: () => {}, messageText: () => {}, staleCards: () => {} })
  /** #874: per session, the tool calls whose live frame was cut — refetched at turn end. */
  const toolRefetchRef = useRef(new Map<string, Map<string, Set<string>>>())
  /** #874: per session, sent messages whose redacted text could not be fetched yet — fetched at turn end. */
  const textRefetchRef = useRef(new Map<string, Set<string>>())
  const noteTextRefetch = (sid: string, messageId: string) => {
    const ids = textRefetchRef.current.get(sid) ?? new Set<string>()
    textRefetchRef.current.set(sid, ids.add(messageId))
  }

  // Generation counter for loadSessionCost so out-of-order /usage responses
  // can't clobber state. Without this, an early-fired loadSessionCost (e.g.
  // from a mid-turn step-finish) that returns AFTER a later loadSessionCost
  // can overwrite the correct value with a stale parent-only number — the
  // root cause of the agent-cost-rollup spec's intermittent failures.
  const sessionCostGenRef = useRef(0)
  /** Generation guard for `loadMessages` — see its docblock. */
  const messagesGenRef = useRef(0)

  // Handle streaming events. `apply` selects the store entry the event mutates:
  // the display-bound `setState` by default (re-attach / recovery, which feed
  // the session on screen), or a target-bound writer for a live run's own
  // session (see sendMessage) — that binding is what keeps a run's stream out
  // of whatever session the surface happens to be showing.
  const handleAgentEvent = useCallback((
    event: SessionProcessor.StreamEvent,
    apply: (updater: (prev: AgentSessionState) => AgentSessionState) => void = setState,
    /** WHICH session this event belongs to (#467). Omitted ⇒ the displayed one,
     *  which is what `apply = setState` already means. Background sessions pass
     *  their own sid alongside `applyToSid`. */
    eventSid?: string,
  ) => {
    // `reloadRef` is bound to the DISPLAYED session, so calling it for a
    // BACKGROUND session's event refetches the wrong conversation — and
    // `loadMessages` REPLACES `state.messages` wholesale, so it is not merely a
    // wasted request. Every reload below goes through this.
    //
    // Skipping is safe rather than lossy: the reload exists to reveal rows that
    // never travel the live stream (a compaction boundary and summary), and the
    // background session refetches those the next time it is opened.
    const reloadDisplayed = (fn: () => void) => {
      if (!eventSid || eventSid === sessionIdRef.current) fn()
    }
    // #874: an oversized live tool frame was cut — remember the call, and at
    // the turn's end patch just those parts from the saved message. Idempotent
    // (a reducer may run twice under StrictMode); a background session drops
    // its entry and reloads when opened.
    const refetchSid = eventSid ?? sessionIdRef.current
    const noteToolRefetch = (messageId: string, callId: string) => {
      if (!refetchSid || !messageId || !callId) return
      const bySid = toolRefetchRef.current.get(refetchSid) ?? new Map<string, Set<string>>()
      toolRefetchRef.current.set(refetchSid, bySid)
      const calls = bySid.get(messageId) ?? new Set<string>()
      bySid.set(messageId, calls.add(callId))
    }
    const flushToolRefetch = () => {
      if (!refetchSid) return
      const calls = toolRefetchRef.current.get(refetchSid)
      const texts = textRefetchRef.current.get(refetchSid)
      toolRefetchRef.current.delete(refetchSid)
      textRefetchRef.current.delete(refetchSid)
      if (calls) reloadDisplayed(() => setTimeout(() => reloadRef.current.toolParts(calls), 0))
      if (texts) reloadDisplayed(() => setTimeout(() => reloadRef.current.messageText(texts), 0))
    }
    apply(prev => {
      switch (event.type) {
        case 'status':
          return { ...prev, status: event.status }

        case 'compaction-progress':
          // Arrives immediately AFTER the synthetic `complete` above (see
          // agui-to-stream). That complete correctly committed the interrupted
          // message and, as it always does, set status 'idle' — which is the
          // one thing that is wrong here, because the worker is still working.
          // Putting it back is what stops the composer unlocking and the
          // spinner dying halfway through a turn.
          if (event.active) {
            return {
              ...prev, compacting: true, status: 'processing',
              // Deliberately NOT cleared by the `active: false` below: the
              // worker clears the indicator BEFORE the terminal, so this is
              // the only thing left that can recognise it.
              pausedTerminalId: event.messageId ?? prev.pausedTerminalId,
            }
          }
          // Compaction REWROTE the transcript server-side — boundary, summary,
          // nudge — and none of those rows travel on the live stream, so the
          // summary divider cannot appear without a refetch.
          //
          // DEFERRED to the end of the turn rather than issued here.
          // `loadMessages` REPLACES the message array (only `temp_` rows
          // survive), and the resumed pass builds its assistant message
          // client-side from text-deltas — so a response landing between the
          // last delta and `complete` drops that message, after which
          // `complete` finds no target and the streamed text is never
          // committed. It vanishes. The divider appearing at turn end instead
          // of mid-turn costs nothing: the indicator already told the user what
          // was happening.
          //
          // Clears `summarizing` as well as `compacting`. They are deliberately
          // distinct fields — `compacting` is an AUTO pass mid-turn — but since
          // #443 a manual `/summarize` produces exactly these events too, and
          // its request now returns the moment the job is accepted. If this did
          // not clear it, the manual spinner would run until the tab reloaded.
          return { ...prev, compacting: false, summarizing: false, resyncAfterTurn: true }

        case 'text-delta': {
          let messages = prev.messages
          // Ensure assistant message exists in the array so it's in correct position
          // (before any subsequent user messages). For pure-text responses without
          // tool calls, this is the only place the assistant message gets created.
          const msgId = (event as any).messageId
          if (msgId && !messages.find(m => m.id === msgId)) {
            messages = [...messages, { id: msgId, role: 'assistant', parts: [] }]
          }
          // B(a): the translator now carries the ACCUMULATED snapshot (`content`);
          // REPLACE with it instead of appending the delta, so two live streams
          // for one session (the lost-abort zombie + the resend) converge instead
          // of doubling every chunk. Longer-wins guards microtask skew between the
          // two streams (a shorter out-of-order snapshot must not shrink the
          // text); a missing `content` (older publisher mid-deploy) falls back to
          // the append.
          const snapshot = (event as any).content as string | undefined
          const streamingText = typeof snapshot === 'string'
            ? (snapshot.length >= prev.streamingText.length ? snapshot : prev.streamingText)
            : prev.streamingText + event.delta
          return { ...prev, messages, streamingText }
        }

        case 'text-done': {
          // Commit accumulated streaming text as a part before clearing,
          // so there's no frame where the text disappears
          let messages = prev.messages
          if (prev.streamingText.trim()) {
            const targetId = (event as any).messageId
            const target = targetId
              ? messages.find(m => m.id === targetId)
              : [...messages].reverse().find(m => m.role === 'assistant')
            if (target) {
              // Idempotency: AG-UI can deliver TEXT_MESSAGE_END twice
              // (live run SSE + bus). Without this guard the second
              // delivery commits an identical part — historically that
              // also collided on `Date.now()` ids → duplicate React
              // keys. Skip if this message's last text part already
              // holds this exact content. (`complete` has an analogous
              // guard via `isFinal`; set it here so that one engages.)
              const lastText = [...target.parts]
                .reverse()
                .find(p => p.type === 'text') as
                | Extract<MessagePart, { type: 'text' }>
                | undefined
              if (!lastText || lastText.content !== prev.streamingText) {
                const textPart: MessagePart = {
                  id: nextStreamPartId('stream_text'),
                  messageId: target.id,
                  type: 'text',
                  content: prev.streamingText,
                  isFinal: true,
                }
                messages = messages.map(m =>
                  m.id === target.id ? { ...m, parts: [...m.parts, textPart] } : m,
                )
              }
            }
          }
          return { ...prev, messages, streamingText: '' }
        }

        case 'reasoning-start': {
          let messages = prev.messages
          const msgId = (event as any).messageId
          if (msgId && !messages.find(m => m.id === msgId)) {
            messages = [...messages, { id: msgId, role: 'assistant', parts: [] }]
          }
          return { ...prev, messages, streamingReasoning: '', streamingReasoningStart: Date.now() }
        }

        case 'reasoning-delta': {
          let messages = prev.messages
          const msgId = (event as any).messageId
          if (msgId && !messages.find(m => m.id === msgId)) {
            messages = [...messages, { id: msgId, role: 'assistant', parts: [] }]
          }
          // B(a): snapshot-based, same as text-delta above.
          const rSnapshot = (event as any).content as string | undefined
          const streamingReasoning = typeof rSnapshot === 'string'
            ? (rSnapshot.length >= prev.streamingReasoning.length ? rSnapshot : prev.streamingReasoning)
            : prev.streamingReasoning + event.delta
          return {
            ...prev,
            messages,
            streamingReasoning,
            streamingReasoningStart: prev.streamingReasoningStart ?? Date.now(),
          }
        }

        case 'reasoning-end': {
          // Commit the block as a static reasoning part (with client-measured
          // duration) so the "Thinking…" shimmer resolves to "Thought · Ns".
          let messages = prev.messages
          const targetId = (event as any).messageId
          const target = targetId
            ? messages.find(m => m.id === targetId)
            : [...messages].reverse().find(m => m.role === 'assistant')
          const content = prev.streamingReasoning
          if (target && content.trim()) {
            const already = target.parts.some((p: any) => p.type === 'reasoning' && p.content === content)
            if (!already) {
              const start = prev.streamingReasoningStart ?? Date.now()
              const part: MessagePart = {
                id: nextStreamPartId('stream_reasoning'),
                messageId: target.id,
                type: 'reasoning',
                content,
                time: { start, end: Date.now() },
              }
              messages = messages.map(m => m.id === target.id ? { ...m, parts: [...m.parts, part] } : m)
            }
          }
          return { ...prev, messages, streamingReasoning: '', streamingReasoningStart: null }
        }

        // @sync OpenCode → tool-input-start: show tool block as "pending" before arguments are ready
        case 'tool-input-start': {
          let messages = prev.messages
          let streamingText = prev.streamingText

          // Commit any accumulated streaming text first
          let committedTextPart: MessagePart | null = null
          if (streamingText.trim()) {
            committedTextPart = {
              id: nextStreamPartId('stream_text'),
              messageId: event.messageId,
              type: 'text',
              content: streamingText,
            }
            const assistantMsg = messages.find(m => m.id === event.messageId)
            if (assistantMsg) {
              messages = messages.map(m =>
                m.id === event.messageId ? { ...m, parts: [...m.parts, committedTextPart!] } : m,
              )
            }
            streamingText = ''
          }

          // Inject tool-call part with pending status and empty input
          const toolPart: MessagePart = {
            id: `stream_tc_${event.callId}`,
            messageId: event.messageId,
            type: 'tool-call',
            toolName: event.toolName,
            callId: event.callId,
            input: {},
            status: 'pending',
          }
          const existing = messages.find(m => m.id === event.messageId)
          if (existing) {
            const alreadyHasPart = existing.parts.some(p => p.type === 'tool-call' && p.callId === event.callId)
            if (alreadyHasPart) {
              return { ...prev, messages, streamingText }
            }
            messages = messages.map(m =>
              m.id === event.messageId ? { ...m, parts: [...m.parts, toolPart] } : m,
            )
          } else {
            const initialParts: MessagePart[] = []
            if (committedTextPart) initialParts.push(committedTextPart)
            initialParts.push(toolPart)
            messages = [...messages, {
              id: event.messageId,
              role: 'assistant',
              parts: initialParts,
            }]
          }
          return { ...prev, messages, streamingText }
        }

        // Update accumulated input size on pending tool part
        case 'tool-input-delta': {
          const messages = prev.messages.map(m =>
            m.id === event.messageId
              ? {
                  ...m,
                  parts: m.parts.map(p =>
                    p.type === 'tool-call' && p.callId === event.callId
                      ? { ...p, inputSize: event.inputSize }
                      : p,
                  ),
                }
              : m,
          )
          return { ...prev, messages }
        }

        // PAPAN EXTENSION: live tool-metadata — merge interim title/progress/
        // output into the running tool-call part (e.g. streaming bash stdout).
        case 'tool-metadata': {
          const meta = event.metadata || {}
          const title = typeof meta.title === 'string' ? meta.title : undefined
          const messages = prev.messages.map(m =>
            m.id === event.messageId
              ? {
                  ...m,
                  parts: m.parts.map(p =>
                    p.type === 'tool-call' && p.callId === event.callId
                      ? { ...p, metadata: { ...p.metadata, ...meta }, title: title ?? p.title }
                      : p,
                  ),
                }
              : m,
          )
          // #874: large fields were dropped from this live frame — patch the saved part at turn end.
          if (meta.papanOversized) noteToolRefetch(event.messageId, event.callId)
          return { ...prev, messages }
        }

        // @sync OpenCode → tool-call: update pending tool to "running" with full input
        case 'tool-call-start': {
          const newCalls = new Map(prev.activeToolCalls)
          newCalls.set(event.callId, { toolName: event.toolName, input: event.input })

          // Update existing pending tool part to running with full input
          // Match by callId directly, or by replacesCallId (temp ID from tool-input-start)
          let messages = prev.messages
          const msg = messages.find(m => m.id === event.messageId)
          const replacesId = (event as any).replacesCallId as string | undefined
          const pendingPart = msg?.parts.find(p =>
            p.type === 'tool-call' && (p.callId === event.callId || (replacesId && p.callId === replacesId))
          )

          if (pendingPart) {
            messages = messages.map(m =>
              m.id === event.messageId
                ? {
                    ...m,
                    parts: m.parts.map(p =>
                      p.type === 'tool-call' && (p.callId === event.callId || (replacesId && p.callId === replacesId))
                        ? { ...p, callId: event.callId, input: event.input, status: 'running' as const }
                        : p,
                    ),
                  }
                : m,
            )
            return { ...prev, activeToolCalls: newCalls, messages }
          }

          // Fallback: no pending part (tool-input-start was missed), create fresh
          let streamingText = prev.streamingText
          let committedTextPart: MessagePart | null = null
          if (streamingText.trim()) {
            committedTextPart = {
              id: nextStreamPartId('stream_text'),
              messageId: event.messageId,
              type: 'text',
              content: streamingText,
            }
            const assistantMsg = messages.find(m => m.id === event.messageId)
            if (assistantMsg) {
              messages = messages.map(m =>
                m.id === event.messageId ? { ...m, parts: [...m.parts, committedTextPart!] } : m,
              )
            }
            streamingText = ''
          }

          const toolPart: MessagePart = {
            id: `stream_tc_${event.callId}`,
            messageId: event.messageId,
            type: 'tool-call',
            toolName: event.toolName,
            callId: event.callId,
            input: event.input,
            status: 'running',
          }
          const existingMsg = messages.find(m => m.id === event.messageId)
          if (existingMsg) {
            messages = messages.map(m =>
              m.id === event.messageId ? { ...m, parts: [...m.parts, toolPart] } : m,
            )
          } else {
            const initialParts: MessagePart[] = []
            if (committedTextPart) initialParts.push(committedTextPart)
            initialParts.push(toolPart)
            messages = [...messages, {
              id: event.messageId,
              role: 'assistant',
              parts: initialParts,
            }]
          }
          return { ...prev, activeToolCalls: newCalls, messages, streamingText }
        }

        case 'tool-call-done': {
          const newCalls = new Map(prev.activeToolCalls)
          const completedCall = newCalls.get(event.callId)
          newCalls.delete(event.callId)
          // Update tool-call status and inject tool-result part.
          // #384: a tool that RETURNED an error (isError via ctx.metadata — e.g. a
          // permission TIMEOUT) has no `event.error`; its message is the output.
          // Match the worker's persistence (`!!event.error || metadata.isError`)
          // so the LIVE card renders the failure immediately — otherwise the part
          // shows a completed "Wrote <file>" until the next message triggers a
          // re-fetch of the (correctly error-flagged) persisted part.
          const hasError = !!event.error || !!(event.metadata as { isError?: boolean } | undefined)?.isError
          const errorText = event.error ?? (hasError ? (event.output || undefined) : undefined)
          // #639: carry the result's snapshot attachments (preview_screenshot's
          // capture — `kind:'snapshot'` + handle, delivered on the event's
          // metadata) onto the LIVE tool-result part, exactly as
          // `mapApiPartToUi` does when the message is later re-loaded. Without
          // this the snapshot renders only after a reload, never in the turn
          // that produced it.
          const liveAttachments = pickSnapshotAttachments(
            (event.metadata as { attachments?: unknown } | undefined)?.attachments,
          )
          const resultPart: MessagePart = {
            id: `stream_tr_${event.callId}`,
            messageId: event.messageId,
            type: 'tool-result',
            callId: event.callId,
            output: event.output ?? '',
            error: errorText,
            ...(liveAttachments.length ? { attachments: liveAttachments } : {}),
          }
          const messages = prev.messages.map(m => {
            if (m.id !== event.messageId) return m
            const updatedParts = m.parts.map(p =>
              p.type === 'tool-call' && p.callId === event.callId
                ? {
                    ...p,
                    status: hasError ? 'error' as const : 'completed' as const,
                    title: event.title ?? p.title,
                    metadata: event.metadata ?? p.metadata,
                  }
                : p,
            )
            return { ...m, parts: [...updatedParts, resultPart] }
          })
          // If a question-owning tool (cred_to_env) finished without a reply
          // (e.g. timed out → "Do this later" path), dismiss ONLY the card
          // that tool owned (tool.callID linkage; Credentials-header
          // fallback) — parallel HITL tools each own their own card.
          const clearQuestions = completedCall?.toolName === 'cred_to_env' && prev.pendingQuestions.length > 0
          // #662: a successful `move_to_drive` re-points a source `file` part
          // server-side (repointAttachmentPartsToDrive). The `message.part.updated`
          // bus event that would patch the chip live doesn't reliably land on a
          // sent chip mid-turn (optimistic vs persisted part id), so the cloud
          // icon otherwise appears only after a manual reload. Flag a
          // turn-end resync: `loadMessages`+`mergeTranscript` take the SERVER
          // (re-pointed) version of the already-reconciled attachment message,
          // so the chip re-points as soon as the turn settles — no reload.
          const movedToDrive = completedCall?.toolName === 'move_to_drive' && !hasError
          // #874: the live result was cut to one frame — patch the saved part at turn end.
          if (event.oversized) noteToolRefetch(event.messageId, event.callId)
          // #662: auto-open the just-moved file in the workspace, matching the
          // manual Save-to-drive dialog (AttachmentChip `onSaved` → `onOpen`).
          // Fires ONLY here — a LIVE tool-completion event, never replayed on
          // reload — so an AI move opens the result once, not on every history
          // load. Deferred via setTimeout (this reducer runs during render).
          if (movedToDrive) {
            const openPath = (event.metadata as { path?: string } | undefined)?.path
            if (openPath && onOpenFileRef.current) {
              const open = onOpenFileRef.current
              // Same guard every other reload in this reducer uses: a move
              // completing in a BACKGROUND session (post-#467 handoff, whose
              // events this hook still processes) must NOT yank a file open in
              // whatever session/workspace is currently displayed.
              reloadDisplayed(() => setTimeout(() => open(openPath), 0))
            }
          }
          return {
            ...prev,
            activeToolCalls: newCalls,
            messages,
            ...(movedToDrive ? { resyncAfterTurn: true } : {}),
            ...(clearQuestions ? {
              pendingQuestions: prev.pendingQuestions.filter(q =>
                q.tool?.callID ? q.tool.callID !== event.callId : q.questions[0]?.header !== 'Credentials'),
            } : {}),
          }
        }

        case 'file-changed': {
          // Mark the current assistant message as having file changes
          // by adding a synthetic patch part. This lets hasChanges work
          // from streaming data without fetching from DB.
          const fcEvent = event as any
          const assistantMsg = [...prev.messages].reverse().find(m => m.role === 'assistant')
          if (!assistantMsg) return prev
          // A file that FAILED to commit earlier this turn (a `sync-failed`
          // warning) and then committed SUCCESSFULLY (this event) must not show a
          // stale "couldn't save" warning beside its success. Drop any sync-failed
          // part for this path. `file-changed` carries the tree-relative path
          // while `file-sync-failed` carries the `/papan/`-prefixed one, so
          // reconcile before comparing.
          const succeededPath = `/papan/${fcEvent.path}`
          const dropStaleWarning = (parts: MessagePart[]): MessagePart[] =>
            parts.filter(p => !(p.type === 'sync-failed' && (p as Extract<MessagePart, { type: 'sync-failed' }>).path === succeededPath))
          // Check if we already have a patch part for this message
          const existingPatch = assistantMsg.parts.find((p: any) => p.type === 'patch')
          if (existingPatch) {
            // Append file to existing patch
            const snap = { path: fcEvent.path, action: fcEvent.action, binary: true, fileId: fcEvent.fileId }
            const updated = { ...existingPatch, snapshots: [...((existingPatch as any).snapshots || []), snap], files: [...((existingPatch as any).files || []), fcEvent.path] }
            return {
              ...prev,
              messages: prev.messages.map(m =>
                m.id === assistantMsg.id
                  ? { ...m, parts: dropStaleWarning(m.parts.map(p => p.id === existingPatch.id ? updated : p)) }
                  : m,
              ),
            }
          }
          // Create new patch part
          const patchPart: MessagePart = {
            id: `stream_patch_${Date.now()}`,
            messageId: assistantMsg.id,
            type: 'patch',
            files: [fcEvent.path],
            snapshots: [{ path: fcEvent.path, action: fcEvent.action, binary: true, fileId: fcEvent.fileId }],
          }
          return {
            ...prev,
            messages: prev.messages.map(m =>
              m.id === assistantMsg.id
                ? { ...m, parts: [...dropStaleWarning(m.parts), patchPart] }
                : m,
            ),
          }
        }

        case 'file-sync-failed': {
          // The agent wrote this file in the sandbox but it could NOT be
          // committed to the drive (quota / disk full). Attach a synthetic
          // `sync-failed` part to the current assistant message so the warning
          // renders INLINE in the transcript and STAYS there (a toast vanishes in
          // seconds — the very "I didn't see it" failure this fixes). Non-fatal:
          // `status`/`error` are untouched (a per-file save failure is not a turn
          // crash). Mirrors the `file-changed` synthetic-patch pattern above.
          const fe = event as Extract<SessionProcessor.StreamEvent, { type: 'file-sync-failed' }>
          const assistantMsg = [...prev.messages].reverse().find(m => m.role === 'assistant')
          if (!assistantMsg) return prev
          // Dedupe by path within the message — a retried write or a re-delivered
          // event must not stack duplicate warnings for the same file.
          if (assistantMsg.parts.some((p) => p.type === 'sync-failed' && (p as Extract<MessagePart, { type: 'sync-failed' }>).path === fe.path)) return prev
          const part: MessagePart = { id: `stream_syncfail_${fe.path}`, messageId: assistantMsg.id, type: 'sync-failed', path: fe.path, reason: fe.reason }
          return {
            ...prev,
            messages: prev.messages.map(m =>
              m.id === assistantMsg.id ? { ...m, parts: [...m.parts, part] } : m,
            ),
          }
        }

        case 'turn-activity': {
          // A plain replace. No dedupe and no merge: the worker already
          // throttles (`makeProgressReporter` in shared/storage/src/sync/diff.ts),
          // and the newest report is always the one worth showing.
          const ae = event as Extract<SessionProcessor.StreamEvent, { type: 'turn-activity' }>
          return { ...prev, turnActivity: ae.activity as TurnActivity | null }
        }

        case 'permission-request': {
          const req = event.request as unknown as PermissionRequest
          // Append (dedupe by id) — mirrors question-request below. Recovery can
          // re-deliver an UNANSWERED permission request more than once (the
          // /pending poll and the mount-effect both re-emit it while the blocked
          // job keeps this session's store entry alive), which without the guard
          // appends a DUPLICATE permission card for one request. Fable r5 flagged
          // the asymmetry: question-request already dedupes, permission-request did not.
          if (prev.pendingPermissions.some(p => p.id === req.id)) return prev
          return { ...prev, pendingPermissions: [...prev.pendingPermissions, req] }
        }

        case 'permission-expired': {
          // #384: the request hit its hard wall with no reply — its prompt is
          // now dead (the worker has stopped listening). Drop just that card so
          // it can't be clicked into a channel with no subscriber. Idempotent:
          // the reply-route 409 backstop may also have cleared it already.
          const { requestId } = event
          if (!prev.pendingPermissions.some(p => p.id === requestId)) return prev
          return { ...prev, pendingPermissions: prev.pendingPermissions.filter(p => p.id !== requestId) }
        }

        case 'question-request': {
          const req = event.request as unknown as QuestionRequest
          // Append (dedupe by id) — parallel HITL tools fan out several
          // question cards; overwriting stranded all but the last one.
          if (prev.pendingQuestions.some(q => q.id === req.id)) return prev
          return { ...prev, pendingQuestions: [...prev.pendingQuestions, req], expiredQuestionNotice: null }
        }

        case 'question-expired': {
          // #637 Parallel to permission-expired: this question was answered or
          // dismissed in ANOTHER tab (or timed out), so drop just that card.
          // Idempotent: the tab that answered already removed it locally.
          const { requestId } = event
          if (!prev.pendingQuestions.some(q => q.id === requestId)) return prev
          return { ...prev, pendingQuestions: prev.pendingQuestions.filter(q => q.id !== requestId) }
        }

        case 'editor-action-preview': {
          // Streamed `set_sql` args from db_editor's TOOL_CALL_ARGS — the
          // "watch the agent type SQL" preview. Reuses the DatabaseViewer's
          // existing papan:editor-action-preview listener (whose detail
          // shape it preserves: {action:'set_sql', payload:{sql}, preview:true}).
          // No id, no reply, no autoRun; the authoritative apply happens when
          // the worker's `client-tool-request` runs the db_editor handler.
          // Bound like the executed call (#352): the session's workspace, and
          // — when the turn's chip named a database — that file, so the SQL
          // being typed lands where it will run.
          // The STREAM's session (`eventSid`), not the one on screen — a
          // background run's preview fires too.
          const { workspaceId: previewWsId, chip } = streamPreviewTarget(eventSid ?? sessionIdRef.current ?? '', workspaceIdRef.current)
          window.dispatchEvent(new CustomEvent('papan:editor-action-preview', {
            detail: {
              action: 'set_sql', payload: { sql: (event as any).sql }, preview: true, workspaceId: previewWsId,
              ...(chip && isDatabaseFileName(chip.fileName) ? { fileId: chip.fileId } : {}),
            },
          }))
          return prev
        }

        case 'retry':
          return { ...prev, retry: { attempt: event.attempt, error: event.error, delay: event.delay } }

        case 'error':
          // `errorAction` belongs to the error it arrived with, so it is cleared
          // with every message that replaces one. A run failing mid-stream is
          // not a budget refusal, and inheriting the refusal's "Buy usage
          // credits" button would offer a fix for a problem the reader does not
          // have. Today the submit path happens to clear it first, but that is
          // an ordering accident rather than a guarantee — an attached run
          // failing while a previous budget block is still on screen would
          // reach here with it set.
          // `errorAdvisory: false` — a mid-run worker/provider failure IS a
          // stuck session, and the flag must not survive from an earlier
          // advisory: dismissal would then skip force-unlock and leave the
          // session wedged with no control that recovers it.
          // The turn ended — run the deferred resync here too (a compaction,
          // a `move_to_drive`), or it waits for some later turn's `complete`.
          // Same routing as `cancelled`. #874's cut tool frames are patched
          // by `flushToolRefetch`, never by this reload.
          if (prev.resyncAfterTurn) setTimeout(() => reloadDisplayed(() => reloadRef.current.messages()), 0)
          flushToolRefetch()
          return clearTurnIndicators({ ...prev, error: event.error, errorAction: null, errorAdvisory: false, status: 'failed', retry: null, resyncAfterTurn: false })

        case 'complete': {
          // #874: the tool parts were saved when each tool finished — a targeted
          // patch is safe here (and on a compaction pause), unlike a reload.
          flushToolRefetch()
          /** A compaction pause borrows this reducer; it is not a turn ending —
           *  see `shouldRunDeferredResync`. */
          const runDeferredResync = shouldRunDeferredResync(prev, event as { compactPending?: boolean })
          // B(a): dedupe duplicate terminals. AG-UI can deliver `complete` twice
          // (two live streams for one session, or the SSE+bus echo). The second
          // application double-counts sessionCost (#269) and re-commits parts —
          // skip it entirely. Keyed on the completed messageId.
          if (event.messageId && event.messageId === prev.lastCompletedMessageId) {
            const ended = resolveClaimedTerminal(prev, event.messageId)
            if (!ended) return prev
            // This IS the turn ending (a compaction that threw, or hit the
            // round cap), so the deferred resync has to happen here too — the
            // early return meant it never did, leaving the boundary and the
            // `[Compaction failed: …]` summary invisible until a reload, with
            // the flag still set to fire on some unrelated later turn.
            if (prev.resyncAfterTurn) setTimeout(() => reloadDisplayed(() => reloadRef.current.messages()), 0)
            return { ...ended, resyncAfterTurn: false }
          }
          // Reload cost/todos. Fetch the completed message's DB parts (patches,
          // tool results) and merge them into the streaming-built message.
          setTimeout(async () => {
            reloadRef.current.cost()
            reloadRef.current.todos()
            reloadRef.current.staleCards()
            // The compaction resync, deferred to here — see the
            // `compaction-progress` case.
            //
            // NOT on a compaction PAUSE. The pause deliberately runs this whole
            // reducer to reuse the commit, so from the SECOND round onward
            // (round 1 having set the flag) it would fire `loadMessages`
            // mid-turn — the wholesale array replace the deferral exists to
            // avoid, which drops the client-built streaming message.
            if (runDeferredResync) reloadDisplayed(() => reloadRef.current.messages())
            // #481 item 4: NO steer-marker refetch here. A wholesale
            // `loadMessages` on `complete` races the worker's save and drops the
            // just-streamed reply — for a window-B steer that is its OWN
            // follow-up reply, which then vanishes until a manual reload (the
            // exact hazard the comment above describes). The marker instead
            // reconciles on the next natural `loadMessages` (reload / session
            // switch): a folded-in steer keeps its persisted flag, a follow-up
            // steer had it cleared by the worker, so it self-corrects then.
            onCompleteRef.current?.()
          }, 0)

          // Commit any remaining streaming text/reasoning as parts
          let messages = prev.messages
          const completedId = event.messageId
          // Worker augments `complete` with `subagentCost` so the per-message
          // `cost` field matches the rolled-up total. Parent-only would drift.
          //
          // USD RESALE, and it is used for `message.cost` ONLY — never added to
          // `sessionCost`. See the note at the `sessionCost` line below.
          const completedCost = (event.cost ?? 0) + (event.subagentCost ?? 0)
          const targetAssistant = completedId
            ? messages.find(m => m.id === completedId)
            : [...messages].reverse().find(m => m.role === 'assistant')

          if (targetAssistant) {
            let extraParts: MessagePart[] = []
            if (prev.streamingReasoning.trim()) {
              extraParts.push({
                id: nextStreamPartId('stream_reasoning_final'),
                messageId: targetAssistant.id,
                type: 'reasoning',
                content: prev.streamingReasoning,
              })
            }
            // Only commit streaming text if it isn't already on the message —
            // either committed by the step handler (`isFinal`) OR already
            // present as the persisted text part reloaded after a mid-run page
            // refresh. mapApiPartToUi doesn't set `isFinal`, so an isFinal-only
            // guard re-appends an identical part on the reload→resume path →
            // the message renders twice (caught by agent-db-editor reload
            // recovery). Dedup by content too, matching the `text-done` guard.
            const alreadyCommitted = targetAssistant.parts.some(
              (p: any) => p.type === 'text' && (p.isFinal || p.content === prev.streamingText),
            )
            if (prev.streamingText.trim() && !alreadyCommitted) {
              extraParts.push({
                id: nextStreamPartId('stream_text_final'),
                messageId: targetAssistant.id,
                type: 'text',
                content: prev.streamingText,
              })
            }
            // Find the parent user message (the one right before this assistant)
            const assistantIdx = messages.findIndex(m => m.id === targetAssistant!.id)
            const parentUser = assistantIdx > 0
              ? [...messages].slice(0, assistantIdx).reverse().find(m => m.role === 'user')
              : undefined

            const now = Date.now()
            // Extract modelId from session model string (e.g. "openai/gpt-5.4-nano" → "gpt-5.4-nano")
            const sessionModel = prev.session?.model || ''
            const modelID = sessionModel.includes('/') ? sessionModel.split('/').pop() : sessionModel

            // This is the SUCCESS path (`complete` — a real tool failure
            // surfaces as a tool-result error, and a failed/aborted turn
            // ends via the `error`/`cancelled` events, not here). So any
            // tool-call still pending/running on a successfully-finished
            // turn did NOT fail — its result event was just lost in a
            // page-reload→re-attach gap (AG-UI pub/sub is fire-and-forget;
            // streamAguiRun only relays events emitted AFTER re-attach).
            // Finalize as 'completed' (no output blob — the result is
            // genuinely gone, but the sibling patch/text parts show what
            // actually changed). Marking 'error' here would paint a
            // red-cross failure on an operation that demonstrably
            // succeeded.
            const finalizeStaleToolCalls = (parts: MessagePart[]): MessagePart[] =>
              parts.map(p =>
                p.type === 'tool-call' && (p.status === 'running' || p.status === 'pending')
                  ? { ...p, status: 'completed' as const }
                  : p,
              )

            messages = messages.map(m => {
              if (m.id !== targetAssistant!.id) return m
              const swept = finalizeStaleToolCalls(m.parts)
              return {
                ...m,
                parts: extraParts.length ? [...swept, ...extraParts] : swept,
                cost: completedCost,
                // Gate 1 (0177): the per-message BADGE reads
                // costBillingMicro/costCurrency (billing-native) rather than
                // USD `cost`. Applied from the streamed `complete` event so
                // the badge renders IMMEDIATELY on turn completion — before
                // Gate 1 this state came in from `loadMessages`, which the
                // complete reducer does NOT trigger (see cost-update path:
                // it reloads /usage only). Undefined ⇒ unchanged (older
                // bridge, or a run without a settled billing rollup) —
                // badge falls back to the next loadMessages tick.
                ...(event.costBillingMicro !== undefined ? { costBillingMicro: event.costBillingMicro } : {}),
                ...(event.costCurrency !== undefined ? { costCurrency: event.costCurrency } : {}),
                timeCompleted: now,
                timeCreated: m.timeCreated || (parentUser?.timeCreated ?? now),
                parentID: m.parentID || parentUser?.id,
                modelID: m.modelID || modelID,
                personaId: m.personaId || prev.lastPersonaId,
              }
            })
          }

          return {
            ...prev,
            messages,
            resyncAfterTurn: false,
            status: 'idle',
            error: null,
            errorAction: null,
            errorAdvisory: false,
            streamingText: '',
            streamingReasoning: '',
            streamingReasoningStart: null,
            activeToolCalls: new Map(),
            turnActivity: null,
            // ── DELIBERATELY NOT `prev.sessionCost + completedCost` ────────
            //
            // `sessionCost` is whatever `/api/agent/usage` last returned, and
            // WHICH money that is depends on the chat-cost mode (0174):
            // billing-currency micro in the 'user' view, USD provider cost in
            // the 'operator' view, and 0 when the mode is 'hidden'.
            // `completedCost` is neither — it is the USD RESALE figure the
            // worker streams for the per-message badge.
            //
            // Adding it was wrong in all three modes, and silently:
            //   - 'user' on a non-USD deployment added dollars to ringgit under
            //     a ringgit label (the client half of the rule stated at
            //     `GET /usage`: "DO NOT ADD USD TO BILLING-CURRENCY MAJOR
            //     UNITS" — the server converts its own live leg through
            //     `usdMicroToBillingMicro` for exactly this reason);
            //   - 'operator' added the CHARGED figure into a provider-cost
            //     total, which diverges precisely when a resale override is in
            //     force, i.e. whenever that view is worth looking at;
            //   - 'hidden' put a number on a total the deployment asked to be 0.
            //
            // The authoritative figure is one tick away regardless: this same
            // handler already schedules `reloadRef.current.cost()` above, and
            // the worker's later `cost-update` fires it again once the rollup
            // has landed. So the footer now waits one round trip instead of
            // showing a wrong-unit number in the interim.
            lastCompletedMessageId: event.messageId ?? prev.lastCompletedMessageId,
            retry: null,
          }
        }

        case 'cancelled':
          // Same routing as the compaction resync — the issue names this as the
          // pre-existing case of the identical shape (#467). The full reload
          // covers any #874 tool refetch, so that is just dropped.
          if (refetchSid) { toolRefetchRef.current.delete(refetchSid); textRefetchRef.current.delete(refetchSid) }
          setTimeout(() => reloadDisplayed(() => reloadRef.current.messages()), 0)
          return clearTurnIndicators({ ...prev, status: 'cancelled', streamingText: '', activeToolCalls: new Map() })

        case 'cost-update':
          // Worker has committed the rolled-up cost to SQLite (json_set on
          // message.data.cost) — /usage's liveCost now reports the correct
          // value. Refetch so the session header catches up. Without this,
          // earlier loadSessionCost calls (from step-finish events fired
          // mid-turn) can have their late /usage responses clobber the
          // correct state. See processor.ts cost-update definition.
          setTimeout(() => reloadRef.current.cost(), 0)
          return prev

        case 'subagent-update': {
          // Yield-port Day 10b-d UI follow-up. Append to the matching
          // tool-call part's subagentUpdates. The link is by parentCallId
          // — the only stable identifier between the running task
          // tool-call card (which only sees callId mid-stream) and the
          // child subagent (sessionId visible only at tool-call-done
          // metadata time). If parentCallId is missing or no matching
          // part exists, silently drop (stream order shouldn't get this
          // wrong; defensive).
          const ev = event as Extract<SessionProcessor.StreamEvent, { type: 'subagent-update' }>
          if (!ev.parentCallId) return prev
          const update: SubagentUpdate = {
            childSessionId: ev.childSessionId,
            kind: ev.kind,
            summary: ev.summary,
            detail: ev.detail,
            at: Date.now(),
          }
          const messages = prev.messages.map(msg => ({
            ...msg,
            parts: msg.parts.map(p =>
              p.type === 'tool-call' && p.callId === ev.parentCallId
                ? { ...p, subagentUpdates: [...(p.subagentUpdates ?? []), update] }
                : p,
            ),
          }))
          return { ...prev, messages }
        }

        case 'step': {
          // Refetch session cost + context tokens so the brain ring and per-session
          // $ display update LIVE during a long turn — the worker writes running
          // totals to the message on each step-finish, but the UI only had a
          // refetch on 'complete' (turn end). Without this the indicators sat
          // frozen for tool-heavy turns.
          if (event.action === 'finish') {
            setTimeout(() => reloadRef.current.cost(), 0)
          }
          // When a step finishes with finishReason 'stop', the model is done —
          // commit accumulated streaming text as the final text part
          if (event.action === 'finish' && (event as any).finishReason === 'stop' && prev.streamingText.trim()) {
            const targetMsg = [...prev.messages].reverse().find(m => m.role === 'assistant')
            if (targetMsg) {
              const finalPart: MessagePart = {
                id: nextStreamPartId('stream_text_final'),
                messageId: targetMsg.id,
                type: 'text',
                content: prev.streamingText,
                isFinal: true,
              }
              const messages = prev.messages.map(m =>
                m.id === targetMsg.id ? { ...m, parts: [...m.parts, finalPart] } : m,
              )
              // Clear streamingText — the committed part now holds the content.
              // Thinking... indicator won't flash because it checks for assistant parts.
              return { ...prev, messages, streamingText: '' }
            }
          }
          return prev
        }

        default:
          return prev
      }
    })
  }, [setState])

  // Client-executed frontend tools (Design note I step 4). When a
  // registered 'client-executed' tool's call finishes streaming
  // (tool-call-start carries the full input + the model's REAL
  // toolCallId), run its registered handler in the browser and re-POST
  // the result as a STANDARD AG-UI tool message. The ingress routes it
  // to the worker's blocked ctx.clientTool (NOT a new turn / NOT a
  // resume); the model continuation streams on the ORIGINAL still-open
  // run SSE — same shape as a HITL reply today.
  // Run a registered client-executed tool's handler and re-POST its
  // result as a STANDARD AG-UI tool message. Shared by the LIVE path
  // (a streamed tool-call-start) and the RELOAD-RECOVERY path (a
  // `client-tool-request` restored from /pending). Deduped by
  // `takeClientToolRun`: the live request and its /pending replay share a wait
  // key (`c#w1`); the model's tool-call-start keys the bare call id (`c`), and
  // the bare id and its waits refuse each other — so no path can double-run,
  // while a resumed call's second wait (`c#w2`) still runs (R9). The result is
  // KEPT and re-sent until the server takes it (#352 I3b) — re-POSTs are safe,
  // re-runs are not.
  // `sidOverride` (Fable r4): the RELOAD-RECOVERY caller runs inside the /pending
  // poll, whose callback can outlive a tab switch (it awaits before firing). It
  // must bind the POLLED session id at call time, not read sessionIdRef.current
  // here — which resolves to the DISPLAYED session and would POST session A's
  // recovered tool-result under session B's threadId. Live callers omit it and
  // keep the display-bound ref (correct: a live tool-call IS the viewed run).
  const runClientToolByCall = useCallback(async (offer: ClientToolOffer, recovered = false, sidOverride?: string, mayDefer = recovered, tenantOverride?: { id: string | null }, fromTimer = false) => {
    const { toolName, toolCallId: callId } = offer
    const entry = getAgentTool(toolName)
    if (!entry || entry.transport !== 'client-executed' || !entry.handler) return
    // #352 I3b: a result this page KEPT is re-sent, never re-run — decided BEFORE
    // the readiness gate, so a kept result never waits for its surface to remount.
    // An offer of a kept wait (pending or taken) only nudges a re-POST: a server
    // that still offers it never consumed the earlier POST (the suspend window, a
    // worker crash). A result kept under another wait (or under the bare call id —
    // an old worker's waitless request) never answers this one (R9): each wait runs
    // its own handler once.
    const key = waitKey(offer)
    const action = onOffer(key, ranOnThisPage(offer))
    if (action === 'skip') return // this page's handler is still running
    if (action === 'nudge') {
      if (offerMayPost(key)) void postKeptResult(key)
      // Owner ruling 2026-10-19: a REPLAYABLE result kept by an earlier load of this tab
      // re-runs once here to restore its UI (readiness-gated, no claim, nothing posted).
      // HERE only — the server still offers this wait; the pump never replays.
      void replayKeptResult(key, CLIENT_ID, replayLookup).catch(() => {})
      return
    }
    // Readiness gate. Applies to any dispatch a RETRY will follow: if the
    // tool's cross-feature surface hasn't re-mounted yet, do NOT consume the
    // callId or POST — return so the next 5 s /pending poll retries until
    // entry.ready() is true. Bounded by the tool's `maxDeferMs` and the
    // worker's own ctx.clientTool wait.
    //
    // `mayDefer` is NOT the same question as `recovered` (which additionally
    // triggers the staggered message reloads below, and must stay off for a
    // live dispatch). It means "the worker has already recorded this call, so
    // the /pending poll WILL come back for it". True for the /pending replay
    // and for the worker's live `client-tool-request` — false for the model's
    // own tool-call-start, where the worker may not have reached
    // ctx.clientTool yet, so there may be no record to retry from and
    // declining would lose the call rather than defer it.
    // #352 R3 first, so `ready(args)` sees the scope: a surface call an old
    // worker sent unstamped acts only in this hook's workspace.
    const args = scopeTargetlessCall(toolName, offer.args, workspaceId)
    const dispatchSession = currentSessionSignal()   // #1316 review round 1: a deferred re-arm ends with this session
    if (shouldDeferClientTool(entry, mayDefer, args, key)) {
      // A page TIMER's re-dispatch (the claim-lost retry, the can't-resolve timer) has
      // no later offer to rely on — re-arm it, with the session and tenant it captured,
      // or a dead claimant may never be replaced (bounded: clientToolRun.ts).
      if (fromTimer) rearmDeferredRedispatch(key, () => {
        if (sessionEndedSince(dispatchSession)) return
        void runClientToolByCallRef.current(offer, recovered, sidOverride, mayDefer, tenantOverride, true)
          .catch((err) => console.error('[agent] client-tool re-dispatch failed (#352 I1):', err))
      })
      return
    }
    // The session first: the slot below is shared by every chat surface on
    // the page, so an instance that cannot run the call must not take it.
    const sid = sidOverride ?? sessionIdRef.current
    if (!sid) return
    // R-13: the tenant of THIS dispatch — read before any await (the claim, the
    // handler), which can outlast a tenant switch; the claim and every re-send of the
    // kept result go under it. A page timer's re-dispatch passes the one it captured.
    const dispatchTenantId = tenantOverride ? tenantOverride.id : getActiveTenantId()
    const handler = entry.handler
    // #352 I1: one TAB runs each wait — claimed on the server before the handler runs
    // (clientToolRun.ts `runClaimedOffer`, which also takes this page's once-per-WAIT
    // slot, R9, keeps the result and sends it). A page that cannot resolve the call's
    // surface claims only after ~8 s, so a tab that can wins. Its own timers re-dispatch
    // THIS offer with the session and tenant captured now — never `sessionIdRef`, which
    // after a tab switch is the displayed session (Fable r4).
    const outcome = await runClaimedOffer({
      offer, sessionId: sid, workspaceId: resultWorkspaceId(offer, workspaceId), tenantId: dispatchTenantId, holder: CLIENT_ID,
      canResolve: entry.canResolve ? entry.canResolve(args) : true,
      replayable: entry.replayable ? entry.replayable(args) : false,
      args,
      pinReplayArgs: entry.pinReplayTarget ? () => entry.pinReplayTarget!(args) : undefined,
      handler: () => handler(args, { toolCallId: callId, sessionId: sid }),
      post: postKeptResult,
      redispatch: () => {
        void runClientToolByCallRef.current(offer, recovered, sid, mayDefer, { id: dispatchTenantId }, true)
          .catch((err) => console.error('[agent] client-tool re-dispatch failed (#352 I1):', err))
      },
    })
    if (outcome !== 'ran') return
    // Reload-recovery reconcile. The mount-effect's resume re-attach
    // stream can close BEFORE the worker emits the post-tool-result
    // text-deltas + `complete` — the worker was blocked when the resume
    // POST connected, so the bridge has nothing to relay and closes
    // promptly. The one-shot reloadMessages on `done` then races the
    // worker's persistence and fetches stale state, leaving the assistant
    // message blank. Live tool-calls have an open run stream that
    // delivers the post-tool text in-band, so they don't need this.
    // Stagger a few reloads to cover varied worker processing time on
    // contended review envs — each reload fully replaces the
    // streaming-built message with the authoritative DB row, so a
    // duplicate fetch is idempotent.
    if (recovered) {
      const reloadAt = [500, 2000, 5000]
      for (const ms of reloadAt) setTimeout(() => reloadRef.current.messages(), ms)
    }
  }, [workspaceId])
  // #352 I1: the claim's page timers re-dispatch through the CURRENT closure.
  const runClientToolByCallRef = useRef(runClientToolByCall)
  runClientToolByCallRef.current = runClientToolByCall
  const runClientTool = useCallback((se: SessionProcessor.StreamEvent) => {
    const d = clientToolDispatch(se, n => getAgentTool(n)?.argsFrom)
    // `recovered` stays FALSE for both live dispatches (no staggered message
    // reloads); `mayDefer` carries the readiness question on its own.
    if (d) void runClientToolByCall(d.offer, false, undefined, d.mayDefer)
  }, [runClientToolByCall])

  // Attach to an in-flight run for the viewed session via a prompt-less resume
  // Attach to a session's in-flight run and stream it. The /api/agent/agui POST
  // streams snapshot+live for an active job REGARDLESS of who started it (400s if
  // nothing is running). First-class, PER-SESSION, MULTI-ATTACHER: the server fans
  // each session's events over Redis pub/sub, so any viewer — this tab, another
  // tab/device, (future) other PEOPLE in a group chat — can stream a session
  // independently of who started it or which chat is on screen. Keyed by the RUN's
  // session id, never the displayed one.
  //
  // De-dup uses the MODULE-level `liveRun` flag — the SAME guard the send loop uses
  // (setLiveRun at 1651/1784) — so it holds ACROSS hook instances: two mounted chat
  // surfaces (ChatPanel + HeroChat) can't both open a resume stream for one background run
  // and double-apply every text-delta ("HelloHello"). setLiveRun(false) at the end
  // also GCs the entry so background attaches don't leak. Per-sid AbortControllers
  // (not one shared) mean a session SWITCH never aborts OTHER sessions' backgrounded
  // attaches. (#269 review — findings 6, 7, 8.)
  const attachControllers = useRef<Map<string, AbortController>>(new Map())
  // #813: stop a lost-connection recovery on cancel / unmount (see recoverLostRun).
  const stoppedRecoveries = useRef<Set<string>>(new Set())
  const unmounted = useRef(false)
  // Assigned below once recoverLostRun exists (beginAttach/beginResume are declared first).
  // #762: one quiet-check EPISODE per session across give-up → re-attach → give-up cycles — its deadline and cycle
  // count carry over: each cycle waits longer before asking again, and once the deadline has passed even a "running"
  // answer ends the episode in the error (`reconcileAfterGiveUp` checks the deadline before re-attaching). Cleared when
  // a recovery succeeds, the turn ends, another run takes the session over, or a Cancel.
  const reconcileEpisodes = useRef(new Map<string, { deadline: number; cycles: number; resumeResent?: boolean }>())
  const recoverLostRunRef = useRef<((sid: string, wsid: string, opts?: RecoverOpts) => Promise<void>) | null>(null)
  // #352 I1: an attach never replays a `client-tool-request` published before its
  // subscription opened (pub/sub), and the `/pending` poll is 5 s away — a tab that
  // attaches to a run another tab started (the busy watch) would then lose the
  // wait to a sender that can't resolve it (its ~8 s claim delay). The worker
  // records the wait before publishing it, so one `/pending` read once the attach
  // is open covers that gap; the claim and the per-wait slot keep it to one run.
  const recoverPublishedClientTool = useCallback(async (sid: string, sidWorkspaceId: string) => {
    try {
      const res = await request<{ pending: SessionProcessor.StreamEvent | null }>(`/api/agent/sessions/${sid}/pending?workspaceId=${encodeURIComponent(sidWorkspaceId)}`)
      if (res?.pending?.type === 'client-tool-request' && sid === sessionIdRef.current) void runClientToolByCallRef.current(res.pending, true, sid)
    } catch { /* non-critical — the /pending poll retries */ }
  }, [])
  const attachSession = useCallback(async (sid: string, sidWorkspaceId: string, signal: AbortSignal, liveToken: object, resume = false): Promise<AttachOutcome> => {
    // Run-anchored: route sid's stream into sid's OWN store entry, never the displayed one.
    const applyToSid = (updater: (prev: AgentSessionState) => AgentSessionState) => sessionStore.update(sid, updater)
    // #813: true only while OUR code handles a received chunk — an exception there
    // is a client bug, never retried. Any other failure (the fetch, the read, or a
    // cut surfacing elsewhere, as on Firefox) is a lost connection worth re-attaching.
    let handlingFrame = false
    // #813: the server ends every run it finishes with RUN_FINISHED / RUN_ERROR; a
    // stream that ends cleanly without one was cut in between (an edge closing the
    // response when the instance behind it died) — a lost connection, not the end.
    let sawTerminal = false
    // #840: the server ended this stream on purpose (API shutdown) — re-attach now.
    let reconnectAsked = false
    // #813 round 7: an attach that relayed the run before being cut again made
    // PROGRESS (recoverRun then restarts its budget). Progress = a real event beyond
    // the attach bootstrap, or the stream held open 10s (a parked turn only pings);
    // "it opened" alone is not, or an instance that dies on every attach would loop.
    let progressed = false
    let openedAt = 0
    // B-I3: a reconnect ask on a progressed stream resets recoverRun's counters too.
    const cutOutcome = (): AttachOutcome =>
      cutOutcomeFor({ reconnectAsked, progressed, openForMs: openedAt > 0 ? Date.now() - openedAt : null })
    // #1316: the stream ends with the session it was opened in (`sessionStreamSignal`) — see the catch.
    const attachStart = currentSessionSignal()
    try {
      const ses = createAguiToStream()
      const res = await sessionFetch(`${getApiBase()}/api/agent/agui`, {
        method: 'POST',
        credentials: 'include',
        headers: aguiHeaders({ accept: 'text/event-stream' }),
        // The run's OWN workspaceId (each session lives in its own per-workspace
        // SQLite). Sending the DISPLAYED workspaceId for a cross-workspace run 404s
        // the wrong DB and retries forever every 4s. (#269 review, finding 3.)
        // #540 PR E: `resume` asks the server to CONTINUE a suspended turn whose
        // parked requests are all answered (a new run, not a re-attach).
        body: JSON.stringify({ threadId: sid, runId: `resume-${Date.now()}`, state: {}, messages: [], tools: [], context: [], forwardedProps: { workspaceId: sidWorkspaceId, papanOmitSnapshot: true, ...(resume ? { resume: true, clientId: getClientId(), ...lastSendProps.get(sid) } : {}) } }),
        signal: sessionStreamSignal(signal),
      })
      // Nothing in flight (400) — nothing to resume. A 5xx/408/429 (the API instance
      // restarting or draining) is worth retrying (run-recovery.ts).
      if (!res.ok || !res.body) return attachOutcomeForStatus(res.status)
      openedAt = Date.now()
      if (!resume && sid === sessionIdRef.current) void recoverPublishedClientTool(sid, sidWorkspaceId)
      const rd = res.body.getReader()
      const dec = new TextDecoder()
      let buf = ''
      for (;;) {
        const { done, value } = await rd.read()
        if (done || signal.aborted) break
        handlingFrame = true
        buf += dec.decode(value, { stream: true })
        const frames = buf.split('\n\n')
        buf = frames.pop() ?? ''
        for (const fr of frames) {
          if (fr.replace(/^\s+/, '').startsWith(':')) continue
          const dl = fr.split('\n').find(l => l.startsWith('data:'))
          if (!dl) continue
          let aev: { type: string; [k: string]: unknown }
          try { aev = JSON.parse(dl.slice(5).trim()) } catch { continue }
          sessionStore.noteFrame(sid) // still delivering — the orphan poll leaves this stream alone
          if (aev.type === 'RUN_FINISHED' || aev.type === 'RUN_ERROR') sawTerminal = true
          const isReconnect = aev.type === 'CUSTOM' && aev.name === 'papan.reconnect'
          if (isReconnect) reconnectAsked = true
          // The reconnect ask itself is not progress (else every ask would read as one).
          if (aev.type !== 'RUN_STARTED' && aev.type !== 'MESSAGES_SNAPSHOT' && !isReconnect) progressed = true
          if (aev.type === 'MESSAGES_SNAPSHOT') continue // history via loadMessages (REST)
          for (const se of ses.push(aev)) {
            handleAgentEvent(se, applyToSid, sid)
            // Client tools manipulate the on-screen surface, so only fire them for
            // the session actually DISPLAYED; a backgrounded run's client tool is
            // recovered via /pending when its session is reopened.
            if (sid === sessionIdRef.current) runClientTool(se)
          }
        }
        handlingFrame = false
      }
      if (!sawTerminal && !signal.aborted) return cutOutcome()
      // The resume stream is lossy across the attach window (a dropped leading
      // text-delta renders a truncated message). Once the run ends, refetch the
      // authoritative persisted messages — but only for the DISPLAYED session
      // (reloadRef is the displayed view's reloader; a backgrounded session
      // reconciles via loadMessages when reopened). Race-free: the worker persists
      // final text on `text-done` (sync) BEFORE it publishes `complete`.
      if (!signal.aborted && sid === sessionIdRef.current) reloadRef.current.messages()
      return 'streamed'
    } catch (err) {
      // #1316: the session this stream was opened in ended (its body cut by `sessionStreamSignal`, or the answer
      // dropped before headers). FINAL — checked before the abort branch below, never a cut to recover: a re-attach
      // would go out as the next session. 'superseded' (recoverRun's 'stopped') reloads nothing. The chat is left
      // idle as the send's P8 path leaves it — the module store outlives the session and the busy-attach may never
      // run again — but only while this attach still owns the run (the next session's own claim is never touched).
      if (sessionEndedSince(attachStart, err)) {
        if (sessionStore.ownsLiveRun(sid, liveToken)) {
          sessionStore.update(sid, (prev) => ({ ...prev, status: 'idle', streamingText: '', streamingReasoning: '', streamingReasoningStart: null }))
        }
        return 'superseded'
      }
      // Resume is best-effort, but a CUT stream (the serving API instance
      // restarted mid-run, #813) is worth re-attaching; an abort is final; an
      // exception in our frame handling is not a lost connection — stop and
      // reconcile from REST.
      if (err instanceof DOMException && err.name === 'AbortError') return 'streamed'
      return handlingFrame ? 'not-running' : cutOutcome()
    } finally {
      // Only drop OUR controller: a resume that superseded this (aborted) attach
      // has already stored its own under the same sid.
      if (attachControllers.current.get(sid)?.signal === signal) attachControllers.current.delete(sid)
      // Release the cross-instance claim (only if we still own the token) AND GC the
      // entry if no view/run still holds it. Owner-token guards against a superseded
      // finally releasing a newer send/attach claim (#269 re-verify, finding 6).
      sessionStore.releaseLiveRun(sid, liveToken)
    }
  }, [handleAgentEvent, runClientTool, recoverPublishedClientTool])

  // Claim a session and start streaming it — idempotent ACROSS hook instances via
  // the module-level liveRun owner token, claimed synchronously here before the first await.
  const beginAttach = useCallback((sid: string, sidWorkspaceId: string) => {
    if (!sid) return
    if (sessionStore.getLiveRun(sid) || attachControllers.current.has(sid)) return
    const liveToken = {}
    // B(b) fix (Fable #1): hand the attach's controller to claimLiveRun too — not
    // just the send loop's. Otherwise a superseding send can't abort a stale
    // attach SSE (a run that died without a terminal frame leaves the busy-watch
    // attach open), and the resend's events double-apply alongside it.
    const ctrl = new AbortController()
    sessionStore.claimLiveRun(sid, liveToken, ctrl)
    attachControllers.current.set(sid, ctrl)
    // #813: a cut of THIS stream (an API restart) is recovered like a send's.
    void attachSession(sid, sidWorkspaceId, ctrl.signal, liveToken).then((o) => {
      if (isRecoverableOutcome(o)) void recoverLostRunRef.current?.(sid, sidWorkspaceId)
    })
  }, [attachSession])

  // #540 PR E: start the RESUME run for a suspended turn once the reply route says
  // every parked request of it is answered (`resumeReady`). Unlike beginAttach this
  // ALWAYS claims: the suspended run's own stream never got a terminal frame, so a
  // stale live-run claim/stream may still be held — claimLiveRun aborts it.
  const beginResume = useCallback((sid: string, sidWorkspaceId: string) => {
    if (!sid) return
    const liveToken = {}
    const ctrl = new AbortController()
    sessionStore.claimLiveRun(sid, liveToken, ctrl)
    attachControllers.current.get(sid)?.abort()
    attachControllers.current.set(sid, ctrl)
    markResumeAttach(sid, true) // R-13: the processing poll does not re-request it meanwhile
    void attachSession(sid, sidWorkspaceId, ctrl.signal, liveToken, true).finally(() => markResumeAttach(sid, false)).then((o) => {
      // Re-sending the resume is safe: if the first was admitted the session is busy
      // and the server re-attaches; if not, it is admitted now.
      if (isRecoverableOutcome(o)) void recoverLostRunRef.current?.(sid, sidWorkspaceId, { resume: true })
    })
  }, [attachSession])
  const beginResumeRef = useRef(beginResume)
  beginResumeRef.current = beginResume

  // #352 I3b: a kept client-tool result's re-POST can report `resumeReady` from the
  // page-wide pump, with no hook's closure behind it — so every mounted hook offers
  // to start a resume (with the KEPT session and workspace), and a resume requested
  // while none was mounted (the pump right after a reload) runs on the first mount.
  useEffect(() => registerResumeStarter((sid, ws) => beginResumeRef.current(sid, ws ?? workspaceId)), [workspaceId])
  // The pump itself: START-ONLY and idempotent per page. No cleanup stops it — a
  // StrictMode double-invoke or a surface unmounting must not end the re-sends.
  useEffect(() => { startResultPump(postKeptResult, refreshKeptClaim, 5000) }, [])

  // #813: re-attach to a run whose live stream was cut (an API restart), retrying
  // while the instance comes back. The chat stays 'processing' throughout (the
  // /pending recovery poll keeps running as a backstop).
  //  - The pre-cut streamed text is cleared first: the re-attached stream carries no
  //    replay, and its fragment would otherwise race the old text (longer-wins) and
  //    commit a truncated message; the final reload restores the canonical text.
  //  - The lost send's OWN optimistic bubble (`tempId`) stays on screen during
  //    recovery (nothing else shows the message until a reload) and is registered as
  //    STORED (storedTempIds): the next canonical load — ours at the end, the end-of-
  //    run reload of whichever surface took the run over, a session switch — swaps it
  //    for the stored copy. Never a reload mid-turn (it can wipe parts streamed while
  //    it is in flight). After a cancel, one reload once the turn is really over.
  //  - The message is treated as STORED: either its stream had begun, or the server
  //    said our runId was admitted. Known limit (accepted): an instance killed between
  //    admitting and persisting a message loses it; the restart drain (it waits up to
  //    6s for in-flight requests) makes that a hard-kill-only case.
  //  - An attempt NEVER claims over another run (a resend, a resume, another
  //    surface): it stops ('superseded'); an ABORTED attempt counts as superseded,
  //    never as success. Cancel and unmount stop it too (checked after each backoff).
  //  - Only a recovery that keeps failing shows an honest error.
  const recoverLostRun = useCallback(async (sid: string, sidWorkspaceId: string, opts: RecoverOpts = {}) => {
    // #1316: a recovery belongs to the session it started in — it stops when that session ends (a re-attach after it
    // would go out as the next session).
    const start = currentSessionSignal()
    // #1316: stopped by its session's end, a recovery leaves the chat idle (no Stop over a turn this tab no longer
    // follows; a same-tenant identity switch keeps this hook mounted) — unless a newer claim (the next session's own
    // attach) holds the run: that one is never overwritten. True = the recovery is over.
    const endedWithSession = (): boolean => {
      if (!sessionEndedSince(start)) return false
      if (!sessionStore.getLiveRun(sid)) {
        sessionStore.update(sid, (p) => ({ ...p, status: 'idle', reconnecting: false, streamingText: '', streamingReasoning: '', streamingReasoningStart: null }))
      }
      return true
    }
    stoppedRecoveries.current.delete(sid)
    if (opts.tempId) markStoredTemp(sid, opts.tempId)
    sessionStore.update(sid, (prev) => ({ ...prev, streamingText: '', streamingReasoning: '', streamingReasoningStart: null }))
    const outcome = await recoverRun(async () => {
      if (sessionStore.getLiveRun(sid)) return 'superseded'
      const liveToken = {}
      const ctrl = new AbortController()
      sessionStore.claimLiveRun(sid, liveToken, ctrl)
      attachControllers.current.set(sid, ctrl)
      const r = await attachSession(sid, sidWorkspaceId, ctrl.signal, liveToken, !!opts.resume)
      // #762: a re-attach that streamed (to the end, or until a later cut) is a recovery that WORKED — a later drop
      // starts a fresh episode, never one whose deadline ran out while the stream was fine.
      if (r === 'streamed' || r === 'cut-after-progress' || r === 'reconnect-after-progress') reconcileEpisodes.current.delete(sid)
      return ctrl.signal.aborted ? 'superseded' : r
    }, {
      shouldStop: () => unmounted.current || stoppedRecoveries.current.has(sid) || sessionEndedSince(start),
    })
    if (endedWithSession()) return
    if (outcome === 'gave-up') {
      if (sessionStore.getLiveRun(sid)) { reconcileEpisodes.current.delete(sid); return }   // another run took over
      // #762: not an error yet — the connection usually comes back while the turn runs on (or has just ended). Say
      // so calmly and keep checking; only an episode that lasts RECONCILE_GIVE_UP_AFTER_MS (no answer, or re-attaches
      // that fail without streaming anything while the server says "running") ends in the error.
      const ep = reconcileEpisodes.current.get(sid) ?? { deadline: Date.now() + RECONCILE_GIVE_UP_AFTER_MS, cycles: 0 }
      ep.cycles++
      reconcileEpisodes.current.set(sid, ep)
      sessionStore.update(sid, (prev) => ({ ...prev, reconnecting: true }))
      const wake = onceBackOnline()
      let after: Awaited<ReturnType<typeof reconcileAfterGiveUp>>
      try {
        after = await reconcileAfterGiveUp(sid, {
          fetchBusy: () => request<Array<{ id: string }>>('/api/agent/sessions/status'),
          shouldStop: () => unmounted.current || stoppedRecoveries.current.has(sid) || !!sessionStore.getLiveRun(sid) || sessionEndedSince(start),
          wakeSignal: wake.next,
          giveUpAfterMs: Math.max(0, ep.deadline - Date.now()),
          firstDelayMs: Math.min(3000 * 2 ** (ep.cycles - 1), 15_000),   // a repeat cycle waits longer
        })
      } finally {
        wake.dispose()
      }
      sessionStore.update(sid, (prev) => ({ ...prev, reconnecting: false }))
      if (endedWithSession()) return
      if (after === 'running') {
        void recoverLostRunRef.current?.(sid, sidWorkspaceId, opts.resume ? { resume: true } : {})   // re-attach; the episode carries on
        return
      }
      if (after === 'finished' && opts.resume && !ep.resumeResent) {
        // A RESUME not in the busy set either never reached the server (the parked turn still waits) or ran and ended.
        // Sending it once more tells them apart, as a resend is safe (the server runs it, or answers "not running" and
        // the turn is reloaded); the episode carries on.
        ep.resumeResent = true
        void recoverLostRunRef.current?.(sid, sidWorkspaceId, { resume: true })
        return
      }
      const resumeStillUnknown = after === 'finished' && opts.resume   // resent once already, and still not running
      reconcileEpisodes.current.delete(sid)
      if (after === 'finished' && !resumeStillUnknown) {
        // The turn ended while we were away: show what it did, as the 'not-running' end of a recovery does. Its cards
        // are KEPT — a turn parked on a permission or question leaves the busy set when a worker shutdown suspends it,
        // and its card must still be answerable.
        sessionStore.update(sid, (prev) => ({ ...prev, status: 'idle', streamingText: '', streamingReasoning: '', streamingReasoningStart: null }))
        if (sid === sessionIdRef.current) {
          reloadRef.current.messages()
          reloadRef.current.cost()
          reloadRef.current.todos()
        }
        return
      }
      if (after === 'timed-out' || resumeStillUnknown) {
        // No answer for RECONCILE_GIVE_UP_AFTER_MS, or the connection kept failing that long while the server said
        // "running" — or a resume that read "finished" even after being sent again (it may still be parked, with its
        // card already answered): a reload is the way back (it resumes a parked turn).
        sessionStore.update(sid, (prev) => ({
          ...prev,
          error: 'Lost the connection to the assistant. It may still be working — reload the page to see the latest.',
        }))
      }
      return
    }
    reconcileEpisodes.current.delete(sid)   // re-attached (or the turn is over): this episode is done
    if (outcome === 'stopped' && stoppedRecoveries.current.has(sid)) {
      // Stopped by a CANCEL: nothing else reloads, so once the turn is really over
      // show the stored transcript (the prompt the user sent included).
      for (let i = 0; i < 20 && isTurnLive(sessionStore.getState(sid).status) && !sessionEndedSince(start); i++) {
        await new Promise((r) => setTimeout(r, 500))
      }
      if (sid === sessionIdRef.current && !isTurnLive(sessionStore.getState(sid).status) && !sessionEndedSince(start)) reloadRef.current.messages()
      return
    }
    if (outcome === 'not-running') {
      sessionStore.update(sid, (prev) => ({ ...prev, status: 'idle', streamingText: '', streamingReasoning: '', streamingReasoningStart: null }))
    }
    // The turn is over for 'streamed' / 'not-running' — reload it canonically.
    // 'stopped': whoever took the run over reloads at its own end.
    if (outcome !== 'stopped' && sid === sessionIdRef.current) reloadRef.current.messages()
  }, [attachSession])
  recoverLostRunRef.current = recoverLostRun

  // Abort every in-flight attach when the hook UNMOUNTS (component gone) — never on
  // a session switch, which is what used to strand background streams (finding 8).
  useEffect(() => {
    const controllers = attachControllers.current
    unmounted.current = false
    return () => { unmounted.current = true; for (const c of controllers.values()) c.abort(); controllers.clear() }
  }, [])

  // On mount / session change: re-attach to the DISPLAYED session's in-flight run +
  // restore a pending HITL prompt. AG-UI is the only transport (A3) — run events
  // arrive on the /api/agent/agui POST→SSE, not a WS subscription.
  useEffect(() => {
    if (!sessionId) return
    let cancelled = false
    // (1) Re-attach to the displayed run (page-reload-mid-run recovery). Self-managed
    // per-sid controller — survives a later switch as a background stream (finding 8).
    beginAttach(sessionId, workspaceId)

    // (2) Restore a permission/question that was ALREADY pending before this mount.
    // The AG-UI resume stream won't replay it (pub/sub is fire-and-forget), so this
    // REST recovery stays. Guarded by `cancelled`: if we switch away before it
    // resolves, don't apply the OLD session's pending to the new display.
    request<{ pending: SessionProcessor.StreamEvent | null; resumeReady?: boolean }>(`/api/agent/sessions/${sessionId}/pending?workspaceId=${encodeURIComponent(workspaceId)}`)
      .then(res => {
        // #540 PR E: a SUSPENDED turn was answered but its resume run never started
        // (e.g. the tab closed right after the answer) — start it now.
        if (!cancelled && res?.resumeReady) { beginResumeRef.current(sessionId, workspaceId); return }
        if (cancelled || !res?.pending) return
        if (res.pending.type === 'client-tool-request') {
          // Reload-mid-db_editor recovery: run the handler + POST the AG-UI
          // tool-result so the worker (blocked in ctx.clientTool) resolves. A
          // wait whose result this page KEPT (sessionStorage survives a reload)
          // is re-sent, never re-run (#352 I3b).
          void runClientToolByCall(res.pending, true)
          return
        }
        reloadRef.current.messages()
        handleAgentEvent(res.pending)
        const statusType = res.pending.type === 'permission-request' ? 'awaiting_permission' : 'awaiting_answer'
        handleAgentEvent({ type: 'status', sessionId, status: statusType } as SessionProcessor.StreamEvent)
      })
      .catch(() => {}) // Non-critical — worst case user sees no prompt

    return () => { cancelled = true }
  }, [sessionId, workspaceId, beginAttach, handleAgentEvent, runClientToolByCall])

  // #352 same-browser hand-off (owner 2026-10-22): another tab of this browser is waiting on a call
  // it can't resolve — if this tab shows that session, ask /pending NOW instead of on the next poll,
  // and the claim gate decides (this tab claims at once only when it can resolve the call).
  useEffect(() => {
    if (!sessionId) return
    return onUnresolvedWaitAnnounced((n) => {
      if (n.sessionId !== sessionId || (n.workspaceId && n.workspaceId !== workspaceId)) return
      request<{ pending: SessionProcessor.StreamEvent | null }>(`/api/agent/sessions/${sessionId}/pending?workspaceId=${encodeURIComponent(workspaceId)}`)
        .then(res => { if (res?.pending?.type === 'client-tool-request') void runClientToolByCall(res.pending, true, sessionId) })
        .catch(() => {}) // the 5 s poll still runs
    })
  }, [sessionId, workspaceId, runClientToolByCall])

  // Cross-tab / cross-device + backgrounded live sync. Watch the tenant-wide busy set
  // and (re-)attach EVERY running session into its own store entry — so a run started
  // by another tab/device, or any backgrounded chat, streams live. Per-sid guards
  // (liveRun / attachControllers) keep each to a single stream across instances.
  //
  // The scan itself is a SHARED query (useBusySessions), not a per-instance
  // setInterval: this hook is mounted by several surfaces at once (ChatPanel,
  // HeroChat), so a local timer meant one request stream per mounted instance.
  const { data: busySessions } = useBusySessions()
  useEffect(() => {
    if (!busySessions) return
    // Each row carries its OWN workspaceId (server includes it) — pass it so a
    // cross-workspace run attaches its correct per-workspace DB (finding 3). These
    // attaches are NOT aborted on switch — only on unmount (finding 8).
    // NOT for a compaction. It rides the prompt queue so the session is busy
    // (that is the #466 fence), but it is not a RUN: attaching opens an AG-UI
    // resume stream whose RUN_STARTED puts the client in `processing`, which
    // renders the composer as "Add instructions while it works…" and shows
    // Stop. Both lie — nothing in the compaction job subscribes to the steer
    // channel, so typed text is published to nobody and destroyed, and nothing
    // consults the abort flag, so Stop cannot stop it.
    //
    // The spinner does not depend on this: it runs off `summarizing` and the
    // busy poll, not off an attached stream.
    for (const b of busySessions) {
      if (b.kind === 'compaction') continue
      beginAttach(b.id, b.workspaceId)
    }
  }, [busySessions, beginAttach])

  // ── Compaction is visible in EVERY tab, not just the one that asked ────
  //
  // `compacting` used to be set by `compaction-progress`, which travels only on
  // an attached AG-UI stream — and the loop above deliberately no longer opens
  // one for a compaction. `summarizing` is per-hook state set inside
  // `summarize()`, so it too only exists in the instance that started it.
  //
  // Without this a second tab (or the same session opened on another device)
  // shows no indicator at all AND an inviting composer, and the message the
  // user then sends is refused with 409 — a composer that asks for input and
  // then rejects it. The busy poll already knows; this reads it.
  // ── A manual compaction is DERIVED from the poll, never stored ─────────
  //
  // This effect has been rewritten five times and every version failed the same
  // way, because it was storing something derived. `state.compacting` lives in
  // the per-SESSION store while the guards deciding when to clear it were
  // per-HOOK refs — different lifetimes, so they desynced on a session switch
  // and the poll ended up lowering a flag the STREAM had raised for a different
  // session, killing a live auto-compaction indicator.
  //
  // So it is not stored. The poll already knows whether the displayed session
  // is compacting; reading that at render is correct by construction — on a
  // session switch, across tabs, when the row disappears, and with no ordering
  // rule between two writers. `state.compacting` remains the AUTO path's flag,
  // owned solely by `compaction-progress`, and the two are OR'd where the hook
  // returns. Nothing lowers what it did not raise, because nothing lowers
  // anything.
  const pollCompacting = !!sessionId
    && !!busySessions?.some(b => b.id === sessionId && b.kind === 'compaction')

  // ── The displayed session finished a compaction (#443) ─────────────────
  //
  // A manual /summarize is NOT an AG-UI run: no run stream, so
  // `compaction-progress` never arrives, so `resyncAfterTurn` is never set, so
  // the transcript is never refetched. Compaction rewrites the conversation
  // server-side — a boundary, the summary, a nudge — and none of those rows
  // travel on any live channel, so without this the work succeeded, charged,
  // and left the UI looking untouched until the tab was reloaded.
  //
  // The refetch is an ACTION, so unlike the flag above it does need an edge.
  // Two things can produce it, and both are needed:
  //
  //  - THE POLL'S OWN true→false, which every viewer sees. Keying only on "this
  //    tab issued the summarize" left a second tab (or a phone) showing the
  //    indicator, then dropping it, with the transcript unchanged for the life
  //    of that tab — the exact symptom above, in the multi-tab case #466 is
  //    written about. A re-render fetches nothing on its own.
  //  - THE AGE FLOOR for the issuing tab, because a ~5s job between two 15s
  //    polls is never observed at all, so there is no edge to see.
  const queryClient = useQueryClient()
  /** The poll's last answer, PER SESSION. Per-session because the hook keeps
   *  rendering across session switches: a single boolean would carry session
   *  A's "was compacting" into session B and fire a reload there. */
  const prevPollCompactingRef = useRef<Record<string, boolean>>({})
  /** The session this tab last asked to compact, and when. Session-scoped for
   *  the same reason — armed on A, it must not fire on B. */
  const compactRequestRef = useRef<{ sid: string; at: number } | null>(null)
  useEffect(() => {
    if (!sessionId || !busySessions) return
    const wasCompacting = prevPollCompactingRef.current[sessionId] ?? false
    prevPollCompactingRef.current[sessionId] = pollCompacting
    if (pollCompacting) return

    const req = compactRequestRef.current
    const mine = req?.sid === sessionId
    // Observed finishing, or this tab's own request is old enough that it
    // cannot still be waiting to start.
    const finished = wasCompacting || (mine && Date.now() - req!.at > 8_000)
    if (!finished) return
    if (mine) compactRequestRef.current = null

    // DEFER while a turn is live. `loadMessages` replaces `state.messages`
    // wholesale (only `temp_` rows survive), so landing it between the last
    // delta and `complete` drops the streaming message and `complete` then
    // finds no target — the text simply vanishes. That is the hazard
    // `resyncAfterTurn` exists for, and this path reaches it on the designed
    // common flow: summarize, then send as soon as it finishes, and the next
    // poll flips this false ~2s into the new turn.
    //
    // The decision is read from `status`, a render value, and the reload is
    // fired OUTSIDE the updater: `setState` updaters must be pure, and
    // StrictMode double-invokes them — a fetch in there would run twice.
    const streaming = isTurnLive(state.status)
    setState(prev => {
      if (streaming) return { ...prev, summarizing: false, resyncAfterTurn: true }
      return prev.summarizing ? { ...prev, summarizing: false } : prev
    })
    if (!streaming) void reloadRef.current.messages()
  }, [busySessions, sessionId, pollCompacting, state.status])

  // Recovery: poll /pending when status is 'processing' to detect missed events
  // (permission requests or job completion that the WebSocket didn't deliver)
  useEffect(() => {
    const status = state.status
    if (status !== 'processing' && status !== 'submitting') return
    if (!sessionId) return

    const interval = setInterval(async () => {
      try {
        const res = await request<{ pending: SessionProcessor.StreamEvent | null; resumeReady?: boolean }>(`/api/agent/sessions/${sessionId}/pending?workspaceId=${encodeURIComponent(workspaceId)}`)
        const outcome = pollOutcome(res, status)
        if (outcome === 'resume') {
          // #352 I3b: a SUSPENDED turn is answered — the worker carried a client-tool
          // result into its row (the POSTing page got only `{ok:true}`), or another
          // tab answered it. Start its resume run for the POLLED session; the orphan
          // branch below would force-idle a turn that only needs resuming.
          // R-13: once per session — not while its resume attach is live, nor
          // within 10 s (each beginResume aborts and restarts the attach).
          if (takeResumeRequest(sessionId)) requestResume(sessionId, workspaceId)
        } else if (outcome === 'run-client-tool' && res?.pending?.type === 'client-tool-request') {
          // Mid-db_editor reload recovery (see the mount effect above):
          // run the handler + POST the result (once per page); the worker resolves
          // instead of stalling. Not a HITL prompt. `recovered: true`
          // for the same restored-editor race tolerance as the mount path.
          // Fable r4: bind the POLLED session id so a tab switch during the
          // /pending await doesn't POST this result under the displayed session.
          void runClientToolByCall(res.pending, true, sessionId)
        } else if (outcome === 'restore-prompt' && res?.pending) {
          // Permission/question is waiting — restore the prompt.
          // Also reload messages: if we're recovering from a page refresh mid-turn,
          // the initial loadMessages may have raced the DB write and missed the
          // user + assistant messages that kicked off this pending request.
          reloadRef.current.messages()
          // Fable r4: route the restored prompt + status into the POLLED session's
          // OWN store entry (same divergence class as the orphan force-idle below).
          // handleAgentEvent defaults `apply` to setState → storeKeyRef.current
          // (the DISPLAYED session); if the user switched tabs during the /pending
          // await, that would write session A's awaiting_permission + prompt into
          // session B. The status event already NAMES `sessionId` (A) in its
          // payload, so applying it to A is what makes it self-consistent.
          const applyToPolled = (updater: (prev: AgentSessionState) => AgentSessionState) => sessionStore.update(sessionId, updater)
          handleAgentEvent(res.pending, applyToPolled)
          const statusType = res.pending.type === 'permission-request' ? 'awaiting_permission' : 'awaiting_answer'
          handleAgentEvent({ type: 'status', sessionId, status: statusType } as SessionProcessor.StreamEvent, applyToPolled)
        } else if (outcome === 'orphan-check') {
          // No pending request AND no live stream/SSE has settled us — the job
          // may have finished (or been killed by a worker restart) without our
          // ever seeing the completion event. `/pending` being null is
          // ambiguous on its own (a running job with no HITL prompt looks
          // identical), so confirm against the authoritative busy set — the
          // same BullMQ active+waiting scan the rail dots use — before giving
          // up. One confirmation is enough: if this session isn't busy, the run
          // is genuinely orphaned, so mirror cancel()'s safety timeout and
          // force the session back to idle — but NOT while a stream for it is
          // still delivering frames: the job leaves the busy set before the
          // stream's tail (final text, RUN_FINISHED) reaches the browser, and
          // aborting then lost the ending (shouldForceIdleOrphan).
          //
          // Deliberately NOT the shared useBusySessions cache: this read decides
          // whether to force a session out of 'processing', and acting on a
          // cache entry that predates the run would wrongly kill a live turn.
          // It also only fires on the narrow processing-with-no-pending path,
          // so it is not part of the steady-state polling load.
          let busy: Array<{ id: string }>
          try {
            busy = await request<Array<{ id: string }>>('/api/agent/sessions/status')
          } catch {
            return // status endpoint unreachable — leave 'processing', retry next tick
          }
          if (orphanPollShouldForceIdle(sessionId, busy)) {
            // First, while the claim still exists: the turn's own bubble → stored copy.
            registerOrphanStoredTemp(sessionId)
            // Fable r3: force-idle the polled session by its OWN key, matching the
            // abort above. `setState` routes to storeKeyRef.current (the DISPLAYED
            // session), which can diverge from `sessionId` if the user switched
            // tabs during this callback's awaits → it would flip the wrong session.
            sessionStore.abortLiveRun(sessionId) // B(b)3: close any stale stream, don't just relabel idle
            sessionStore.update(sessionId, prev => forceIdleIfStuck(prev, 'Orphan recovery (job not in busy set)'))
            // The server has the finished turn; the aborted stream never delivered its
            // ending, so fetch it. (The send loop's "Do NOT reloadRef.messages()" is about
            // merging over a stream that FINISHED; this one was aborted — nothing streamed
            // is left to double.) Displayed session only: reloadRef is the displayed view's.
            // Cost and todos too, as the normal end of a turn refreshes them.
            if (sessionId === sessionIdRef.current) {
              reloadRef.current.messages()
              reloadRef.current.cost()
              reloadRef.current.todos()
            }
          }
        }
      } catch {}
    }, 5000)

    return () => clearInterval(interval)
  }, [state.status, sessionId, workspaceId, handleAgentEvent, runClientToolByCall])

  // #540: client-DRIVEN presence heartbeat during a permission/question WAIT.
  // The recovery poll above only runs while status is 'processing'/'submitting'
  // and stops the moment it flips to awaiting_* (the actual wait), so nothing
  // else pings the server then. Hit /pending on an interval so the server
  // refreshes the presence marker (session.ts) — a client→server signal that
  // stops the instant the tab closes. That is what lets the worker's permission
  // wait give up on a disconnected client: the SSE heartbeat cannot, because the
  // CF/nginx edge holds the SSE open long after the client leaves (R6). Ignores
  // the response — the prompt is already on screen.
  useEffect(() => {
    const status = state.status
    if (status !== 'awaiting_permission' && status !== 'awaiting_answer') return
    if (!sessionId) return
    // I198 review round 6: the response is no longer ignored — a SUSPENDED turn whose
    // answer the worker carried into its row reports `resumeReady` here, and this tab
    // starts the resume (see awaiting-ping.ts).
    const pingOnce = createAwaitingPing({
      fetchPending: () => request<{ resumeReady?: boolean }>(`/api/agent/sessions/${sessionId}/pending?workspaceId=${encodeURIComponent(workspaceId)}`),
      onResumeReady: () => beginResumeRef.current(sessionId, workspaceId),
    })
    const ping = () => { void pingOnce() }
    ping() // immediate, so presence is fresh the moment the wait begins
    const interval = setInterval(ping, 5000)
    return () => clearInterval(interval)
  }, [state.status, sessionId, workspaceId])

  // #841: this view's DISPLAYED session, while it holds a card. Background chats
  // are ChatPanel's source (open tabs ∩ busy). Never report every loaded store
  // entry: the busy-set attach keeps every busy session's card loaded, so a card
  // would stay alive after its chat tab was closed — and closing it means leaving.
  const presenceDisplayedRef = useRef<string[]>([])
  useEffect(() => {
    presenceDisplayedRef.current = sessionId && hasOpenCard(state) ? [sessionId] : []
  }, [sessionId, state])
  useEffect(() => presenceHeartbeat.register(() => presenceDisplayedRef.current), [])
  useEffect(() => {
    if (state.status === 'awaiting_permission' || state.status === 'awaiting_answer') presenceHeartbeat.poke()
  }, [state.status])

  // #1316: bumped at a session end, so the bus subscription below is opened again for the new session.
  const [eventStreamEpoch, setEventStreamEpoch] = useState(0)

  // Subscribe to Bus events via SSE (/api/agent/event)
  useEffect(() => {
    const base = getApiBase()
    const es = new EventSource(`${base}/api/agent/event`, { withCredentials: true })
    // #1316: the server authenticates this stream by cookie when it CONNECTS, so after an account change it would go
    // on delivering the old account's bus events. A session end closes it at once and reopens it (the epoch is in this
    // effect's deps) — after the commit, never from inside the end listener (that would connect as the old session).
    // THIS tab's sign-out only closes it: its end lands BEFORE the server's sign-out completes, so a new source would
    // connect on the still-valid old cookie (and the page reloads once the server answered). Every other end reopens
    // it — review round 1 (B-3): a sign-out in ANOTHER tab is announced only after its server call, so a reopen
    // connects on what the server holds now: if that sign-out FAILED the user stays here and needs the bus; if the user
    // is gone (or changed, or the session died) the page reloads (the account end's full load) anyway.
    const offSessionEnd = onSessionEnd((reason) => {
      es.close()
      if (reason === 'signed-out') return
      setEventStreamEpoch((n) => n + 1)
    })

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data) as { type: string; properties: Record<string, unknown> }

        // Broadcast workspace-scoped events before session filtering
        if (data.type === 'workspace.model-preference-updated') {
          window.dispatchEvent(new CustomEvent('papan:workspace-preference', { detail: data.properties }))
          return
        }

        const props = data.properties
        // Route each event to ITS OWN session's store entry (run-anchored), NOT the
        // displayed session. Bus events are server-pushed for real sessions and the
        // store is keyed by session id, so a backgrounded chat's summary / revert /
        // todos land in its own entry and stay live — and can never bleed into
        // whatever chat is on screen. (Previously this dropped every non-displayed
        // event and wrote via the display-bound setState. #254 review — chat
        // concurrency: complete the run-anchored migration.)
        const evSid = (props.sessionID as string | undefined)
          ?? (props.info as { sessionID?: string } | undefined)?.sessionID
          // `message.part.updated`'s payload is `{ part }` with no top-level
          // sessionID — the part itself carries it (`PartBase.sessionID`).
          ?? (props.part as { sessionID?: string } | undefined)?.sessionID
        if (!evSid) return
        // #352 I1: a part of the displayed session that this page isn't streaming is
        // a run started elsewhere (another tab or device) — attach now, not at the
        // busy poll's next 15 s tick, or its client-tool wait goes to a tab that
        // can't resolve it (foreign-run-discovery.ts).
        if ((data.type === 'message.part.updated' || data.type === 'message.part.oversized')
          && shouldNudgeForeignRun({ evSid, displayedSid: sessionIdRef.current, streaming: sessionStore.getLiveRun(evSid) || attachControllers.current.has(evSid) })) {
          void queryClient.invalidateQueries({ queryKey: busySessionsKey })
        }
        const applyToEvent = (updater: (prev: AgentSessionState) => AgentSessionState) => sessionStore.update(evSid, updater)

        switch (data.type) {
          case 'summary.updated':
            applyToEvent(prev => ({
              ...prev,
              summary: props.info as unknown as SummaryInfo,
            }))
            break

          case 'compaction.created':
            // ── The PUSH signal that a compaction finished (#443) ─────────
            //
            // Bus events are tenant-scoped and server-pushed on this SSE, so
            // this reaches EVERY tab viewing the session — with no AG-UI
            // attach (the attach loop skips compaction jobs on purpose: an
            // attached stream makes the composer offer Steer and Stop, and a
            // compaction job subscribes to neither) and with no dependence on
            // the busy poll.
            //
            // That last part is why this is here rather than left to the poll.
            // The poll's true→false edge cannot be relied on for the refetch:
            // a compaction takes ~5s and the poll idles at 15s, so a second tab
            // usually never samples the busy window at all, and even in the
            // issuing tab a run of the effect requires one of its deps to
            // CHANGE — TanStack's structural sharing returns the same array
            // reference for two equal responses, so an elapsed-time floor can
            // never fire on its own. The poll remains the INDICATOR's source
            // (`pollCompacting`) and a fallback edge for the refetch; this is
            // the signal that actually arrives.
            applyToEvent(prev => {
              if (isTurnLive(prev.status)) {
                // Mid-turn: this is an AUTO compaction, whose own
                // `compaction-progress` already sets `resyncAfterTurn`. Setting
                // it again is idempotent — `complete` consumes it once — and
                // refetching here would replace `state.messages` wholesale
                // between the last delta and `complete`, dropping the streamed
                // message.
                return { ...prev, compacted: true, summarizing: false, resyncAfterTurn: true }
              }
              return { ...prev, compacted: true, summarizing: false }
            })
            // Only the DISPLAYED session's transcript is refetched here:
            // `reloadRef` is bound to it, and a background session reloads when
            // it is next opened. Same guard as the reducer's `reloadDisplayed`.
            if (evSid === sessionIdRef.current && !isTurnLive(sessionStore.getState(evSid).status)) {
              void reloadRef.current.messages()
            }
            break

          case 'session.reverted':
            applyToEvent(prev => ({
              ...prev,
              revertState: {
                reverted: true,
                info: props.info as unknown as RevertState['info'],
              },
              // Never reset summary — the menu item stays visible as long as
              // the session ever had file changes. Re-reverting is idempotent.
              // Reset processing state — the server cancels the worker on revert,
              // so the UI should unblock immediately rather than waiting for the
              // 'cancelled' stream event (which may arrive late or not at all).
              status: 'idle',
              streamingText: '',
              streamingReasoning: '',
              streamingReasoningStart: null,
              activeToolCalls: new Map(),
              pendingPermissions: [],
              pendingQuestions: [],
              turnActivity: null,
              retry: null,
            }))
            break

          case 'todo.updated':
            applyToEvent(prev => ({
              ...prev,
              todos: (props.todos as unknown as TodoItem[]) ?? prev.todos,
            }))
            break

          case 'message.part.oversized': {
            // #874 — the part was too large for one frame; only its ids came. A
            // part streamed in a live turn is not in the store under this id (it
            // came over the AG-UI stream), so only an already-loaded part — a
            // server rewrite, another tab's PATCH — is patched, from the saved
            // message. Never a transcript reload (it races the worker's save).
            const messageID = props.messageID as string
            const partID = props.partID as string
            if (evSid === sessionIdRef.current && sessionStore.getState(evSid).messages.some(m =>
              m.id === messageID && m.parts.some(p => p.id === partID || p.id === `${partID}_result`))) {
              reloadRef.current.part(messageID, partID)
            }
            break
          }

          case 'papan.event-oversized': {
            // #874 — any other event too large for one frame (event.ts busEventFrame).
            // A todo list is not in the transcript: reload the todos for it (the
            // displayed session's; another session's load on open).
            if (props.eventType === 'todo.updated') {
              if (evSid === sessionIdRef.current) reloadRef.current.todos()
              break
            }
            // Idle and on screen: reload now. Mid-turn, or another session: the
            // next load shows it — a turn-end reload would race the worker's save.
            if (canReloadNow(sessionStore.getState(evSid).status, evSid === sessionIdRef.current)) {
              void reloadRef.current.messages()
            }
            break
          }

          case 'message.part.updated': {
            // #662 — a re-pointed (moved) file part, or any server-side part
            // rewrite. Patch it in place so the chip re-points live, whether
            // the move came from the AI tool or from another tab, without a
            // reload. See `applyPartUpdateEvent` for why this goes through
            // `mapApiPartToUi` rather than a raw spread merge.
            const rawPart = props.part as { id: string; messageID: string } & Record<string, unknown>
            applyToEvent(prev => ({
              ...prev,
              messages: applyPartUpdateEvent(prev.messages, rawPart),
            }))
            break
          }
        }
        // If this event was for a session with no mounted view and no live run,
        // `applyToEvent` just minted an orphan entry — drop it so bus events don't
        // leak the store (#269 review, finding 7). Re-fetched when the session opens.
        sessionStore.gcIfIdle(evSid)
      } catch {
        // Ignore malformed SSE messages
      }
    }

    return () => {
      offSessionEnd()
      es.close()
    }
  }, [eventStreamEpoch])

  const wsQuery = `?workspaceId=${encodeURIComponent(workspaceId)}`

  // Load session data
  const loadSession = useCallback(async () => {
    if (!sessionId) return
    try {
      const data = await request<AgentSessionInfo>(
        `/api/agent/sessions/${sessionId}${wsQuery}`,
      )
      setState(prev => ({ ...prev, session: data }))
    } catch {
      // Session may not exist yet
    }
  }, [sessionId, wsQuery])

  // Load messages — transforms API shape { info, parts } into UI shape
  /** In-flight guard for `summarize()` — see its own comment. */
  const summarizingRef = useRef(false)

  // Generation-safe, for the same reason `loadSessionCost` is — and with worse
  // consequences, because this one REPLACES the whole array.
  //
  // Several unawaited callers can be in flight at once (the compaction push,
  // the busy-poll fallback edge, `complete`'s deferred resync, the mount load),
  // and the branch's own design invites a send the moment a summarize lands.
  // So: the push fires a reload of a post-compaction transcript — hundreds of
  // KB on a session that just hit the ~240K trigger — the user presses Enter
  // ~100ms later, the send's confirmation renames the `temp_` row to its real id,
  // and THEN the slow response resolves and writes the pre-send transcript with
  // no `temp_` rows left to preserve. The message the user just sent vanishes,
  // and is restored only if `resyncAfterTurn` happened to be set.
  const loadMessages = useCallback(async () => {
    // BUMPED BEFORE THE EARLY RETURN, which is the whole point of it being
    // here. A real→real switch invalidates the old fetch because the new
    // session's own call bumps; a real→DRAFT switch does not call through
    // (`sessionId` is null for a draft), so with the bump below the return an
    // in-flight response stayed valid — and `storeKeyRef.current` had already
    // re-pointed at the draft, so the previous chat's transcript was written
    // into the blank "New chat" tab and never corrected, since no further
    // reload runs for a draft.
    const myGen = ++messagesGenRef.current
    if (!sessionId) return
    // The transcript as it was when this request STARTED. Anything the client
    // gains while the response is in flight is the client's own news and must
    // survive the write below — see the merge at the end.
    // Read from the STORE, not a per-hook ref: `setState` writes through
    // `storeKeyRef.current`, so the comparison has to be against the same
    // entry or a session switch mid-fetch would compare two conversations.
    const idsAtRequestStart = new Set(
      sessionStore.getState(storeKeyRef.current).messages.map(m => m.id),
    )
    // #813: only stored bubbles registered BEFORE this request can be in its data.
    const storedMarkAtStart = storedTempMark()
    try {
      const data = await request<Array<{ info: any; parts: any[] }>>(
        `/api/agent/sessions/${sessionId}/message${wsQuery}`,
      )
      if (myGen !== messagesGenRef.current) return
      const mapped: MessageWithParts[] = data.map(msg => ({
        id: msg.info.id,
        role: msg.info.role,
        parts: (msg.parts || []).flatMap((p: any) => mapApiPartToUi(p, msg.info.id)),
        createdAt: msg.info.time?.created ? String(msg.info.time.created) : undefined,
        modelID: msg.info.modelID,
        personaId: msg.info.personaId,
        timeCreated: msg.info.time?.created,
        timeCompleted: msg.info.time?.completed,
        cost: msg.info.cost,
        // Passed straight through from the SQLite message blob (written by the
        // worker). No conversion here — the value is already the settled,
        // per-message billing-native amount.
        costBillingMicro: msg.info.costBillingMicro,
        costCurrency: msg.info.costCurrency,
        parentID: msg.info.parentID,
        synthetic: msg.info.synthetic,
        // #481 item 4: the persisted steer flag, so the "steered into this turn"
        // marker survives a reload (it is why the flag is persisted at all).
        steer: msg.info.steer,
        // The authoritative "this assistant message is a compaction summary"
        // marker. `modelID === 'compaction'` only agrees while both callers
        // leave `model` unset — process() writes `input.model ?? 'compaction'`.
        isSummary: msg.info.summary,
      }))
      // Remove orphan assistant messages whose parent user message was deleted
      // (happens when worker restarts mid-job after edit-resend deletes the user message)
      // Also hide system role messages — they're injected context for the AI only.
      const messageIds = new Set(mapped.map(m => m.id))
      const filtered = mapped.filter(m => {
        if (m.role === 'system') return false
        if (m.role === 'assistant' && m.parentID && !messageIds.has(m.parentID)) return false
        return true
      })

      // Clean up stale tool calls — only for assistant messages whose turn
      // actually finished (timeCompleted is set). For in-flight turns the
      // worker may legitimately be mid-execution (e.g. cred_to_env blocked
      // on a pending question), so leave those alone.
      // A finished turn means the agent successfully concluded — a tool
      // that genuinely failed carries a tool-result error; a dangling
      // running/pending one only means its result wasn't persisted/
      // streamed (transport gap), NOT a failure. Finalize as 'completed'
      // (matches the live `complete` sweep — keeps a reload from
      // re-painting a red-cross error on an op that actually succeeded).
      for (const msg of filtered) {
        if (msg.role !== 'assistant' || !msg.timeCompleted) continue
        for (const part of msg.parts) {
          if (part.type === 'tool-call' && (part.status === 'running' || part.status === 'pending')) {
            part.status = 'completed'
          }
        }
      }

      // Extract last personaId from messages for session persona restoration
      const lastPersona = [...filtered].reverse().find(m => m.personaId)?.personaId
      // Re-checked after the mapping above, which is synchronous but not free
      // on a large transcript — a newer call can have started meanwhile.
      if (myGen !== messagesGenRef.current) return
      // #813: a cut send's optimistic bubble is replaced by its stored copy here —
      // never for a session the view has already switched away from.
      const storedTemps = storeKeyRef.current === sessionId ? takeStoredTemps(sessionId, storedMarkAtStart) : undefined
      setState(prev => ({
        ...prev,
        messages: mergeTranscript(
          filtered,
          storedTemps?.size ? prev.messages.filter(m => !storedTemps.has(m.id)) : prev.messages,
          idsAtRequestStart,
          unansweredSteerBubbles,
        ),
        lastPersonaId: lastPersona,
      }))
    } catch {
      // Ignore
    }
  }, [sessionId, wsQuery])

  // Load diff summary
  const loadSummary = useCallback(async () => {
    if (!sessionId) return
    try {
      const data = await request<SummaryInfo>(
        `/api/agent/sessions/${sessionId}/diff${wsQuery}`,
      )
      setState(prev => ({ ...prev, summary: data }))
    } catch {
      // Ignore
    }
  }, [sessionId, wsQuery])

  // Load revert state for the session
  const loadRevertState = useCallback(async () => {
    if (!sessionId) return
    try {
      const data = await request<RevertState>(
        `/api/agent/sessions/${sessionId}/revert${wsQuery}`,
      )
      setState(prev => ({ ...prev, revertState: data }))
    } catch {
      // Session may not exist or revert not supported
      setState(prev => ({ ...prev, revertState: { reverted: false } }))
    }
  }, [sessionId, wsQuery])

  // Load session cost and context usage from API.
  // Generation-safe: each call bumps `sessionCostGenRef` and the response
  // handler aborts the setState if a NEWER call has started since. Without
  // this guard, multiple in-flight /usage requests (one per step-finish
  // event, one per `complete`, one per `cost-update`) can land out of
  // order — an early-fired call returning AFTER a later one would
  // overwrite the correct value with a stale parent-only number.
  const loadSessionCost = useCallback(async () => {
    // Before the early return — same reason as `loadMessages`: switching to a
    // draft must invalidate an in-flight response, or the new chat shows the
    // previous session's cost.
    const myGen = ++sessionCostGenRef.current
    if (!sessionId) return
    try {
      const data = await request<AgentUsageResponse>(
        `/api/agent/usage${wsQuery}${wsQuery ? '&' : '?'}sessionId=${sessionId}`,
      )
      if (myGen !== sessionCostGenRef.current) return
      setState(prev => ({
        ...prev,
        sessionCost: data.sessionCost,
        // Carried beside the USD figure, never re-derived from it: the server
        // converted PER ROW at the rate in force when each was incurred, and
        // multiplying the total by one current rate is the restatement bug
        // 0172 exists to prevent.
        sessionCostDisplayMinor: data.sessionCostDisplayMinor ?? null,
        estimatedTokens: data.estimatedTokens,
        contextLimit: data.contextLimit,
      }))
    } catch {
      // Ignore
    }
  }, [sessionId, wsQuery])

  // Load session todos
  const loadTodos = useCallback(async () => {
    if (!sessionId) return
    try {
      const data = await request<TodoItem[]>(
        `/api/agent/sessions/${sessionId}/todo${wsQuery}`,
      )
      setState(prev => ({ ...prev, todos: data }))
    } catch {
      // Ignore
    }
  }, [sessionId, wsQuery])

  // #648: once a turn has ended, keep only the cards the server still holds open (stale-cards.ts).
  const reconcileStaleCards = useCallback(async () => {
    if (!sessionId || !workspaceId) return
    const sid = sessionId
    const lifecycle = steerLifecycleRef.current.signal
    // code review rounds 5–7: only while THIS view still shows that session, mounted, with cards on it — no polling (and
    // no presence refresh) for a chat the person left (a card left stale there: see the mount effect's note).
    const stillMine = () => {
      if (lifecycle.aborted || sessionIdRef.current !== sid) return false
      const st = sessionStore.getState(storeKeyRef.current)
      return st.pendingPermissions.length + st.pendingQuestions.length > 0
    }
    if (!stillMine()) return
    // The open set comes only once the finished job has left the queue (/pending's durable branch); at `complete` the
    // job is usually still finishing, so look again a few times (code review round 4) — while this tab stays idle.
    for (let attempt = 0; attempt < STALE_CARD_ATTEMPTS; attempt++) {
      // (dropStaleCards judges only an IDLE tab — a new turn's cards are its own)
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, STALE_CARD_RETRY_MS))
        if (!stillMine()) return
      }
      try {
        const res = await request<{ openRequestIds?: string[] }>(`/api/agent/sessions/${sid}/pending${wsQuery}`)
        const open = res?.openRequestIds
        if (!Array.isArray(open)) continue // the job is still finishing (or an older API) — nothing to judge by yet
        if (!stillMine()) return
        setState(prev => dropStaleCards(prev, open))
        return
      } catch {
        return // the card stays, as before
      }
    }
  }, [sessionId, workspaceId, wsQuery])

  // #874: targeted refetches — one saved message, patching only named parts.
  const fetchSavedMessage = useCallback(async (messageId: string) => {
    if (!sessionId) return null
    try {
      return await request<{ info: { id: string }; parts: Array<{ id: string; messageID: string } & Record<string, unknown>> }>(
        `/api/agent/sessions/${sessionId}/message/${encodeURIComponent(messageId)}${wsQuery}`,
      )
    } catch { return null }
  }, [sessionId, wsQuery])
  const refetchToolParts = useCallback(async (calls: Map<string, Set<string>>) => {
    const sid = sessionId
    if (!sid) return
    for (const [messageId, callIds] of calls) {
      const msg = await fetchSavedMessage(messageId)
      if (!msg) continue
      const mapped = (msg.parts ?? []).flatMap(p => mapApiPartToUi(p, messageId))
      sessionStore.update(sid, prev => {
        const messages = patchToolCallParts(prev.messages, messageId, mapped, callIds)
        return messages === prev.messages ? prev : { ...prev, messages }
      })
    }
  }, [sessionId, fetchSavedMessage])
  const refetchPart = useCallback(async (messageId: string, partId: string) => {
    const sid = sessionId
    if (!sid) return
    const raw = (await fetchSavedMessage(messageId))?.parts?.find(p => p.id === partId)
    if (!raw) return
    sessionStore.update(sid, prev => {
      const messages = applyPartUpdateEvent(prev.messages, raw)
      return messages === prev.messages ? prev : { ...prev, messages }
    })
  }, [sessionId, fetchSavedMessage])

  // Keep reload ref current so event handlers can call without stale closures
  const refetchMessageText = useCallback(async (messageIds: Set<string>) => {
    const sid = sessionId
    if (!sid) return
    for (const messageId of messageIds) {
      const msg = await fetchSavedMessage(messageId)
      if (!msg) continue
      const text = (msg.parts ?? []).filter(p => p.type === 'text').map(p => String(p.text ?? '')).join('').trim()
      sessionStore.update(sid, prev => patchMessageText(prev, messageId, text))
    }
  }, [sessionId, fetchSavedMessage])

  reloadRef.current = { messages: loadMessages, cost: loadSessionCost, todos: loadTodos, toolParts: refetchToolParts, part: refetchPart, messageText: refetchMessageText, staleCards: reconcileStaleCards }

  // Load on mount
  useEffect(() => {
    loadSession()
    loadMessages()
    loadSummary()
    loadSessionCost()
    loadRevertState()
    loadTodos()
    // #648 code review round 8: NOT the stale-card reconcile here — on mount a running turn's tab can still read idle,
    // and an open set without that turn's live request would drop its card. A card left stale off screen stays until
    // the session's next turn ends; answering it gets 409 and the card goes (as before #648); a reload restores only
    // the open ones.
  }, [loadSession, loadMessages, loadSummary, loadSessionCost, loadRevertState, loadTodos])

  // Send a prompt — returns true on success, false on failure (e.g. session busy)
  //
  // credentialEnv used to be attached to the body here, populated by a
  // browser-side getter. It's now resolved by the API at job-submit
  // time from the session_credential rows (see
  // resolveSessionCredentialsForRequest), which makes share-revoke
  // real-time and keeps plaintext out of every request body.
  // Redaction of credential values typed into the prompt happens
  // server-side in the same step, since the server is the only place
  // that knows the resolved values.
  const sendMessage = useCallback(async (prompt: string, attachments?: Attachment[], model?: string, sessionIdOverride?: string, personaId?: string, activeTab?: { fileId: string; fileName: string; filePath: string; fileType: string; unsaved?: boolean }, selection?: { text: string; fileName: string; filePath: string; fileId: string; startLine: number; endLine: number; kind?: string; cellRange?: string; sheetName?: string; slideNumber?: number; pageNumber?: number; elementTag?: string; elementSelector?: string; elementPath?: string }, clientId?: string, systemContext?: string, variant?: string): Promise<boolean> => {
    const targetId = sendTargetId(sessionIdOverride, sessionId)
    if (!targetId) return false

    // Bind every write from this run to its OWN session's store entry, not the
    // session the surface currently shows. This is what makes the draft→real
    // send (override != displayed key), the tab-switch, and the chat-home →
    // workspace handoff all land in the right conversation instead of bleeding
    // into whatever is on screen. `handleAgentEvent(se, applyTarget, targetId)`
    // routes the reducer here too — the THIRD argument matters as much as the
    // second (#467): `applyTarget` routes the STATE, `targetId` routes the
    // side-effects. Without it every reload in the reducer falls back to the
    // displayed session, which is the bug that fix was for, still live on this
    // path because only the attach path was updated.
    const applyTarget = (updater: (prev: AgentSessionState) => AgentSessionState) => sessionStore.update(targetId, updater)

    // Cleared with `error`, never separately. The flag describes THE ERROR
    // CURRENTLY SET, so leaving it behind here was how it went stale: the user
    // waits out a SESSION_COMPACTING advisory and presses Enter, which clears
    // the banner and leaves `errorAdvisory: true` in the session store for the
    // life of the tab — disarming force-unlock for every real error after it.
    applyTarget(prev => ({ ...prev, status: 'submitting', error: null, errorAction: null, errorAdvisory: false, expiredQuestionNotice: null, reconnecting: false }))

    // Owner token for this send's live-run claim (released only by us in finally, so a
    // concurrent attach's finally can't clear it — #269 re-verify, finding 6).
    const sendLiveToken = {}
    // #813: set once the server has ACCEPTED this run (a 200 with its stream). A
    // failure after that is a lost connection, not a failed turn — see the catch.
    let runAccepted = false
    // #813: true only while OUR code handles a received chunk. After acceptance, an
    // exception there is a client bug and keeps the normal error path; ANY other
    // failure is a lost connection. Classified by exclusion because a cut does not
    // always surface at the read (Firefox showed "Error in input stream" through a
    // path Chrome never takes) — no list of cut sites to keep complete.
    let handlingFrame = false
    // #813: a stream that ends cleanly without RUN_FINISHED / RUN_ERROR was cut in
    // between (see attachSession) — re-attach rather than treat it as the end.
    let sawTerminal = false
    let runWorkspaceId = workspaceId
    let sendTempId: string | undefined
    // #1316: the session this send was made in — read when the POST starts; the catch checks it first.
    let sentSession: AbortSignal | undefined
    // #1316 (P10): the signed-in account when the send started — an ACCOUNT end hands the composer to someone else.
    const sentAccount = accountEpoch()
    try {
      const body: Record<string, unknown> = { prompt, workspaceId }
      if (model) body.model = model
      if (variant) body.variant = variant
      if (personaId) body.personaId = personaId
      if (activeTab) body.activeTab = activeTab
      if (clientId) body.clientId = clientId
      // The user's own time zone (IANA), so the agent reads "9 am" as nine on THEIR clock
      // — e.g. a schedule's `tz`. Validated server-side; absent when the browser can't say.
      const timeZone = browserTimeZone()
      if (timeZone) body.timeZone = timeZone
      if (targetId) recordSendTarget(targetId, workspaceId, activeTab)
      if (targetId) lastSendProps.set(targetId, {
        ...(model ? { model } : {}), ...(variant ? { variant } : {}),
        ...(personaId ? { personaId } : {}),
        ...(timeZone ? { timeZone } : {}),
        // NOT activeTab: after a long suspension it is stale — the resumed turn
        // would be told the user is looking at a tab they have long left.
      })
      if (selection) body.selection = { text: selection.text, fileName: selection.fileName, filePath: selection.filePath, fileId: selection.fileId, startLine: selection.startLine, endLine: selection.endLine, kind: selection.kind, cellRange: selection.cellRange, sheetName: selection.sheetName, slideNumber: selection.slideNumber, pageNumber: selection.pageNumber, elementTag: selection.elementTag, elementSelector: selection.elementSelector, elementPath: selection.elementPath }
      if (attachments?.length) {
        body.attachments = attachments.map(a => ({
          mime: a.mime,
          url: a.url || '',
          filename: a.filename,
          path: a.path,
          source: a.source,
          // #639 Task 3.8: carry the session-private handle through to the
          // wire — without it a binary routed to the session store arrives
          // handle-less and the server has no bytes to resolve for the
          // model (the composer never had inline bytes to send in `url`).
          handle: a.handle,
          // #662 Task 8: carry a moved-to-drive attachment's fileId through
          // to the wire — without it the persisted part has `path` but no
          // `fileId`, and a moved composer attachment sends as a plain
          // path-only reference instead of a real drive-file part.
          fileId: a.fileId,
          // #662: carry the stashed inline bytes so a composer-moved
          // attachment can still revert to its inline preview if the drive
          // file is later deleted (the sent-chip move path stashes this
          // server-side in repointAttachmentPartsToDrive instead).
          previewUrl: a.previewUrl,
        }))
      }
      // Attach system context — AI sees it, user doesn't
      if (systemContext) body.systemContext = systemContext

      // Add user message optimistically BEFORE the API call —
      // the worker may start streaming before the POST returns,
      // so the assistant message must come after the user message.
      const tempId = `temp_${Date.now()}`
      sendTempId = tempId
      const parts: MessagePart[] = [{ id: `part_temp`, messageId: '', type: 'text' as const, content: prompt }]
      if (attachments?.length) {
        for (const att of attachments) {
          parts.push({
            id: `part_file_${att.id}`,
            messageId: '',
            type: 'file' as const,
            mime: att.mime,
            url: att.url || '',
            filename: att.filename,
            path: att.path,
            source: att.source,
            handle: att.handle,
            fileId: att.fileId,
            previewUrl: att.previewUrl,
          })
        }
      }

      applyTarget(prev => ({
        ...prev,
        status: 'processing',
        // B(a) fix (Fable #2): reset the streaming accumulators when a NEW run
        // starts. A previous run that died mid-text-block (error, or its stream
        // aborted by a superseding claim) leaves `streamingText` populated with
        // no TEXT_MESSAGE_END to clear it — and longer-wins would then keep that
        // stale text over the new run's shorter early snapshots (or commit it as
        // the new message's content). Clearing here makes the new run start clean.
        streamingText: '',
        streamingReasoning: '',
        streamingReasoningStart: null,
        messages: [...prev.messages, {
          id: tempId,
          role: 'user',
          parts,
          timeCreated: Date.now(),
        }],
      }))

      {
        // AG-UI ingress (A3: sole transport, no flag). POST RunAgentInput; the
        // response body IS this run's
        // SSE. Feed each AG-UI event through the inverse translator into the
        // existing reducer (handleAgentEvent) — unchanged. On end, reconcile
        // via the existing REST source-of-truth (canonical messages replace
        // the optimistic temp; cost/todos refresh) — the documented seam.
        // B(b): the send loop's SSE fetch previously had NO AbortController — once
        // opened, nothing client-side (unmount, session switch, cancel, forced
        // idle) could ever close it. Give it one and hand it to claimLiveRun so a
        // superseding run (resend after a zombie) aborts THIS stream at the source
        // (aborting the fetch aborts the SSE body → the reader loop below exits).
        const sendCtrl = new AbortController()
        // R2.1: mint the run's id BEFORE the POST and record it, so cancel() can name
        // THIS exact run — even a cancel that races ahead of the POST reaching the
        // server. Stable across the 409 retry loop below (one identity per run, so a
        // retried POST and any later /abort agree on the id).
        const runId = `cp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        sessionStore.setLiveRunId(targetId, runId)
        sessionStore.claimLiveRun(targetId, sendLiveToken, sendCtrl, { tempId: sendTempId }) // suppress the re-attach effect's duplicate subscription
        const ses = createAguiToStream()
        sentSession = currentSessionSignal()   // #1316: a refusal answered after this session ended tells no one
        // #1316: once its session ended the send stops — no retry goes out as the NEXT session (a gateway or busy retry
        // rebuilds its headers per attempt), and a refusal answered for the old session is never shown as one
        // (`sessionBoundAttempt`). Its stream ends with the session too (`sessionStreamSignal`). The person is told (P8)
        // by the catch below. Worded "may not have been sent": an earlier attempt can have been admitted already (#813's
        // lost answer), so it never claims the turn did not run, and it asks nothing of the person (after an account
        // change the chat may belong to the identity that left).
        const doFetch = sessionBoundAttempt(() => sessionFetch(`${getApiBase()}/api/agent/agui`, {
          method: 'POST',
          credentials: 'include',
          signal: sessionStreamSignal(sendCtrl.signal),
          headers: aguiHeaders({ accept: 'text/event-stream' }),
          body: JSON.stringify({
            threadId: targetId,
            runId,
            state: {},
            messages: [{ id: `u${Date.now()}`, role: 'user', content: prompt }],
            tools: [],
            context: [],
            // Forward the full request body (same shape the deleted bespoke
            // /message route consumed): workspaceId, model, personaId,
            // attachments, activeTab, selection, clientId, systemContext.
            // The server ignores body.prompt (it reads messages[]) and
            // re-resolves credentials + redaction server-side.
            // #874: no MESSAGES_SNAPSHOT — the send's confirmation rides
            // RUN_STARTED.metadata.papan instead (handled below).
            forwardedProps: { ...body, papanOmitSnapshot: true },
          }),
        }), sentSession, sessionEndedError)
        // Fable #4: the C fix returns 409 SESSION_BUSY while a JUST-cancelled run
        // is still DRAINING (the abort fires seconds before the job leaves the
        // active set). The most common flow — cancel then immediately edit-resend
        // — would then error. Retry the POST a few times as the drain frees the
        // session; fall through to normal error handling if it's genuinely still
        // busy after that. (A superseding claim aborts sendCtrl → the fetch throws
        // AbortError → caught + exited quietly, so this loop can't outlive the run.)
        // #813: a send that hits a restarting API instance (502/503/504, network
        // error) is retried with the SAME runId.
        const sent = await fetchWithGatewayRetry(doFetch)
        let ares = sent.res
        runWorkspaceId = typeof body.workspaceId === 'string' ? body.workspaceId : workspaceId
        for (let attempt = 0; ares.status === 409 && attempt < 3; attempt++) {
          let code: string | undefined
          let holder: string | undefined
          try { ({ code, runId: holder } = (await ares.clone().json()) as { code?: string; runId?: string }) } catch { /* non-JSON body */ }
          if (code !== 'SESSION_BUSY') break
          if (holder === runId) break // #813: it is OUR run (a retried send) — handled below
          await new Promise(r => setTimeout(r, 800))
          ares = await doFetch()
        }
        if (!ares.ok) {
          // The action rides ON the Error so the single catch below can lift it
          // into state without a second channel — see `gateActionOf`.
          // (`aguiErrorMessage` is this minus the action, so this supersedes it.)
          // `SESSION_COMPACTING` means a summarize this user just asked for is
          // still running — a wait, not a fault. Tagged so the banner's Dismiss
          // does not force-unlock and cancel it.
          let refusalCode: string | undefined
          let refusalRunId: string | undefined
          let refusalBody: Record<string, unknown> | undefined
          try {
            refusalBody = (await ares.clone().json()) as Record<string, unknown>
            refusalCode = refusalBody.code as string | undefined
            refusalRunId = refusalBody.runId as string | undefined
          } catch { /* non-JSON */ }

          // SCOPED REVIEW OF ROUND 1, FINDING 4 — publish the refusal so the
          // shell's chat-storage dialog can open.
          //
          // This transport streams, so it calls `fetch` directly rather than
          // `request()` — which is the only thing that reports failures. The
          // result was that the storage-full dialog, built for exactly this
          // moment, never appeared on the PRIMARY send path: a member at
          // their limit attached a file and got a generic stream error.
          // Reported here rather than moving AG-UI onto `request()`, which
          // cannot carry an SSE body.
          if (ares.status === 413) {
            reportApiFailure({ status: 413, code: refusalCode, path: '/api/agent/agui', body: refusalBody })
          }
          // #813: the server names the run holding the session. When that is OUR runId,
          // our earlier attempt was admitted (its response was lost at a restarting
          // instance): re-attach to it instead of reporting an error. Anyone else's run
          // keeps the normal "a run is still active" error.
          if (refusalCode === 'SESSION_BUSY' && refusalRunId === runId) {
            runAccepted = true
            throw new RunConnectionLost()
          }
          const gate = await aguiErrorResult(ares)
          throw Object.assign(new Error(gate.message), {
            gateAction: gate.action,
            advisory: refusalCode === 'SESSION_COMPACTING',
          })
        }
        if (!ares.body) throw new Error("The assistant couldn't start (no response stream).")
        runAccepted = true
        const rd = ares.body.getReader()
        const dec = new TextDecoder()
        let abuf = ''
        for (;;) {
          const { done, value } = await rd.read()
          // A superseding claim aborts sendCtrl, but a read that had already
          // resolved still hands us its chunk: processing it would noteFrame the
          // NEW claim (keyed by session), marking a send that never streamed as
          // admitted — and the orphan poll would then drop its unsaved prompt.
          // Same check as the attach loop's; the frame loop below has no awaits.
          if (done || sendCtrl.signal.aborted) break
          handlingFrame = true
          abuf += dec.decode(value, { stream: true })
          const frames = abuf.split('\n\n')
          abuf = frames.pop() ?? ''
          for (const fr of frames) {
            if (fr.replace(/^\s+/, '').startsWith(':')) continue
            const dl = fr.split('\n').find(l => l.startsWith('data:'))
            if (!dl) continue
            let aev: { type: string; [k: string]: unknown }
            try { aev = JSON.parse(dl.slice(5).trim()) } catch { continue }
            sessionStore.noteFrame(targetId) // still delivering — the orphan poll leaves this stream alone
            if (aev.type === 'RUN_FINISHED' || aev.type === 'RUN_ERROR') sawTerminal = true
            // The server's confirmation of THIS send's user message — the AG-UI
            // equivalent of bespoke's `res.messageID` (sent-message-confirmation.ts):
            // RUN_STARTED.metadata.papan when the snapshot is omitted (#874), else
            // MESSAGES_SNAPSHOT's last user message. Reconcile the optimistic temp
            // user message → its real id (drop temp if the real one is already
            // present). Without this the temp message is never reconciled and
            // coexists with canonical messages → the trailing duplicate user message.
            // D52: credentials the server dropped for this turn → the session's credentials hook tells the user
            const credentialDrops = credentialDropsOf(aev)
            if (credentialDrops !== null && typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent<CredentialDropsEventDetail>(CREDENTIAL_DROPS_EVENT, { detail: { sessionId: targetId, drops: credentialDrops } }))
            }
            const confirmed = sentMessageConfirmation(aev)
            if (confirmed) {
              const realId = confirmed.id
              // The confirmed content is the SERVER-CANONICAL text — which is
              // REDACTED when a session credential is bound. We must overwrite
              // the optimistic bubble with it, not just swap the id: otherwise
              // the raw secret the user typed stays visible in their own message
              // until a refresh (the server already persisted/streamed the
              // redacted form). Mirrors the deleted bespoke path's `res.prompt`.
              const canonicalText = confirmed.content
              applyTarget(prev => {
                const realPresent = prev.messages.some(m => m.id === realId)
                return {
                  ...prev,
                  // #813: only THIS send's bubble — an older one (a cut send still
                  // awaiting its canonical load) must not be renamed to this id.
                  messages: realPresent
                    ? prev.messages.filter(m => m.id !== tempId)
                    : prev.messages.map(m => m.id === tempId
                        ? {
                            ...m,
                            id: realId,
                            parts: canonicalText !== undefined
                              ? m.parts.map(p => p.type === 'text' ? { ...p, content: canonicalText } : p)
                              : m.parts,
                          }
                        : m),
                }
              })
            }
            if (confirmed?.contentOmitted) {
              // #874: the prompt was too large for the frame, so the bubble still
              // holds what the user typed — with a bound credential, the raw
              // secret. Fetch this ONE message's persisted (redacted) text now:
              // a patch of one bubble, safe mid-turn, unlike a transcript reload.
              const realId = confirmed.id
              const url = `/api/agent/sessions/${targetId}/message/${encodeURIComponent(realId)}?workspaceId=${encodeURIComponent(String(body.workspaceId ?? ''))}`
              // Retried twice; then, if the turn has ended and it is on screen, a
              // reload — never a turn-end reload (it races the worker's save).
              const fetchRedacted = (attempt: number): void => {
                void request<{ parts?: Array<{ type?: string; text?: string }> }>(url).then(
                  (msg) => applyTarget(prev => patchMessageText(prev, realId, (msg.parts ?? []).filter(p => p.type === 'text').map(p => p.text ?? '').join('').trim())),
                  () => {
                    if (attempt < 2) { setTimeout(() => fetchRedacted(attempt + 1), 1000 * (attempt + 1) * 2); return }
                    // Out of retries. Fetch this ONE message again — it was saved
                    // before the job was queued, so that never races the worker's
                    // end-of-turn save, unlike a transcript reload: now when the turn
                    // has ended and it is on screen, else at the turn's end.
                    if (canReloadNow(sessionStore.getState(targetId).status, targetId === sessionIdRef.current)) reloadRef.current.messageText(new Set([realId]))
                    else noteTextRefetch(targetId, realId)
                  },
                )
              }
              fetchRedacted(0)
            }
            if (aev.type === 'MESSAGES_SNAPSHOT') continue // not a reducer event
            for (const se of ses.push(aev)) {
              // Route the reducer to the run's OWN session entry (applyTarget),
              // so switching tabs / navigating away can't splice this stream
              // into whatever session is now on screen — the store keying makes
              // the old runtime foreign-session guard unnecessary.
              handleAgentEvent(se, applyTarget, targetId)
              // Client tools execute against the VISIBLE browser surface (they read
              // sessionIdRef.current), so only fire them while THIS run is still the
              // one on screen. For a backgrounded run (user switched tabs) firing here
              // would (a) run the tool against the wrong session's surface, (b) POST the
              // result to the wrong threadId so this run's worker never unblocks, and
              // (c) take the page's once-only run slot for the wait (per WAIT; a bare call
              // id and its waits refuse each other) — defeating the /pending recovery that
              // reopening the session relies on. Let /pending recover it instead. (#254 review.)
              if (sessionIdRef.current === targetId) runClientTool(se)
            }
          }
          handlingFrame = false
        }
        // #840 final review B-M3: the send path keeps NO reconnect flag — a
        // `papan.reconnect` on this stream (the API draining) ends it without a terminal
        // frame and lands here as a plain cut. That is fine only because recovery's FIRST
        // re-attach is immediate (recoverRun sleeps only after a failure), so the send
        // gets the same instant hand-over the attach path's 'reconnect' outcome gives.
        // Adding a backoff before the first attempt would break that.
        if (!sawTerminal && !sendCtrl.signal.aborted) throw new RunConnectionLost()
        // Do NOT reloadRef.messages() here (a FINISHED stream — the orphan poll's
        // reload after it ABORTS a stream is the other case). The streamed events already built
        // the conversation via the SAME reducer the bespoke path uses, and
        // bespoke does no per-turn message reload. Reloading canonical
        // messages on top of streamed state double-rendered the user message
        // + assistant/tool parts ("double send / double tool run"). cost/todos
        // are scalar/array domain refreshes (not in AG-UI's vocab — the
        // documented seam) and do not duplicate, so they stay.
        // Known follow-up: the optimistic user message keeps a temp_ id (the
        // bespoke /message response gives the real id for reconciliation;
        // /agui's response is the SSE). Only affects revert/edit/fork on the
        // just-sent message before a refresh — not this bug.
        // cost/todos are refreshed via the display-bound loaders, so only run
        // them when the target is still the session on screen; otherwise the
        // next open of that session reloads them (loadSessionCost/loadTodos).
        if (sessionIdRef.current === targetId) {
          reloadRef.current.cost()
          reloadRef.current.todos()
        }
        return true
      }

      // A3: the bespoke /sessions/:id/message POST + its temp→real-id
      // reconcile were removed. The AG-UI confirmation (RUN_STARTED.metadata.papan, or MESSAGES_SNAPSHOT from an older server — `sentMessageConfirmation`) reconciles the
      // optimistic temp user message to its canonical id (handled in the
      // block above). Known follow-up: until a refresh the just-sent user
      // message keeps a temp_ id (only affects revert/edit/fork on it
      // pre-refresh — documented seam, not this path's concern).
      return true
    } catch (err) {
      // B(b): a superseding run aborted THIS stream (claimLiveRun on a resend
      // aborted our controller) — the AbortError is intentional teardown, not a
      // failure. Exit quietly so the NEW run owns the session state (don't wipe
      // its optimistic message or stamp an error). The owner-token releaseLiveRun
      // in `finally` already no-ops for a superseded token, so the new claim
      // stands.
      // #648: a stream superseded AFTER its run was accepted was a SENT message — the resume run of a turn suspended on a
      // prompt claims the session and aborts the original stream when the person answers. Returning false here made
      // the composer put the sent text back as a "failed send". Never accepted ⇒ still false.
      // #1316: the session this send was made in ended — its stream cut by `sessionStreamSignal` (an AbortError) or its
      // answer dropped before headers (a `SessionEndedError`, which no retry recognises). Checked FIRST: never a
      // supersede, never a lost connection to recover (a re-attach would go out as the next session) — the P8 path
      // below, with the "may not have been sent" message.
      const sessionGone = !!sentSession?.aborted
      if (sessionGone && accountEpoch() !== sentAccount) {
        // Another person may now be at this composer (another account signed in, the tab staying on the same tenant):
        // their composer must not get the previous person's prompt back. Clear the in-flight state and the optimistic
        // bubble and answer "don't restore" (true — every caller restores only on false). No message: there is no one
        // to tell. A switch of identity by the SAME person keeps the restore and the message below.
        applyTarget(prev => ({
          ...prev,
          status: 'idle',
          streamingText: '',
          streamingReasoning: '',
          streamingReasoningStart: null,
          messages: sendTempId ? prev.messages.filter(m => m.id !== sendTempId) : prev.messages,
        }))
        return true
      }
      if (sessionGone) err = sessionEndedError()
      else if (err instanceof DOMException && err.name === 'AbortError') return runAccepted
      // #813: the server accepted this run and then the connection was lost (the
      // API instance serving the stream restarted — "Error in input stream"). The
      // worker is still running the turn: keep the chat working and re-attach once
      // this send has released its claim (the `finally` below). RunConnectionLost
      // (our own earlier attempt was admitted) lands here too.
      if (!sessionGone && runAccepted && !handlingFrame) {
        const lost = { tempId: sendTempId }
        queueMicrotask(() => { void recoverLostRun(targetId, runWorkspaceId, lost) })
        return true
      }
      const message = err instanceof Error ? err.message : 'Request failed'
      // Surface the failure on the run's OWN session (applyTarget) — whether or
      // not it's the one on screen. Remove the optimistic user message.
      applyTarget(prev => ({
        ...prev,
        status: 'idle',
        error: message,
        errorAction: gateActionOf(err),
        errorAdvisory: (err as { advisory?: boolean })?.advisory === true,
        // Fable #2: drop any partial streamed text so it can't leak into the next run.
        streamingText: '',
        streamingReasoning: '',
        streamingReasoningStart: null,
        // #813: only THIS send's bubble — an earlier cut send's may still be shown.
        messages: sendTempId ? prev.messages.filter(m => m.id !== sendTempId) : prev.messages,
      }))
      return false
    } finally {
      sessionStore.releaseLiveRun(targetId, sendLiveToken)
    }
  }, [sessionId, workspaceId, handleAgentEvent, runClientTool, recoverLostRun])

  // Cancel generation
  const cancel = useCallback(async () => {
    if (!sessionId) return
    stoppedRecoveries.current.add(sessionId) // #813: a cancel ends any lost-connection recovery
    // #762: …and its "reconnecting" line, at once (the quiet check notices the stop after its current wait)
    sessionStore.update(sessionId, (prev) => prev.reconnecting ? { ...prev, reconnecting: false } : prev)
    reconcileEpisodes.current.delete(sessionId)
    // ── An ADVISORY has nothing of yours to cancel ─────────────────────────
    //
    // Putting this guard only in `dismissError` was wrong, and the comment
    // there claiming it is "the ONE place both controls land" was false: both
    // banner buttons are `onClick={() => { cancel(); dismissError() }}`, and on
    // HeroChat that is the ONLY button — labelled "Dismiss". So the guard was
    // entirely inert on that surface.
    //
    // `cancel` POSTs /abort with no runId (the failed send already released the
    // live-run token), which makes `cancelSession` match SESSION-WIDE and
    // `job.remove()` a still-queued compaction. Clicking "Dismiss" on "this
    // session is being summarized" therefore cancelled the summarize the user
    // asked for, leaving the transcript unchanged with nothing said about it —
    // exactly what the advisory flag exists to prevent.
    //
    // Refusing is also the honest semantics: an advisory means no run of THIS
    // user's is in flight. There is nothing for Abort to abort.
    //
    // GATED ON IDLE, not on the flag alone. The flag records a PAST refusal and
    // nothing on the compaction-completion path clears it — so once a real turn
    // started (this tab attaches to another tab's run and renders Stop), the
    // bare flag disarmed Stop for a run that genuinely was live, silently, with
    // the 3s force-idle fallback skipped too. The only escape was noticing an
    // unrelated banner and dismissing it first.
    //
    // `status` is the live fact and needs no second clear site — which is how
    // the flag went stale in the first place.
    if (state.errorAdvisory && !isTurnLive(state.status)) return
    // R2.1: name the run being cancelled so the abort is recorded against its own
    // durable key (honored at pickup even if the /agui POST is still in flight). The
    // runId is what this client minted for the live send; undefined for a run started
    // elsewhere (an attach), where the server derives it from the active job.
    const runId = sessionStore.getLiveRunId(sessionId)
    const runQuery = runId ? `&runId=${encodeURIComponent(runId)}` : ''
    await request(`/api/agent/sessions/${sessionId}/abort${wsQuery}${runQuery}`, {
      method: 'POST',
    })
    // A: CONFIRM the stop against the authoritative busy set instead of forcing
    // idle blindly. The old unconditional `forceIdleIfStuck` flipped the UI to
    // 'idle' after 3s even when the run was STILL going (the lost-abort zombie) —
    // so the user saw "stopped", edited + resent, and a SECOND concurrent run
    // started on the same session. Now, on the fallback tick: not in the busy set
    // → the run really ended → idle; still busy → do NOT force (forcing idle is
    // exactly what resurrects the double-run hazard) — re-arm once to give the
    // durable abort a beat, then leave the run visible. The worker's
    // {type:'cancelled'} frame (or its 15s periodic durable-abort check) clears
    // the spinner honestly. Same busy-set idiom as the orphan-recovery poll.
    // Fable #3: force-idle the CANCELLED session by key, NOT `setState` — which
    // routes to whatever session is on screen (`storeKeyRef.current`). Cancelling
    // a backgrounded session and switching tabs within the (now up to 9s) confirm
    // window would otherwise flip the DISPLAYED session to idle.
    const cancelledId = sessionId
    // Fable r2 #B(b)3: when we DO force idle, also abort the live stream — otherwise
    // a still-open (zombie) SSE keeps applying deltas to a session now labeled idle.
    const idle = (reason: string) => {
      sessionStore.abortLiveRun(cancelledId)
      sessionStore.update(cancelledId, prev => forceIdleIfStuck(prev, reason))
    }
    const confirmStop = async (attempt: number): Promise<void> => {
      let busy: Array<{ id: string }>
      try {
        busy = await request<Array<{ id: string }>>('/api/agent/sessions/status')
      } catch {
        // status unavailable — fall back to the old force-idle rather than wedge
        idle('Abort timeout (status unavailable)')
        return
      }
      if (isSessionOrphaned(cancelledId, busy)) {
        idle('Abort confirmed — not in busy set')
      } else if (attempt < 2) {
        setTimeout(() => { void confirmStop(attempt + 1) }, 3000)
      } else {
        console.warn(`[Agent] cancel ${cancelledId}: still busy after abort — leaving the run visible (not forcing idle)`)
      }
    }
    setTimeout(() => { void confirmStop(1) }, 3000)
  }, [sessionId, wsQuery, state.errorAdvisory, state.status])

  // Steer an active run (@sync OpenCode SessionInput delivery=steer): the text
  // is delivered to the running worker job and injected at its next step
  // boundary. Adds the user message optimistically for the live view. On
  // `ok: false` the text was NOT delivered and the caller decides what to say —
  // see `describeSteerRefusal`.
  /**
   * #648 (owner, 2026-10-22): a message sent while a prompt is open SKIPS that prompt. The server suspends the turn's
   * parked waits and waits for the turn to end. 'ended' = it ended (the caller sends the message as an ordinary prompt,
   * which supersedes the skipped requests, and this tab drops their cards); 'pending' = the skip landed but the turn is
   * still ending after every attempt (the caller puts the text back); 'failed' = nothing was parked or the call failed
   * (the caller steers, as before #648).
   */
  const skipOpenPrompts = useCallback(async (): Promise<'ended' | 'pending' | 'failed'> => {
    if (!sessionId || !workspaceId) return 'failed'
    // THIS session's entry, captured now (as steerMessage does): the skip can wait up to a minute, and the cards it
    // clears are the skipped session's — never those of whichever session is on screen when it answers
    const key = storeKeyRef.current
    // Review round 4: once a skip has landed the turn is ENDING (latched) — a steer would never be answered. If it is
    // still finishing a parallel step (a subagent, a long command), ask again until it has ended; each call waits ~15 s.
    // #1316 review round 1 (B-2): the attempts belong to the session they started in — never asked again as the next
    // one (sendMidRun then hands the text back)
    const start = currentSessionSignal()
    for (let attempt = 0; attempt < SKIP_ATTEMPTS; attempt++) {
      if (sessionEndedSince(start)) return 'failed'
      let res: { ended?: boolean; ending?: boolean } | null
      try {
        res = await request<{ ended?: boolean; ending?: boolean }>(
          `/api/agent/sessions/${sessionId}/skip-prompts`,
          { method: 'POST', body: JSON.stringify({ workspaceId }) },
        )
      } catch {
        return attempt === 0 ? 'failed' : 'pending' // never landed (steer as before) / landed, then lost
      }
      if (res?.ended === true) {
        sessionStore.update(key, prev => ({ ...prev, pendingPermissions: [], pendingQuestions: [] }))
        return 'ended'
      }
      // nothing was parked (a stale card — the turn running is not one this skip ended): steer at once, as before
      if (res?.ending !== true) return 'failed'
    }
    return 'pending'
  }, [sessionId, workspaceId])

  const steerMessage = useCallback(async (text: string): Promise<SteerResult> => {
    const t = text.trim()
    if (!sessionId || !t) return { ok: false, reason: 'error' }
    const id = newSteerClientId()
    // Place the steer just BEFORE the in-progress assistant turn rather than
    // trailing it — the assistant keeps writing its (single) bubble above, so a
    // trailing user bubble reads backwards ("assistant replied, then user
    // steered"). Splicing before the last assistant message reads correctly:
    // user → steer → assistant. The worker persists it with a matching
    // (pre-assistant) timestamp so reload agrees.
    const steerMsg = { id, role: 'user' as const, parts: [{ id: `${id}_p`, messageId: id, type: 'text' as const, content: t }], timeCreated: Date.now(), steer: true }
    // E7 Codex round 8: every write — the optimistic bubble, the reconcile, a refusal, an unknown outcome — goes to THIS
    // session's entry (captured now), never to whichever session is displayed when the response lands
    const key = storeKeyRef.current
    const retry = trackSteerRetry(key, steerLifecycleRef.current.signal)
    const session = currentSessionSignal()   // #1316 review round 1: the retries end with the session that sent it
    return postSteer({
      update: (u) => sessionStore.update(key, u),
      session,
      clientId: id,
      bubble: steerMsg,
      post: () => request<{ steered: boolean; reason?: string; id?: string }>(`/api/agent/sessions/${sessionId}/steer${wsQuery}`, {
        method: 'POST',
        body: JSON.stringify({ id, text: t }),
      }),
      // round 10: the retries of an unanswered post end with the hook (unmount) or the session's delete
      signal: retry.signal,
    }).finally(retry.done)
  }, [sessionId, wsQuery])

  // Reply to permission
  const replyPermission = useCallback(async (
    permissionId: string,
    action: 'allow' | 'deny',
    always?: boolean,
    feedback?: string,
  ) => {
    if (!sessionId) return

    // Convert frontend action/always to backend reply format
    let reply: 'once' | 'always' | 'reject'
    if (action === 'deny') {
      reply = 'reject'
    } else {
      reply = always ? 'always' : 'once'
    }

    // Redaction of values typed into feedback text happens
    // server-side (the server is the only place that knows the
    // resolved values under the new pipeline). workspaceId is
    // required so the server can pull the active session_credential
    // rows for this session.
    try {
      const res = await request<{ resumeReady?: boolean }>(`/api/agent/permissions/${permissionId}/reply`, {
        method: 'POST',
        body: JSON.stringify({ sessionID: sessionId, workspaceId, reply, feedback }),
      })
      // #540 PR E: answered a SUSPENDED prompt (the worker restarted) — once every
      // parked request of the turn is answered, continue it.
      if (res?.resumeReady) beginResumeRef.current(sessionId, workspaceId)
    } catch (err) {
      // #384: a 409 PERMISSION_EXPIRED means the prompt is already dead (it
      // timed out, or was answered elsewhere). Clear the card silently — the
      // request was never going to be heard — rather than surfacing an error or
      // leaving a stale, clickable prompt. Any OTHER failure (network, 5xx):
      // keep the card so the user can retry, and rethrow.
      const code = (err as { code?: string; status?: number }).code
      const status = (err as { status?: number }).status
      // #1468 I1 review round 1 (m1): SESSION_BUSY_ELSEWHERE is a 409 that is NOT a dead prompt — another project's
      // same-id chat is running; the card stays for a retry
      if (code === 'SESSION_BUSY_ELSEWHERE') throw err
      if (code !== 'PERMISSION_EXPIRED' && status !== 409) throw err
    }
    setState(prev => ({ ...prev, pendingPermissions: prev.pendingPermissions.filter(p => p.id !== permissionId) }))
  }, [sessionId, workspaceId])

  // Reply to question
  //
  // credentialEnv was previously trusted from the browser; it's now
  // resolved server-side from `entryId` so the same security guarantee
  // holds for mid-turn form submissions as for fresh prompts. Redaction
  // of values typed into free-form answers also moves to the server
  // (only the server has access to the resolved values).
  //
  // envMapping is the SHAPE the agent declared at cred_to_env time
  // (fieldName → envVarName). Sending it in the body lets the server
  // resolve directly via resolveByEntryId without depending on the
  // session_credential SQLite row being there yet — the
  // sessionCreds.bind() POST that creates that row races this
  // replyQuestion POST, and if the reply lands first the row hasn't
  // appeared and the env var ends up empty in the worker. Passing it
  // here closes the race; the resolver still permission-checks +
  // decrypts server-side, so the only thing the browser dictates is
  // env var names, never values.
  const replyQuestion = useCallback(async (
    questionId: string,
    answers: string[][],
    entryId?: string,
    envMapping?: Record<string, string>,
    /** Mutation-approval outcome (AccessApprovalCard). Sent in the reply
     *  body as `result`; the API route + worker publisher relay it to the
     *  blocked tool as the non-enumerable `_result` side-channel (same
     *  mechanism as `_entryId`). Inert until the server catches up —
     *  reconcile the route/publisher pass-through at integration. */
    result?: unknown,
    /** I198 / D56: THIS submit created or updated the keybook entry and the write
     *  completed — decides whether an expired-question note may say "your key was
     *  saved". Reported by CredentialPrompt per path; never inferred from `entryId`
     *  (an existing entry picked or auto-resolved is not a save). */
    credentialSaved?: boolean,
    /** A credential card's answer: #1441 the EXACT field map it showed (requested field → stored field name — names
     *  only), and #1448 the values typed into its `prompt` fields (one-time codes — never stored in the key: the
     *  server puts them in an encrypted one-time slot for this turn). */
    credential?: { fieldMap?: Record<string, string>; oneTime?: Record<string, string> },
  ) => {
    if (!sessionId) return
    const body: Record<string, unknown> = { sessionID: sessionId, answers, workspaceId }
    if (entryId) body.entryId = entryId
    if (envMapping && Object.keys(envMapping).length > 0) body.envMapping = envMapping
    // #1324 S2: this card ran the shared field matcher (and showed its fills before the person submitted — it never
    // auto-submits a by-kind fill), so the answer resolves the same way; an older tab sends none (exact names)
    if (entryId) body.fieldMatch = 'kind'
    if (entryId && credential?.fieldMap) body.fieldMap = credential.fieldMap
    if (credential?.oneTime && Object.keys(credential.oneTime).length > 0) body.oneTime = credential.oneTime
    if (result !== undefined) body.result = result
    let res: { resumeReady?: boolean } | undefined
    try {
      res = await request<{ resumeReady?: boolean }>(`/api/agent/questions/${questionId}/reply`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
    } catch (err) {
      // I198 / D56: a 409 QUESTION_EXPIRED means the worker gave up on this question (its
      // client was gone past the presence grace — e.g. a backgrounded phone — or its
      // ceiling passed), or it was answered elsewhere. Not silent: the card is replaced by
      // a short note (`questionExpiredNote`). Any OTHER failure keeps the card for a
      // retry and rethrows.
      if (questionAlreadyAnswered(err)) {
        // Another tab / a double click answered first — drop the card quietly.
        setState(prev => ({ ...prev, pendingQuestions: prev.pendingQuestions.filter(q => q.id !== questionId) }))
        return
      }
      const noteKey = questionExpiredNote(err, credentialSaved === true)
      if (noteKey === null) throw err
      setState(prev => ({
        ...prev,
        pendingQuestions: prev.pendingQuestions.filter(q => q.id !== questionId),
        expiredQuestionNotice: { questionId, noteKey },
      }))
      return
    }
    setState(prev => ({ ...prev, pendingQuestions: prev.pendingQuestions.filter(q => q.id !== questionId) }))
    if (res?.resumeReady) beginResumeRef.current(sessionId, workspaceId) // #540 PR E
  }, [sessionId, workspaceId])

  // Reject/dismiss a question
  const rejectQuestion = useCallback(async (questionId: string) => {
    if (!sessionId) return
    let res: { resumeReady?: boolean } | undefined
    try {
      res = await request<{ resumeReady?: boolean }>(`/api/agent/questions/${questionId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ sessionID: sessionId, workspaceId }),
      })
    } catch (err) {
      // I198 Codex round: a dismissal of a question that already expired or was already
      // answered has nothing left to do — the card goes, as the user asked. Anything
      // else keeps it for a retry.
      if (!questionAlreadyAnswered(err) && questionExpiredNote(err, false) === null) throw err
    }
    setState(prev => ({ ...prev, pendingQuestions: prev.pendingQuestions.filter(q => q.id !== questionId) }))
    if (res?.resumeReady) beginResumeRef.current(sessionId, workspaceId) // #540 PR E
  }, [sessionId, workspaceId])

  // (The file-content `revert` call — a reverse patch from the inline
  // before/after text in patch parts — was removed in #768. File restoration
  // goes through the version store: RestoreVersionsModal.)

  // Restore conversation — delete all messages after a given messageID
  // Backend cancels any running job before deleting
  const restoreConversation = useCallback(async (
    messageID: string,
    options?: {
      inclusive?: boolean
      /**
       * #639 Task 3.8: handles of attachments the caller is about to
       * RE-REFERENCE in an immediately-following resend (edit-and-resend
       * keeping an existing attachment) — excluded from the server's
       * synchronous dereference so the resend's promotion check still finds
       * the row. Omit for a plain restore/revert with no resend.
       */
      keepAttachmentHandles?: string[]
    },
  ) => {
    if (!sessionId) return
    await request(`/api/agent/sessions/${sessionId}/restore${wsQuery}`, {
      method: 'POST',
      body: JSON.stringify({
        messageID,
        inclusive: options?.inclusive,
        keepAttachmentHandles: options?.keepAttachmentHandles,
      }),
    })
    setState(prev => ({ ...prev, status: 'idle', streamingText: '', activeToolCalls: new Map(), pendingPermissions: [] }))
    await loadMessages()
  }, [sessionId, wsQuery, loadMessages])

  // Fork/Branch session — create a new session branching from a message.
  // Returns the new session id plus the new id of the branch-point message
  // (remapped during copy) so the UI can open edit mode on it.
  const forkSession = useCallback(async (
    messageID?: string,
  ): Promise<{ id: string; branchedMessageID?: string } | null> => {
    if (!sessionId) return null
    try {
      const data = await request<{ id: string; branchedMessageID?: string }>(
        `/api/agent/sessions/${sessionId}/fork${wsQuery}`,
        {
          method: 'POST',
          body: JSON.stringify({ messageID }),
        },
      )
      return { id: data.id, branchedMessageID: data.branchedMessageID }
    } catch {
      return null
    }
  }, [sessionId, wsQuery])

  // Summarize — trigger compaction
  //
  // Returns the SERVER's reason on failure rather than a bare 'error'. Since
  // #438 R3 this route is admitted like any other spend path, so a refusal is
  // now something the user can act on — over the cap (402), or a session
  // pinned to a different wallet (409). Collapsing those to "Failed to
  // summarize session" tells someone their button is broken when in fact
  // their budget ran out.
  const summarize = useCallback(async (instructions?: string): Promise<SummarizeResult> => {
    if (!sessionId) return { status: 'error' }
    // One at a time. Only the context ring disabled itself; the `/compact`
    // slash command and both session menus fired unconditionally, and the route
    // has no busy-fence of its own — so two quick invocations meant two
    // full-transcript LLM passes, two boundaries in one session, and two
    // charges. Guarding at the hook covers every caller, present and future.
    //
    // A ref, not `state.summarizing`: this callback closes over the state from
    // its last render, so two clicks in the same tick would both read `false`.
    if (summarizingRef.current) return { status: 'busy' }
    summarizingRef.current = true
    setState(prev => ({ ...prev, summarizing: true }))
    try {
      // The route ACCEPTS the work now, it does not do it (#443): compaction is
      // an LLM pass over the whole transcript and runs as a worker job. So the
      // response is 202 + a jobId, and there is no `skipped` to report — whether
      // there was anything to compact is only known in the worker.
      //
      // No `loadMessages()` here either. Progress and completion arrive on the
      // session stream as `compaction-progress`, and its `active: false` sets
      // `resyncAfterTurn`, which refetches at the end of the turn — the deferral
      // that exists so a wholesale message-array replace cannot land mid-stream
      // and drop the client-built streaming message.
      await request<{ accepted?: boolean; jobId?: string }>(
        `/api/agent/sessions/${sessionId}/summarize${wsQuery}`,
        // Always a body, even when empty: the route parses one and a POST with
        // no content-type is the shape most likely to trip a proxy later.
        { method: 'POST', body: JSON.stringify(instructions ? { instructions } : {}) },
      )
      // NUDGE THE POLL, then arm the age floor. The busy poll runs every 15s
      // while it believes nothing is running, and a compaction finishes in
      // about 5 — so left alone it starts and ends BETWEEN two polls, the
      // session is never observed compacting, and the completion effect has no
      // edge to fire on. That is a spinner that never stops and a transcript
      // that never refetches, which is exactly what was reported.
      compactRequestRef.current = { sid: sessionId, at: Date.now() }
      void queryClient.invalidateQueries({ queryKey: busySessionsKey })
      return { status: 'queued' }
    } catch (err) {
      // `request` attaches the server's `error` string as the message, plus
      // `.status` and `.code`.
      // No job was accepted, so no `compaction-progress` will ever arrive to
      // clear the indicator — this is the one path that has to clear it itself.
      setState(prev => ({ ...prev, summarizing: false }))
      const e = err as Error & { status?: number; code?: string }
      return { status: 'error', message: e?.message, code: e?.code, httpStatus: e?.status }
    } finally {
      // Clears the REQUEST guard, not the visible indicator. The request is
      // over once the job is accepted; the compaction has only just started.
      //
      // `state.summarizing` is left set deliberately: clearing it here would
      // blank the indicator the instant the POST returned, which is the whole
      // point of moving the work off the request path. It is cleared by
      // `compaction.created` (a Bus event, which reaches every tab) and by the
      // busy-poll edge.
      //
      // NOT by `compaction-progress`, despite that event owning the auto path's
      // `compacting`: it travels only on the per-session Redis channel the
      // AG-UI bridge subscribes to, and both the client attach loop and the
      // server resume gate now refuse to attach to a compaction — so for a
      // manual summarize nothing receives it. Said explicitly because a future
      // change that removed either real path would look safe against the
      // sentence that used to be here. The catch above covers the
      // case where no job was ever accepted.
      //
      // The ref is still worth clearing: it is a cheap first check against a
      // double click, and the server-side fence (#466) is what actually decides.
      summarizingRef.current = false
    }
  }, [sessionId, wsQuery])

  const dismissError = useCallback(async () => {
    // Force-unlock the session if it's stuck (e.g. worker restarted mid-job) —
    // but NEVER for an advisory. Force-unlock drains the session, and
    // `cancelSession` REMOVES a job that is still queued: dismissing "this
    // session is being summarized" would cancel the summarize the user asked
    // for, then leave the transcript unchanged with nothing said about it. The
    // banner's only two controls both land here, so the guard belongs in this
    // one place rather than at each of them.
    if (sessionId && !state.errorAdvisory) {
      try {
        await request(`/api/agent/sessions/${sessionId}/force-unlock${wsQuery}`, { method: 'POST' })
      } catch {}
    }
    setState(prev => ({ ...prev, status: 'idle', error: null, errorAction: null, errorAdvisory: false, pendingPermissions: [], streamingText: '', activeToolCalls: new Map() }))
  }, [sessionId, wsQuery, state.errorAdvisory])

  // #662 — local patch fired by `AttachmentChip.onMoved` right after the
  // move-to-drive dialog reports success, so the chip re-points without
  // waiting for a `message.part.updated` bus round-trip. Threaded down
  // ChatPanel/HeroChat → MessageList → MessageBubble → AttachmentChip,
  // mirroring `onOpenFile`'s wiring.
  const moveAttachmentPart = useCallback((partId: string, drive: { fileId: string; path: string }) => {
    setState(prev => ({ ...prev, messages: applyAttachmentMove(prev.messages, partId, drive) }))
  }, [setState])

  // #662 slice 9 — a moved attachment's DRIVE FILE was deleted: drop the chip's
  // moved state (fileId/path/url) SILENTLY so the tick disappears and the chip
  // becomes a plain, non-opening reference, and PERSIST the clear so a stale
  // tick never comes back on reload.
  //
  // Sweeps EVERY loaded session, not just the one on screen: a `file:deleted`
  // fires for every drive delete tenant-wide, and the user typically deletes
  // from the drive/file view while a DIFFERENT chat session (or none) is the
  // active tab — clearing only `storeKeyRef.current` there left the moved chip
  // stranded until a reload (the reported "tick doesn't go until I refresh").
  // Reads each entry directly (not via the updater) so the "did anything
  // match?" decision — which gates the persist — is made outside React's pure
  // reducer. The persist is per real session (skip `__none__`/drafts); it is
  // idempotent, so a duplicate from another mounted instance is harmless. A
  // cross-workspace session swept with the wrong `wsQuery` 404s server-side
  // (its own workspace's hook persists it correctly), and the local clear
  // stands regardless.
  const clearMovedAttachment = useCallback((deletedFileId: string) => {
    for (const key of sessionStore.keys()) {
      const current = sessionStore.getState(key)
      if (applyAttachmentDeletion(current.messages, deletedFileId) === current.messages) continue
      sessionStore.update(key, prev => ({ ...prev, messages: applyAttachmentDeletion(prev.messages, deletedFileId) }))
      if (key !== '__none__' && !isDraftSession(key)) {
        void request(
          `/api/agent/sessions/${key}/attachments/clear-moved${wsQuery}`,
          { method: 'POST', body: JSON.stringify({ fileId: deletedFileId }) },
        ).catch(() => {})
      }
    }
  }, [wsQuery])

  // #662 (follow-move): a moved attachment's drive file may have MOVED/renamed
  // (a drive drag, or an agent `mv`). The `fileId` is stable but the part froze
  // the path — so the chip's stale path 404s and it wrongly reverts. This asks
  // the server to RE-RESOLVE the current path from `fileId` (authoritative,
  // never a client-supplied path) and either FOLLOW it (update the part's
  // path/label → chip stays, and the model's annotation reads the live path) or
  // CLEAR it when the file is gone OR the person can no longer read it (#1238
  // S4: the server answers both alike, `{ path: null }`, and clears the part —
  // so the chip is cleared here too). Sweeps every loaded session (a `fileId`
  // may be referenced off-screen), skips `__none__`/drafts, and — like the
  // clear sweep — fails SAFE: an error (a transient failure, or a read check
  // that failed — the server answers 5xx and keeps the part) leaves the part
  // untouched and returns `false` so the load-verify retries. Returns whether
  // the server gave a definitive answer for at least one attempt.
  const resyncMovedAttachment = useCallback(async (fileId: string): Promise<boolean> => {
    let done = true
    let sawPart = false
    for (const key of sessionStore.keys()) {
      const current = sessionStore.getState(key)
      if (!current.messages.some(m => m.parts.some(p => p.type === 'file' && p.fileId === fileId))) continue
      if (key === '__none__' || isDraftSession(key)) continue
      sawPart = true
      try {
        const res = await request<{ path: string | null }>(
          `/api/agent/sessions/${key}/attachments/refresh-moved${wsQuery}`,
          { method: 'POST', body: JSON.stringify({ fileId }) },
        )
        const path = res?.path ?? null
        sessionStore.update(key, prev => ({
          ...prev,
          messages: path === null
            ? applyAttachmentDeletion(prev.messages, fileId)
            : applyAttachmentPathRefresh(prev.messages, fileId, path),
        }))
      } catch {
        done = false // transient — leave the part as-is; the load-verify retries
      }
    }
    return sawPart ? done : true
  }, [wsQuery])

  // #662 follow-move (SYNC-DETECTED agent `mv`): unlike a drive-UI drag, a
  // sandbox `mv` mints a NEW drive row, so the `file:moved` event carries
  // `previousFileId` (the OLD id the chip stored) AND a new `fileId`. Sweep every
  // loaded session for a part still on the OLD id and ask the server to re-point
  // it onto the new id + current path (path re-resolved from the NEW id server-
  // side, never client-supplied). A NEW id the person can't read is answered
  // like a gone one (#1238 S4) and the chip is cleared. Fails SAFE like
  // `resyncMovedAttachment` on an error.
  const followMovedAttachment = useCallback(async (previousFileId: string, newFileId: string): Promise<boolean> => {
    let done = true
    let sawPart = false
    for (const key of sessionStore.keys()) {
      const current = sessionStore.getState(key)
      if (!current.messages.some(m => m.parts.some(p => p.type === 'file' && p.fileId === previousFileId))) continue
      if (key === '__none__' || isDraftSession(key)) continue
      sawPart = true
      try {
        const res = await request<{ path: string | null }>(
          `/api/agent/sessions/${key}/attachments/refresh-moved${wsQuery}`,
          { method: 'POST', body: JSON.stringify({ fileId: newFileId, previousFileId }) },
        )
        const path = res?.path ?? null
        sessionStore.update(key, prev => ({
          ...prev,
          messages: path === null
            ? applyAttachmentDeletion(prev.messages, previousFileId)
            : applyAttachmentIdSwap(prev.messages, previousFileId, newFileId, path),
        }))
      } catch {
        done = false // transient — leave the part as-is; the load-verify retries
      }
    }
    return sawPart ? done : true
  }, [wsQuery])

  // Proactive (realtime) clear: drive `file:*` events flow through the
  // workspace realtime layer, not the chat's own SSE bus. Subscribe here so a
  // `file:deleted` (which carries only `fileId`) reaches the chat's message
  // state. `useRealtimeOptional` returns null outside a RealtimeProvider (some
  // test/mobile mounts), where the lazy fallback still covers an explicit open.
  const realtime = useRealtimeOptional()
  useEffect(() => {
    if (!realtime) return
    return realtime.onFileEvent((event) => {
      if (event.type === 'file:deleted' && event.fileId) clearMovedAttachment(event.fileId)
      // #662 follow-move: a moved/renamed drive file — re-resolve and update the
      // chip's path so it tracks the file instead of going stale (which would
      // otherwise revert the chip on the next load).
      else if (event.type === 'file:moved' && event.fileId) {
        // A SYNC-DETECTED agent `mv` carries `previousFileId` (the fileId
        // CHANGED — a new drive row); follow OLD→NEW. A drive-UI drag keeps the
        // same id (no previousFileId) — just re-resolve its path.
        if (event.previousFileId && event.previousFileId !== event.fileId) {
          void followMovedAttachment(event.previousFileId, event.fileId)
        } else {
          void resyncMovedAttachment(event.fileId)
        }
      }
    })
  }, [realtime, clearMovedAttachment, resyncMovedAttachment, followMovedAttachment])

  // Lazy fallback (deletion happened while this chat was closed, so no
  // `file:deleted` reached it): when a MOVED chip is opened and the drive file
  // turns out to be gone, clear it too. Wired from ChatPanel/HeroChat's
  // transcript open handler, which already delegates the open (and the
  // host's own "File not found" toast, slice 3) to `onOpenFile` unchanged —
  // this only DECIDES whether to clear. A resolve fetch fires only when the
  // path actually belongs to a moved part in this session (the cheap in-memory
  // gate below), i.e. at click time for a moved chip, never on render and never
  // for an ordinary path link. Fails safe toward keeping the tick.
  const verifyMovedAttachmentByPath = useCallback(async (path: string) => {
    const current = sessionStore.getState(storeKeyRef.current)
    const fileId = current.messages
      .flatMap(m => m.parts)
      .find((p): p is Extract<MessagePart, { type: 'file' }> => p.type === 'file' && !!p.fileId && p.path === path)
      ?.fileId
    if (!fileId) return
    // Re-sync by `fileId` (never the clicked path): the server re-resolves the
    // current location and either follows the move (update path) or clears if
    // gone. Fails safe — a transient error leaves the chip untouched.
    await resyncMovedAttachment(fileId)
  }, [resyncMovedAttachment])

  // #662 (#3 + follow-move): the proactive realtime handlers only fire while
  // THIS chat is loaded when the drive change happens; a file deleted OR
  // moved/renamed while the chat was closed otherwise leaves a stale chip that
  // survives a reload (the reported "icon stays" / "chip reverts after a move").
  // On load, re-sync each MOVED part ONCE by its `fileId`: the server
  // re-resolves the current path and the chip either FOLLOWS the move (path
  // updated) or CLEARS (file gone). Keyed on `fileId`, never the frozen path.
  // LIMITATION: this backstop follows only a SAME-id move (a drive-UI drag,
  // whose fileId is stable). An agent `bash mv` mints a NEW fileId; the mapping
  // old→new rides the LIVE `file:moved` event only (see `followMovedAttachment`),
  // so if that move happened while the chat was closed, the stored part still
  // holds the OLD id, `getPath(oldId)` is null (binned), and this CLEARS to
  // inline rather than following. It fails safe (the inline bytes stay openable);
  // a closed-chat follow would need a persisted old→new redirect, out of scope.
  // Guarded by a per-session set so each part is synced ONCE — but the fileId
  // is marked verified only AFTER the server gives a definitive answer, never
  // before the await. `state.messages` gets a fresh reference on every streaming
  // tick, which re-runs this effect and cancels the in-flight closure; marking
  // before would drop that part from `pending` forever and never re-sync it —
  // the exact case this backstop exists for (a chat reopened while streaming).
  // Marking on completion means a cancelled/transient sync simply retries next
  // run, and the final settle run confirms it. Fails safe on a transient error.
  const verifiedMovedRef = useRef<Set<string>>(new Set())
  useEffect(() => { verifiedMovedRef.current = new Set() }, [sessionId])
  useEffect(() => {
    if (!sessionId) return
    const pending = state.messages
      .flatMap(m => m.parts)
      .filter((p): p is Extract<MessagePart, { type: 'file' }> =>
        p.type === 'file' && !!p.fileId && !!p.path && !verifiedMovedRef.current.has(p.fileId))
    if (pending.length === 0) return
    let cancelled = false
    void (async () => {
      for (const p of pending) {
        if (cancelled) return // superseded/unmounted — next run retries
        const done = await resyncMovedAttachment(p.fileId!)
        if (cancelled) return
        if (done) verifiedMovedRef.current.add(p.fileId!) // definitive — don't recheck
      }
    })()
    return () => { cancelled = true }
  }, [sessionId, state.messages, resyncMovedAttachment])

  return {
    ...state,
    // The AUTO flag from the stream, OR'd with the poll-derived MANUAL one.
    // Derived rather than stored — see the note beside `pollCompacting`: five
    // rewrites of a stored version desynced from their per-hook guards, and
    // this one cannot, because there is nothing to keep in step.
    compacting: state.compacting || pollCompacting,
    sendMessage,
    steerMessage,
    skipOpenPrompts,
    cancel,
    dismissError,
    replyPermission,
    replyQuestion,
    rejectQuestion,
    restoreConversation,
    forkSession,
    summarize,
    refresh: loadMessages,
    moveAttachmentPart,
    verifyMovedAttachmentByPath,
  }
}

// ─── useAgentSessions ───────────────────────────────────────────────────

/**
 * The member's default for auto mode in NEW chats (0357), one copy per tab and org identity. Every `useAgentSessions`
 * reads it here: a composer that kept its own copy went stale while another workspace (WorkspaceCache keeps them all
 * mounted) or the chat home changed it, and its draft then created a chat with the OLD value — review round 3. Read
 * when a composer mounts, when the ⚡ popover opens and when the tab comes back into view or the window regains focus
 * (a change made in another tab or window); written only from a save whose reply shows the value.
 */
const autoModeDefaultStore = (() => {
  type Entry = { value: boolean | null; gen: number; subs: Set<() => void> }
  const entries = new Map<string, Entry>()
  const keyOf = (orgEmail?: string) => `${getActiveTenantId() ?? ''}\u0000${orgEmail ?? ''}`
  const entry = (k: string) => {
    let e = entries.get(k)
    if (!e) { e = { value: null, gen: 0, subs: new Set() }; entries.set(k, e) }
    return e
  }
  const set = (e: Entry, value: boolean | null) => { if (e.value !== value) { e.value = value; e.subs.forEach(f => f()) } }
  return {
    keyOf,
    get: (k: string) => entry(k).value,
    subscribe: (k: string, f: () => void) => { const e = entry(k); e.subs.add(f); return () => { e.subs.delete(f) } },
    /** A read that fails keeps nothing it can't vouch for (null); a read started before a save never lands over it. */
    reload(orgEmail?: string) {
      const e = entry(keyOf(orgEmail))
      const gen = ++e.gen
      request<{ on?: unknown }>('/api/agent/sessions/auto-mode/default').then(
        (r) => { if (gen === e.gen) set(e, typeof r?.on === 'boolean' ? r.on : null) },
        () => { if (gen === e.gen) set(e, null) },
      )
    },
    /** As setSessionAutoMode: the reply must show the change, or it was not saved. */
    async save(orgEmail: string | undefined, on: boolean) {
      const e = entry(keyOf(orgEmail))
      const r = await request<{ on?: unknown } | undefined>('/api/agent/sessions/auto-mode/default', {
        method: 'PUT', body: JSON.stringify({ on }),
      })
      if (r?.on !== on) throw new Error('the auto mode default was not saved')
      e.gen++
      set(e, on)
    },
  }
})()

function useAutoModeDefaultFor(orgEmail?: string): boolean | null {
  const k = autoModeDefaultStore.keyOf(orgEmail)
  const value = useSyncExternalStore(
    useCallback((f: () => void) => autoModeDefaultStore.subscribe(k, f), [k]),
    () => autoModeDefaultStore.get(k),
  )
  useEffect(() => {
    autoModeDefaultStore.reload(orgEmail)
    // back to this tab, or into this window (two windows side by side never hide either): another may have changed it
    const onVisible = () => { if (document.visibilityState === 'visible') autoModeDefaultStore.reload(orgEmail) }
    const onFocus = () => autoModeDefaultStore.reload(orgEmail)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)
    return () => { document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onFocus) }
  }, [k, orgEmail])
  return value
}

export function useAgentSessions(workspaceId: string, orgEmail?: string) {
  const [sessions, setSessions] = useState<AgentSessionInfo[]>([])
  const [loading, setLoading] = useState(true)
  // True once a fetch SUCCEEDED for this workspace. A failed load leaves `sessions`
  // at its last value; it must never read as "you have no chats" — ChatPanel prunes
  // the open tabs against this list, and pruning against a failed (empty) load wiped
  // every tab's open chats and this tab's own chat during an API restart.
  const [loaded, setLoaded] = useState(false)
  useEffect(() => { setLoaded(false) }, [workspaceId, orgEmail])
  const loadGen = useRef(0)

  // `retry` = the MOUNT load only: keep trying (any failure, capped backoff) until the
  // list loads — an API instance restarting answers 5xx / drops the connection, and a
  // load that gave up left the tab strip blank. Every other refresh is one attempt:
  // createSession awaits its refresh, so retrying there held a new chat's first send
  // ~15s during a restart. A newer load or an unmount (the effect cleanup bumps the
  // generation) ends the loop — re-checked after each backoff.
  const load = useCallback(async (retry = false) => {
    const gen = ++loadGen.current
    try {
      setLoading(true)
      for (let attempt = 0; ; attempt++) {
        try {
          const data = await request<AgentSessionInfo[]>(
            `/api/agent/sessions?workspaceId=${encodeURIComponent(workspaceId)}`,
          )
          if (gen !== loadGen.current) return
          setSessions(data)
          setLoaded(true)
          return
        } catch {
          if (!retry || gen !== loadGen.current) return
          await new Promise(r => setTimeout(r, Math.min(500 * 2 ** attempt, 15_000)))
          if (gen !== loadGen.current) return
        }
      }
    } finally {
      if (gen === loadGen.current) setLoading(false)
    }
  }, [workspaceId, orgEmail])

  useEffect(() => { void load(true); return () => { loadGen.current++ } }, [load])
  const refreshOnce = useCallback(() => load(false), [load])

  const createSession = useCallback(async (title?: string, model?: string, autoMode?: boolean): Promise<string | null> => {
    try {
      // No `id`: the server mints every session id (#1468 I1).
      const data = await request<AgentSessionInfo>('/api/agent/sessions', {
        method: 'POST',
        // a new chat switched to auto mode before its first message starts in it — the first turn included
        body: JSON.stringify({ workspaceId, title, model, ...(autoMode !== undefined ? { autoMode } : {}) }),
      })
      await load()
      return data.id
    } catch {
      return null
    }
  }, [workspaceId, load])

  const deleteSession = useCallback(async (sessionId: string) => {
    cancelSteerRetries(sessionId)   // E7 Codex round 10: a deleted session's unanswered steer posts stop retrying
    await request(
      `/api/agent/sessions/${sessionId}?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: 'DELETE' },
    )
    await load()
  }, [workspaceId, load])

  const renameSession = useCallback(async (sessionId: string, title: string) => {
    await request(
      `/api/agent/sessions/${sessionId}?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: 'PATCH', body: JSON.stringify({ title }) },
    )
    await load()
  }, [workspaceId, load])

  /** Auto mode for one conversation (null = never chosen, which is off). The worker reads it at each of the AI's
   *  actions, so it applies from the next one. */
  // `reloadList: false` for a chat the list leaves out (archived): reloading the list makes the composer drop that open
  // tab — the caller re-reads the chat on its own instead (useConversationAutoMode).
  // The reply must SHOW the change: an API from before auto mode (a rolling deploy) answers 200 and ignores the field,
  // and the chip would then claim a setting the worker doesn't have — so a reply that doesn't carry it is a failed save.
  const setSessionAutoMode = useCallback(async (sessionId: string, autoMode: boolean | null, opts?: { reloadList?: boolean }) => {
    const info = await request<AgentSessionInfo | undefined>(
      `/api/agent/sessions/${sessionId}?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: 'PATCH', body: JSON.stringify({ autoMode }) },
    )
    if (info?.id !== sessionId || (info.autoMode ?? null) !== autoMode) throw new Error('auto mode was not saved')
    if (opts?.reloadList !== false) await load()
  }, [workspaceId, load])

  /** One conversation, archived ones included — for a chat on screen that `sessions` leaves out (it lists live chats
   *  only), so what is shown about it is read, never guessed. null = not found. */
  const fetchSession = useCallback(async (id: string): Promise<AgentSessionInfo | null> => {
    const data = await request<AgentSessionInfo[]>(
      `/api/agent/sessions?workspaceId=${encodeURIComponent(workspaceId)}&includeArchived=true`,
    )
    return data.find(s => s.id === id) ?? null
  }, [workspaceId])

  /** Take back one "Always" given in a conversation. Applies once its current reply has ended. */
  const removeAlwaysGrant = useCallback(async (sessionId: string, grant: { permission: string; pattern: string }, opts?: { reloadList?: boolean }) => {
    const info = await request<AgentSessionInfo | undefined>(
      `/api/agent/sessions/${sessionId}?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: 'PATCH', body: JSON.stringify({ removeAlwaysGrant: { permission: grant.permission, pattern: grant.pattern } }) },
    )
    // as setSessionAutoMode: an older API ignores the field — the reply must show the grant gone
    const still = (info?.permission ?? []).some(r => r.permission === grant.permission && r.pattern === grant.pattern)
    if (info?.id !== sessionId || still) throw new Error('the "Always" answer was not taken back')
    if (opts?.reloadList !== false) await load()
  }, [workspaceId, load])

  // The member's own default for NEW chats in this org (0357) — what a draft's chip shows and what its create SENDS
  // (useConversationAutoMode `draftValue`). ONE copy per tab (`autoModeDefaultStore`, above), so every mounted composer
  // (each cached workspace's ChatPanel, the chat home's HeroChat) shows the same value and a change made in any of them
  // reaches all at once. null = not read (yet, or the read failed).
  const autoModeDefault = useAutoModeDefaultFor(orgEmail)
  const setAutoModeDefault = useCallback((on: boolean) => autoModeDefaultStore.save(orgEmail, on), [orgEmail])
  const reloadAutoModeDefault = useCallback(() => autoModeDefaultStore.reload(orgEmail), [orgEmail])

  return { sessions, loading, loaded, createSession, deleteSession, renameSession, setSessionAutoMode, removeAlwaysGrant, fetchSession, autoModeDefault, setAutoModeDefault, reloadAutoModeDefault, refresh: refreshOnce }
}

// ─── useAgentUsage ────────────────────────────────────────────────────

/**
 * Fetches workspace-level cost total. Reloads on workspaceId change
 * and exposes a refresh() for manual reload after session completion.
 *
 * Also carries `costVisibility` (0174) — WHICH cost the deployment shows in the
 * chat, resolved server-side for this viewer and this tenant. It is returned
 * `null` until the first response lands, and every caller must treat that as
 * HIDDEN rather than as a default: guessing 'user' would flash a money figure
 * on a deployment configured to hide it, which is the one outcome the setting
 * exists to prevent. The delay is invisible in practice — these badges are
 * small, late-loading chrome, not the page's subject.
 *
 * The FIGURES need no such guard: the server returns 0 for both totals when the
 * mode is 'hidden' and never runs the query, so a client that ignored the flag
 * entirely still could not render a hidden number. The flag exists for the
 * per-message badge, whose value comes from SQLite through the message payload
 * and therefore does not pass through that gate.
 */
export function useAgentUsage(workspaceId: string) {
  const [workspaceCost, setWorkspaceCost] = useState(0)
  const [workspaceCostDisplayMinor, setWorkspaceCostDisplayMinor] = useState<number | null>(null)
  const [costCurrency, setCostCurrency] = useState<ChatCostCurrency | null>(null)
  const [costVisibility, setCostVisibility] = useState<ChatCostVisibility | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await request<AgentUsageResponse>(
        `/api/agent/usage?workspaceId=${encodeURIComponent(workspaceId)}`,
      )
      setWorkspaceCost(data.workspaceCost)
      // Both `null` unless the server converted. Never derived from the
      // currency code — see `formatChatCost`.
      setWorkspaceCostDisplayMinor(data.workspaceCostDisplayMinor ?? null)
      setCostCurrency(data.currency ?? null)
      // `?? null` rather than `?? 'user'`: an API that predates the field is
      // indistinguishable from one that has not answered yet, and both should
      // withhold rather than assume.
      setCostVisibility(data.costVisibility ?? null)
    } catch {
      // Ignore
    }
  }, [workspaceId])

  useEffect(() => { load() }, [load])

  return {
    workspaceCost, workspaceCostDisplayMinor, costCurrency, costVisibility,
    refresh: load,
  }
}

// ─── useSessionActivity ───────────────────────────────────────────────

/**
 * Per-session running / finished-while-away state for a session switcher
 * (the workspace chat tab bar). A single hook only knows the ACTIVE session's
 * live `status`; a backgrounded run has no on-screen signal, so its tab looked
 * idle (reads as "cancelled") and a turn that finished off-tab showed nothing.
 *
 * Same shape as the chat home rail (`useChatHomeSessions`), and now literally
 * the same query: both consume `useBusySessions`, plus a `doneIds` set for
 * sessions that LEAVE the busy set while they aren't the active tab (cleared on
 * open).
 *
 * This used to be a local 5s `setInterval`, justified by "this package has no
 * react-query dep, and the two surfaces aren't mounted at once". Both halves
 * were wrong: the surfaces DO overlap (the since-removed RevertConflictModal
 * rendered inside ChatPanel; ChatPanel and HeroChat can both be mounted), and
 * the dep is now present precisely so this scan is shared.
 *
 * @param activeSessionId the session currently on screen — never gets a done dot
 */
export function useSessionActivity(activeSessionId: string | null) {
  const prevBusyRef = useRef<Set<string> | null>(null)
  const activeRef = useRef(activeSessionId)
  activeRef.current = activeSessionId
  const [doneIds, setDoneIds] = useState<Set<string>>(() => new Set())

  // Shares the ONE tenant-wide scan with useAgentSession and the chat-home
  // rail. Previously its own 5s setInterval, which ran concurrently with
  // theirs against the same endpoint for the same data.
  const { data: rows } = useBusySessions()
  const busyIds = useMemo(() => new Set((rows ?? []).map(r => r.id)), [rows])

  useEffect(() => {
    if (!rows) return // no scan yet — keep the last known set
    const prev = prevBusyRef.current
    prevBusyRef.current = busyIds
    // Sessions that dropped off the busy set while not on screen get a dot.
    if (!prev) return
    const finished = [...prev].filter(id => !busyIds.has(id) && id !== activeRef.current)
    if (finished.length) setDoneIds(s => new Set([...s, ...finished]))
  }, [rows, busyIds])

  const clearDone = useCallback((id: string) => {
    setDoneIds(s => { if (!s.has(id)) return s; const n = new Set(s); n.delete(id); return n })
  }, [])

  return { busyIds, doneIds, clearDone }
}
